import {t, useLanguage} from '../i18n';
import {interfaceLocale} from '../i18n';
import {CustomerSessionPrograms} from "../components/SessionPrograms";
import { matchesCrmSegment } from '../utils/crmInsights';
import { crmWorkspace } from '../utils/crmWorkspace';
import CrmWorkspaceManager from '../components/CrmWorkspaceManager';
import CustomerDossier from '../components/CustomerDossier';
import {usePendingEditor} from "../hooks/useEditorNavigation";
// LOOP CRM FLAWLESS DEDUPE R29 — full backend plan, resumable cleanup, friendly progress, and archived secondaries.
// src/pages/CustomersPage.jsx
// LOOP CRM CLUSTERED MERGE JOBS V27 — smart dedupe creates clustered backend jobs and processes chunks with progress.
// LOOP CRM NAME-ONLY SMART DEDUPE V26 — smart sweep is always visible and can auto-merge safe empty name-only duplicates.
// LOOP CRM PERFORMANCE SMART DEDUPE V25 — fast non-blocking search, safe bulk merge preview, clear inline rows, and modal stacking.
// LOOP CRM SEARCH IMPORT REDESIGN V24 — robust search fallback, inline customer list, and faster duplicate cleanup.
// LOOP D2.9.4E.7 — customer card chip wrap + contact overflow polish
// LOOP D2.9.4E.6 — real activity + dynamic benefits panel
// LOOP D2.9.4E.5 — equal CRM list/detail panel heights
// LOOP D2.9.4E.4 — null-safe CRM actions + agenda direct CRM route
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CustomersPolish.css";
import "./CustomersWorkspace.css";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  limit as fsLimit,
  startAfter,
  where,
} from "firebase/firestore";
import {
  Search,
  ChevronDown,
  Upload,
  User,
  Phone,
  Mail,
  CalendarDays,
  Tag,
  ArrowLeft,
  MoreHorizontal,
  MessageCircle,
  QrCode,
  Copy,
  ExternalLink,
  Printer,
} from "lucide-react";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { adjustCustomerMembershipCreditsAction, summarizeMembershipCreditAdjustmentResult, renewCustomerMembershipAction, summarizeMembershipRenewalResult, expireCustomerMembershipsNowAction, summarizeMembershipExpirationResult, getCustomerMembershipActivityAction } from "../adminCalendar/bookingActions";
import { QRCodeCanvas } from "qrcode.react";
import CustomerImportModal from "../components/CustomerImportModal";
import AssistedMembershipEnrollment from "../components/memberships/AssistedMembershipEnrollment";
import { hasCurrentMembershipPlan, membershipBenefitType, membershipCreditStats, membershipStatusValue, membershipStatusLabel } from "../utils/crmMembershipDisplay";
import { MembershipMiniCard, LoyaltyMiniCard } from "../components/memberships/CrmBenefitCards";
import { formatPhoneDisplay, normalizePhoneToE164 } from "../utils/phoneUtils";

function tsToDate(value) {
  try {
    if (!value) return null;
    if (value.seconds) return new Date(value.seconds * 1000);
    return new Date(value);
  } catch {
    return null;
  }
}

function formatDate(value) {
  const d = tsToDate(value);
  if (!d || Number.isNaN(d.getTime())) return "Sin visitas registradas en Loop";
  return d.toLocaleDateString(interfaceLocale(), { day: "2-digit", month: "short", year: "numeric" });
}

function getInitials(name) {
  const parts = String(name || "Cliente").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
}

function normalizeCustomerSearchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeCustomerPhone(value, countryCode = "PA") {
  return normalizePhoneToE164(value, countryCode);
}

function normalizeCustomerEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function customerSearchText(customer = {}) {
  return normalizeCustomerSearchText([
    customer.name,
    customer.customerName,
    customer.fullName,
    customer.displayName,
    customer.nameKey,
    customer.searchableName,
    customer.searchText,
    customer.phone,
    customer.phoneDisplay,
    customer.phoneE164,
    customer.phoneNormalized,
    customer.phoneDigits,
    customer.whatsapp,
    customer.whatsApp,
    customer.email,
    customer.emailNormalized,
    customer.customerEmail,
    customer.notes,
    customer.customerNotes,
    Array.isArray(customer.tags) ? customer.tags.join(" ") : "",
  ].filter(Boolean).join(" "));
}

function crmCustomerIdentity(customer = {}) {
  return String(customer.phoneNormalized || customer.phone || customer.emailNormalized || customer.email || customer.id || customer.name || "").trim().toLowerCase();
}

function crmCustomerIsMergedOrHidden(customer = {}) {
  return customer.hiddenFromCrm === true || customer.archived === true || Boolean(customer.mergedIntoCustomerId || customer.mergedIntoCustomer);
}


function crmCustomerDocumentId(customer = {}, explicitDocumentId = "") {
  return String(
    explicitDocumentId ||
    customer?.__documentId ||
    customer?.docId ||
    customer?.id ||
    customer?.customerId ||
    customer?.clientId ||
    ""
  ).trim();
}

function crmCanonicalCustomerRow(customer = {}, explicitDocumentId = "") {
  const source = customer && typeof customer === "object" ? customer : {};
  const id = crmCustomerDocumentId(source, explicitDocumentId);
  return {
    ...source,
    __documentId: id || source.__documentId || null,
    id: id || null,
    customerId: id || source.customerId || null,
    clientId: id || source.clientId || null,
  };
}

function crmCustomerRowFromDoc(docSnap) {
  return crmCanonicalCustomerRow(docSnap?.data ? (docSnap.data() || {}) : {}, docSnap?.id || "");
}

function mergeCrmCustomerRows(rows = []) {
  const map = new Map();
  rows.forEach((rawRow) => {
    const row = crmCanonicalCustomerRow(rawRow);
    const key = crmCustomerIdentity(row);
    if (!key) return;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, row);
      return;
    }
    const existingBookings = Number(existing.totalBookings || existing.importedTotalBookings || 0);
    const rowBookings = Number(row.totalBookings || row.importedTotalBookings || 0);
    map.set(key, {
      ...existing,
      ...row,
      id: existing.id || row.id,
      name: existing.name || row.name,
      phone: existing.phone || row.phone,
      phoneNormalized: existing.phoneNormalized || row.phoneNormalized,
      email: existing.email || row.email,
      emailNormalized: existing.emailNormalized || row.emailNormalized,
      totalBookings: Math.max(existingBookings, rowBookings),
      importedTotalBookings: Math.max(existingBookings, rowBookings),
    });
  });
  return Array.from(map.values());
}

function customerMatchesCrmSearch(customer, normalized, digits) {
  if (!normalized && !digits) return true;
  const haystack = customerSearchText(customer);
  if (normalized && haystack.includes(normalized)) return true;
  if (digits && digits.length >= 3) {
    const phoneText = String(customer.phone || customer.phoneNormalized || "").replace(/\D/g, "");
    if (phoneText.includes(digits)) return true;
  }
  return false;
}


function crmContactPhone(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  const raw = c.phoneDisplay || c.phoneE164 || c.phoneNormalized || c.phone || c.whatsapp || c.whatsApp || "";
  return raw ? formatPhoneDisplay(raw, "PA") : "";
}

function crmContactEmail(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  return c.email || c.emailNormalized || c.customerEmail || "";
}

function crmBookingsCount(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  const total = Number(c.totalBookings ?? c.importedTotalBookings ?? c.bookingsCount ?? c.reservationsCount ?? 0);
  return Number.isFinite(total) && total > 0 ? Math.round(total) : 0;
}

function crmDateNice(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  return formatDate(c.lastBookingAt || c.lastVisitAt || c.createdAt || c.updatedAt);
}

function crmVisitLabel(customer = {}) {
  const count = crmBookingsCount(customer);
  if (count > 0) return `${count} reserva${count === 1 ? "" : "s"}`;
  return "Sin visitas registradas en Loop";
}

function crmWhatsappOk(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  const optOut = c.whatsappOptOut === true || c.whatsappOptIn === false || c.customerWhatsappConsent === false;
  const phone = crmContactPhone(c);
  return !optOut && Boolean(phone);
}

function crmOriginLabel(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  const raw = String(c.importSource || c.source || c.origin || c.createdSource || "manual").trim().toLowerCase();
  if (raw.includes("import")) return "Importado";
  if (raw.includes("booking") || raw.includes("reserva")) return "Reserva";
  return "Manual";
}

function crmSegmentLabel(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  const tags = Array.isArray(c.tags) ? c.tags.map((t) => String(t).toLowerCase()) : [];
  if (tags.some((t) => t.includes("vip"))) return "VIP";
  if (crmBookingsCount(c) >= 3) return "Cliente recurrente";
  return "Cliente nuevo";
}

function crmPrimaryNote(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  return String(c.notes || c.customerNotes || "").trim() || "Prefiere horarios de mañana.";
}

function crmPointsEstimate(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  const direct = Number(c.points || c.rewardPoints || c.loyaltyPoints || c.importedRewardPointsText || 0);
  if (Number.isFinite(direct) && direct > 0) return Math.round(direct);
  const estimate = crmBookingsCount(c) * 20;
  return estimate > 0 ? estimate : 0;
}

function crmDateFromAny(value) {
  try {
    if (!value) return null;
    if (value.seconds) return new Date(value.seconds * 1000);
    if (typeof value.toDate === "function") return value.toDate();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function crmBookingDate(booking = {}) {
  const candidates = [
    booking.scheduledAt,
    booking.start,
    booking.startTime,
    booking.startDate,
    booking.dateTime,
    booking.bookingDate,
    booking.createdAt,
  ];
  for (const value of candidates) {
    const d = crmDateFromAny(value);
    if (d && !Number.isNaN(d.getTime())) return d;
  }
  const dateText = String(booking.date || booking.day || "").trim();
  const timeText = String(booking.time || booking.hour || booking.startHour || "09:00").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateText)) {
    const d = new Date(`${dateText}T${/^\d{1,2}:\d{2}$/.test(timeText) ? timeText : "09:00"}:00-05:00`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function crmBookingServiceLabel(booking = {}) {
  return String(booking.serviceName || booking.service || booking.packageName || booking.serviceTitle || booking.title || "Reserva").trim() || "Reserva";
}

function crmBookingStatusLabel(booking = {}) {
  const raw = String(booking.status || booking.bookingStatus || booking.confirmationStatus || booking.state || "confirmed").trim().toLowerCase();
  if (["pending", "pendiente", "requested", "to_confirm"].includes(raw)) return "Pendiente";
  if (["confirmed", "confirmada", "approved", "accepted"].includes(raw)) return "Confirmada";
  if (["completed", "complete", "done", "completada"].includes(raw)) return "Completada";
  if (["cancelled", "canceled", "cancelada"].includes(raw)) return "Cancelada";
  if (["no_show", "no-show", "noshow", "no asistió", "no_asistio"].includes(raw)) return "No asistió";
  if (["rejected", "declined", "rechazada"].includes(raw)) return "Rechazada";
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Reserva";
}

function crmBookingSourceLabel(booking = {}) {
  const raw = String(booking.bookingSource || booking.source || booking.createdSource || booking.origin || "").trim().toLowerCase();
  if (raw.includes("online") || raw.includes("public")) return "Online";
  if (raw.includes("manual") || raw.includes("admin") || raw.includes("agenda")) return "Manual";
  if (raw.includes("whatsapp")) return "WhatsApp";
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Reserva";
}

function crmBookingDateNice(booking = {}) {
  const d = crmBookingDate(booking);
  if (!d) return "Sin fecha";
  return d.toLocaleDateString(interfaceLocale(), { day: "2-digit", month: "short", year: "numeric" });
}

function crmBookingTimeNice(booking = {}) {
  const d = crmBookingDate(booking);
  if (!d) return "";
  return d.toLocaleTimeString(interfaceLocale(), { hour: "2-digit", minute: "2-digit" });
}

function crmBookingClientMatch(booking = {}, customer = {}) {
  if (!customer?.id) return false;
  const id = String(customer.id || "");
  const phone = crmContactPhone(customer).replace(/\D/g, "");
  const email = crmContactEmail(customer).toLowerCase();
  const ids = [
    booking.customerId,
    booking.clientId,
    booking.customer?.id,
    booking.customer?.customerId,
    booking.customerSnapshot?.id,
    booking.customerSnapshot?.customerId,
  ].map((x) => String(x || ""));
  if (ids.includes(id)) return true;
  const bPhone = String(booking.customerPhone || booking.clientPhone || booking.phone || booking.customer?.phone || booking.customerSnapshot?.phone || "").replace(/\D/g, "");
  const bEmail = String(booking.customerEmail || booking.clientEmail || booking.email || booking.customer?.email || booking.customerSnapshot?.email || "").toLowerCase();
  return Boolean((phone && bPhone && phone === bPhone) || (email && bEmail && email === bEmail));
}

async function loadCustomerBookingHistory(dbClient, businessId, customer = {}, maxRows = 80) {
  if (!dbClient || !businessId || !customer?.id) return [];
  const bookingsCol = collection(dbClient, `businesses/${businessId}/bookings`);
  const customerId = String(customer.id || "");
  const phone = crmContactPhone(customer);
  const email = crmContactEmail(customer);
  const tasks = [
    getDocs(query(bookingsCol, where("customerId", "==", customerId), fsLimit(maxRows))),
    getDocs(query(bookingsCol, where("customer.id", "==", customerId), fsLimit(maxRows))),
    getDocs(query(bookingsCol, where("customerSnapshot.id", "==", customerId), fsLimit(maxRows))),
    getDocs(query(bookingsCol, where("customerSnapshot.customerId", "==", customerId), fsLimit(maxRows))),
  ];
  if (phone) tasks.push(getDocs(query(bookingsCol, where("customerPhone", "==", phone), fsLimit(30))));
  if (email) tasks.push(getDocs(query(bookingsCol, where("customerEmail", "==", email), fsLimit(30))));

  const settled = await Promise.allSettled(tasks);
  const map = new Map();
  settled.forEach((res) => {
    if (res.status !== "fulfilled") return;
    res.value.docs.forEach((d) => {
      const row = { id: d.id, ...(d.data() || {}) };
      if (crmBookingClientMatch(row, customer)) map.set(d.id, row);
    });
  });

  return Array.from(map.values()).sort((a, b) => {
    const da = crmBookingDate(a)?.getTime() || 0;
    const dbb = crmBookingDate(b)?.getTime() || 0;
    return dbb - da;
  });
}

function crmPickActivityBookings(rows = []) {
  const now = Date.now();
  const dated = (Array.isArray(rows) ? rows : [])
    .map((booking) => ({ booking, date: crmBookingDate(booking) }))
    .filter((x) => x.date && !Number.isNaN(x.date.getTime()));
  const future = dated.filter((x) => x.date.getTime() >= now).sort((a, b) => a.date - b.date)[0] || null;
  const past = dated.filter((x) => x.date.getTime() < now).sort((a, b) => b.date - a.date)[0] || null;
  return { future: future?.booking || null, past: past?.booking || null };
}

function crmLoyaltyCardsFromSummary(summary = {}, customer = {}) {
  const programs = Array.isArray(summary.programs) ? summary.programs.filter((p) => p && p.active !== false) : [];
  const items = Array.isArray(summary.items) ? summary.items : [];
  const byProgram = new Map(items.map((item) => [String(item?.program?.id || item?.account?.programId || ""), item]));
  const cards = programs.map((program) => {
    const item = byProgram.get(String(program.id || "")) || { program, account: {} };
    const account = item.account || {};
    const isPoints = String(program.type || "stamps") === "points";
    const required = Math.max(1, Number(isPoints ? (program.pointsRequired || account.pointsRequired || 100) : (program.stampsRequired || account.stampsRequired || 5)) || 1);
    const balance = Math.max(0, Number(isPoints ? (account.pointsBalance || 0) : (account.stampsBalance || 0)) || 0);
    const rewards = Math.max(0, Number(account.rewardsAvailable || 0) || 0);
    return {
      key: `loyalty-${program.id || program.name}`,
      type: isPoints ? "points" : "stamps",
      title: program.name || (isPoints ? "Programa de puntos" : "Programa de sellos"),
      eyebrow: isPoints ? "Fidelidad por puntos" : "Fidelidad por sellos",
      value: isPoints ? `${balance} pts` : `${balance}/${required}`,
      helper: rewards > 0 ? `${rewards} recompensa lista` : balance > 0 ? `Faltan ${Math.max(0, required - balance)} ${isPoints ? "pts" : "sellos"}` : "Sin actividad todavía",
    };
  });

  if (cards.length) return cards;

  const customerHasLoyalty = customer?.hasLoyalty === true ||
    customer?.loyaltyQrEnabled === true ||
    Boolean(customer?.publicLoyaltyToken || customer?.loyaltyPublicToken || customer?.loyaltyPoints || customer?.rewardPoints || customer?.points);

  if (!customerHasLoyalty) return [];

  const points = crmPointsEstimate(customer);
  return [{
    key: "loyalty-customer-active",
    type: "stamps",
    title: "Fidelidad activa",
    eyebrow: "Fidelidad",
    value: points > 0 ? `${points} pts` : "Activa",
    helper: customer?.loyaltyQrEnabled ? "QR habilitado" : "Cliente con fidelidad activa",
    active: true,
  }];
}


function crmLoyaltyEventDate(value) {
  try {
    if (!value) return "";
    const d = value.seconds ? new Date(value.seconds * 1000) : value.toDate ? value.toDate() : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(interfaceLocale(), { day: "2-digit", month: "short" });
  } catch (_) {
    return "";
  }
}

function crmLoyaltyEventKind(row = {}) {
  return String(row.type || row.action || row.reason || "").trim().toLowerCase();
}

function crmLoyaltyEventTitle(row = {}) {
  const kind = crmLoyaltyEventKind(row);
  const rawAmount = Number(row.amount ?? row.pointsEarned ?? row.stampsEarned ?? 0);
  const amount = Number.isFinite(rawAmount) ? rawAmount : 0;
  const programType = String(row.programType || "").toLowerCase();
  const unit = programType === "points" || row.pointsEarned ? "pts" : "sello";
  const reward = row.rewardLabel || "Recompensa";

  if (kind.includes("reversal") || kind.includes("reverse")) return `Recompensa reversada · ${reward}`;
  if (kind.includes("redeem")) return `Recompensa redimida · ${reward}`;
  if (kind.includes("earn") || kind.includes("paid_booking")) {
    const qty = Math.abs(Number(row.pointsEarned || row.stampsEarned || amount || 1));
    return `+${qty} ${unit}${unit === "sello" && qty === 1 ? "" : "s"} ganado${qty === 1 ? "" : "s"}`;
  }
  if (kind.includes("adjust") || kind.includes("manual_add") || kind.includes("manual_remove")) {
    const sign = amount >= 0 ? "+" : "-";
    const qty = Math.abs(amount || 1);
    return `${sign}${qty} ${unit}${unit === "sello" && qty === 1 ? "" : "s"} ajustado${qty === 1 ? "" : "s"}`;
  }
  return row.title || "Movimiento de fidelidad";
}

function crmLoyaltyEventTone(row = {}) {
  const kind = crmLoyaltyEventKind(row);
  if (kind.includes("reversal") || kind.includes("reverse")) return "reversal";
  if (kind.includes("redeem")) return "redeem";
  if (kind.includes("earn") || kind.includes("paid_booking")) return "earn";
  if (kind.includes("adjust") || kind.includes("manual")) return "adjust";
  return "activity";
}

function crmLoyaltyEventIcon(row = {}) {
  const tone = crmLoyaltyEventTone(row);
  if (tone === "reversal") return "↩";
  if (tone === "redeem") return "−";
  if (tone === "earn") return "+";
  return "•";
}

function crmLoyaltyEventMeta(row = {}) {
  const pieces = [
    row.programName || row.programLabel || "",
    row.bookingServiceName || row.serviceName || "",
    row.bookingId ? "Reserva vinculada" : "",
    row.reversed === true && crmLoyaltyEventTone(row) !== "reversal" ? "Reversada después" : "",
  ].filter(Boolean);
  return pieces.join(" · ") || (row.source === "qr_redemption" ? "QR de recompensa" : "Fidelidad");
}

function crmLoyaltyEventDetail(row = {}) {
  const pieces = [];
  if (row.bookingScheduledAt) pieces.push(`Fecha reserva ${crmLoyaltyEventDate(row.bookingScheduledAt)}`);
  if (row.actorEmail || row.createdByEmail || row.reversedByEmail) pieces.push(`por ${row.actorEmail || row.createdByEmail || row.reversedByEmail}`);
  const reason = String(row.reversalReason || row.reason || "").trim();
  if (reason && !["booking_payment_paid", "client_generated_redemption_qr"].includes(reason)) pieces.push(`motivo: ${reason}`);
  return pieces.join(" · ");
}


function crmMembershipCardFromData(plans = [], membershipRows = []) {
  const activePlans = (Array.isArray(plans) ? plans : []).filter((p) => p && p.active !== false);
  if (!activePlans.length) return null;
  const rows = Array.isArray(membershipRows) ? membershipRows : [];
  const active = rows.find((item) => membershipStatusValue(item?.membership?.status) === "active") || null;
  if (active) {
    const membership = active.membership || {};
    const plan = active.plan || activePlans.find((p) => String(p.id) === String(membership.planId)) || {};
    const benefitType = membershipBenefitType(membership, plan);
    const credits = membershipCreditStats(membership, plan);
    const ends = formatDate(membership.endDate || membership.expiresAt);
    const discount = Math.max(0, Number(membership.discountPercent ?? plan.discountPercent ?? 0) || 0);
    const helperBase = ends && ends !== "Sin visitas registradas en Loop" ? `Renueva / vence ${ends}` : "Cliente con membresía activa";
    return {
      key: `membership-${membership.id || plan.id || "active"}`,
      type: "membership",
      title: plan.name || membership.planName || "Membresía activa",
      eyebrow: benefitType === "unlimited" ? "Membresía ilimitada" : benefitType === "discount" ? "Membresía con descuento" : "Membresía activa",
      value: benefitType === "unlimited" ? "Ilimitado" : benefitType === "discount" ? `${discount || 0}%` : (credits.included > 0 ? `${credits.remaining}/${credits.included}` : membershipStatusLabel(membership.status)),
      helper: benefitType === "unlimited" ? `Uso ilimitado · ${helperBase}` : benefitType === "discount" ? `Solo descuento · ${helperBase}` : helperBase,
      active: true,
    };
  }
  return {
    key: "membership-empty",
    type: "membership",
    title: "Sin membresía activa",
    eyebrow: "Membresías disponibles",
    value: `${activePlans.length} plan${activePlans.length === 1 ? "" : "es"}`,
    helper: "El negocio tiene membresías, pero este cliente no tiene una activa.",
    active: false,
  };
}

function CustomerDetailMatch({ customer, businessId, onSave = async () => {}, navigate = null }) {
  useLanguage();
  const [editOpen, setEditOpen] = useState(false);
  const [editFocus, setEditFocus] = useState("profile");
  const [actionsOpen, setActionsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  usePendingEditor({pending:editOpen,busy:savingEdit,title:"Cliente",discard:()=>setEditOpen(false)});
  const [editError, setEditError] = useState("");
  const [draft, setDraft] = useState({ name: "", phone: "", email: "", notes: "", tags: "" });
  const [bookingHistory, setBookingHistory] = useState([]);
  const [bookingHistoryLoading, setBookingHistoryLoading] = useState(false);
  const [benefitsLoading, setBenefitsLoading] = useState(false);
  const [loyaltySummary, setLoyaltySummary] = useState({ programs: [], items: [], recentEvents: [], timelineEvents: [] });
  const [membershipSummary, setMembershipSummary] = useState({ plans: [], items: [] });
  const [benefitsManagerOpen, setBenefitsManagerOpen] = useState(false);
  const [benefitsRefreshKey, setBenefitsRefreshKey] = useState(0);

  useEffect(() => {
    setDraft({
      name: customer?.name || customer?.customerName || customer?.fullName || "",
      phone: crmContactPhone(customer),
      email: crmContactEmail(customer),
      notes: customer?.notes || customer?.customerNotes || "",
      tags: Array.isArray(customer?.tags) ? customer.tags.join(", ") : "",
    });
    setEditError("");
    setActionsOpen(false);
    setHistoryOpen(false);
    setBenefitsManagerOpen(false);
  }, [customer?.id]);

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!businessId || !customer?.id) {
        setBookingHistory([]);
        return;
      }
      setBookingHistoryLoading(true);
      try {
        const rows = await loadCustomerBookingHistory(db, businessId, customer, 90);
        if (alive) setBookingHistory(rows);
      } catch (err) {
        console.warn("[CustomersPage] customer booking history failed", err);
        if (alive) setBookingHistory([]);
      } finally {
        if (alive) setBookingHistoryLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, [businessId, customer?.id]);

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!businessId || !customer?.id) {
        setLoyaltySummary({ programs: [], items: [], recentEvents: [], timelineEvents: [] });
        setMembershipSummary({ plans: [], items: [] });
        return;
      }
      setBenefitsLoading(true);
      try {
        const functions = getFunctions();
        const [loyaltyRes, plansRes, membershipsRes] = await Promise.allSettled([
          httpsCallable(functions, "getCustomerLoyaltySummary")({ businessId, customerId: customer.id }),
          httpsCallable(functions, "getBusinessMembershipPlans")({ businessId }),
          httpsCallable(functions, "getCustomerMemberships")({ businessId, customerId: customer.id }),
        ]);
        if (!alive) return;
        const loyaltyData = loyaltyRes.status === "fulfilled" ? (loyaltyRes.value?.data || {}) : {};
        const plansData = plansRes.status === "fulfilled" ? (plansRes.value?.data || {}) : {};
        const membershipsData = membershipsRes.status === "fulfilled" ? (membershipsRes.value?.data || {}) : {};
        setLoyaltySummary({
          programs: Array.isArray(loyaltyData.programs) ? loyaltyData.programs : [],
          items: Array.isArray(loyaltyData.items) ? loyaltyData.items : [],
          recentEvents: Array.isArray(loyaltyData.recentEvents) ? loyaltyData.recentEvents : [],
          timelineEvents: Array.isArray(loyaltyData.timelineEvents) ? loyaltyData.timelineEvents : (Array.isArray(loyaltyData.recentEvents) ? loyaltyData.recentEvents : []),
        });
        setMembershipSummary({
          plans: Array.isArray(plansData.plans) ? plansData.plans : [],
          items: Array.isArray(membershipsData.items) ? membershipsData.items : [],
        });
      } catch (err) {
        console.warn("[CustomersPage] dynamic benefits failed", err);
        if (alive) {
          setLoyaltySummary({ programs: [], items: [], recentEvents: [], timelineEvents: [] });
          setMembershipSummary({ plans: [], items: [] });
        }
      } finally {
        if (alive) setBenefitsLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, [businessId, customer?.id, benefitsRefreshKey]);

  const closeBenefitsManager = () => {
    setBenefitsManagerOpen(false);
    setBenefitsRefreshKey((value) => value + 1);
  };

  if (!customer) {
    return (
      <aside className="cp-detail cp-detail-empty cp-detail-match-empty">
        <User size={28} />
        <h3>{t("Selecciona un cliente")}</h3>
        <p>{t("Verás notas, etiquetas, fidelidad y acciones rápidas.")}</p>
      </aside>
    );
  }

  const phone = crmContactPhone(customer);
  const email = crmContactEmail(customer);
  const created = formatDate(customer.createdAt || customer.lastBookingAt || customer.updatedAt);
  const { future: nextBooking, past: lastBooking } = crmPickActivityBookings(bookingHistory);
  const featuredBooking = nextBooking || lastBooking || null;
  const featuredIsFuture = Boolean(nextBooking);
  const loyaltyCards = crmLoyaltyCardsFromSummary(loyaltySummary, customer);
  const membershipCard = crmMembershipCardFromData(membershipSummary.plans, membershipSummary.items);
  const benefitCards = [...loyaltyCards, ...(membershipCard ? [membershipCard] : [])];
  const loyaltyTimelineEvents = (Array.isArray(loyaltySummary.timelineEvents) ? loyaltySummary.timelineEvents : Array.isArray(loyaltySummary.recentEvents) ? loyaltySummary.recentEvents : []).slice(0, 12);
  const loyaltyRecentEvents = loyaltyTimelineEvents.slice(0, 8);
  const waDigits = String(phone || "").replace(/\D/g, "");
  const waUrl = waDigits ? `https://wa.me/${waDigits}` : "";
  const detailMeta = [
    created ? `Cliente desde ${created}` : "Cliente registrado",
    crmOriginLabel(customer),
    `ID #${String(customer.id || "").slice(0, 8) || "CL-00072"}`,
  ].filter(Boolean).join(" · ");

  const openEdit = (focus = "profile") => {
    setEditFocus(focus);
    setDraft({
      name: customer?.name || customer?.customerName || customer?.fullName || "",
      phone: crmContactPhone(customer),
      email: crmContactEmail(customer),
      notes: customer?.notes || customer?.customerNotes || "",
      tags: Array.isArray(customer?.tags) ? customer.tags.join(", ") : "",
    });
    setEditError("");
    setEditOpen(true);
    setActionsOpen(false);
  };

  const saveEdit = async () => {
    if (!customer?.id || savingEdit) return;
    const nextName = String(draft.name || "").trim();
    const nextPhone = String(draft.phone || "").trim();
    const nextEmail = String(draft.email || "").trim().toLowerCase();
    const nextNotes = String(draft.notes || "").trim();
    const tags = String(draft.tags || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    if (!nextName) {
      setEditError("El nombre del cliente es obligatorio.");
      return;
    }

    setSavingEdit(true);
    setEditError("");
    try {
      await onSave({
        ...customer,
        name: nextName,
        customerName: nextName,
        displayName: nextName,
        nameKey: normalizeCustomerSearchText(nextName),
        phone: nextPhone,
        phoneNormalized: normalizeCustomerPhone(nextPhone),
        whatsapp: nextPhone,
        whatsApp: nextPhone,
        email: nextEmail,
        emailNormalized: normalizeCustomerEmail(nextEmail),
        customerEmail: nextEmail,
        notes: nextNotes,
        customerNotes: nextNotes,
        tags,
      });
      setEditOpen(false);
    } catch (err) {
      console.error("[CustomersPage] customer edit failed", err);
      setEditError(err?.message || "No pudimos guardar este cliente.");
    } finally {
      setSavingEdit(false);
    }
  };

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      setActionsOpen(false);
    } catch (_) {
      window.alert(`No pude copiar ${label}.`);
    }
  };

  const createReservation = () => {
    setActionsOpen(false);
    try {
      const target = `/business/${encodeURIComponent(businessId || "")}/calendar?customerId=${encodeURIComponent(customer.id || "")}`;
      if (typeof navigate === "function" && businessId) navigate(target, { state: { prefillCustomer: customer } });
      else window.alert("Abre la agenda para crear una reserva con este cliente.");
    } catch (_) {
      window.alert("Abre la agenda para crear una reserva con este cliente.");
    }
  };

  const sendWhatsapp = () => {
    if (waUrl) window.open(waUrl, "_blank", "noopener,noreferrer");
    else openEdit("contact");
  };

  return (
    <aside className="cp-detail cp-detail-match">
      <style>{`
        .cp-loyalty-activity-mini{margin-top:12px;border:1px solid rgba(226,232,240,.9);background:#fff;border-radius:18px;padding:10px;display:grid;gap:7px}
        .cp-loyalty-activity-head{display:flex;align-items:center;justify-content:space-between;gap:10px}
        .cp-loyalty-activity-head span{display:block;color:#00675f;font-size:10px;font-weight:950;letter-spacing:.13em;text-transform:uppercase}
        .cp-loyalty-activity-head em{font-style:normal;color:#64748b;font-size:11px;font-weight:900;white-space:nowrap}
        .cp-loyalty-activity-row{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:flex-start;gap:9px;padding:8px 0;border-top:1px solid rgba(226,232,240,.85)}
        .cp-loyalty-activity-row:first-of-type{border-top:0}
        .cp-loyalty-activity-dot{width:27px;height:27px;border-radius:10px;display:grid;place-items:center;background:#ecfdf5;color:#00675f;font-size:13px;font-weight:950}
        .cp-loyalty-activity-row.is-redeem .cp-loyalty-activity-dot{background:#fff7ed;color:#c2410c}
        .cp-loyalty-activity-row.is-reversal .cp-loyalty-activity-dot{background:#eff6ff;color:#1d4ed8}
        .cp-loyalty-activity-row.is-adjust .cp-loyalty-activity-dot{background:#f8fafc;color:#475569}
        .cp-loyalty-activity-row strong{display:block;color:#0f172a;font-size:12px;line-height:1.1;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .cp-loyalty-activity-row small{display:block;margin-top:3px;color:#64748b;font-size:11px;line-height:1.2;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .cp-loyalty-activity-row small.cp-loyalty-activity-detail{color:#0f766e;font-weight:850}
        .cp-loyalty-activity-row em{color:#64748b;font-size:11px;font-style:normal;font-weight:900;white-space:nowrap}
      `}</style>
      <div className="cp-detail-profile-head">
        <div className="cp-detail-avatar is-small">{getInitials(customer.name)}</div>
        <div className="cp-detail-profile-copy">
          <div className="cp-detail-name-row">
            <h2>{customer.name || t("Cliente")}</h2>
            <span className="cp-status-chip">{t("Activo")}</span>
            <span className="cp-status-chip is-segment">{crmSegmentLabel(customer)}</span>
          </div>
          <p>{detailMeta}</p>
        </div>
        <div className="cp-more-wrap">
          <button className="cp-detail-menu" type="button" aria-label={t("Más opciones")} onClick={() => setActionsOpen((v) => !v)}><MoreHorizontal size={18} /></button>
          {actionsOpen ? (
            <div className="cp-more-menu">
              <button type="button" onClick={() => openEdit("profile")}>{t("Editar cliente")}</button>
              <button type="button" onClick={createReservation}>{t("Crear reserva")}</button>
              <button type="button" onClick={sendWhatsapp}>{waUrl ? "Enviar WhatsApp" : t("Agregar teléfono")}</button>
              {phone ? <button type="button" onClick={() => copyValue(phone, "teléfono")}>{t("Copiar teléfono")}</button> : null}
              {email ? <button type="button" onClick={() => copyValue(email, "email")}>{t("Copiar email")}</button> : null}
            </div>
          ) : null}
        </div>
      </div>


      <section className="cp-detail-section cp-contact-section">
        <div className="cp-section-title-row">
          <h3>{t("Contacto")}</h3>
          {waUrl ? <a className="cp-soft-action cp-chat-action" href={waUrl} target="_blank" rel="noreferrer"><MessageCircle size={14} />{t("Abrir chat")}</a> : <button type="button" className="cp-soft-action cp-chat-action" onClick={() => openEdit("contact")}><MessageCircle size={14} />{t("Agregar teléfono")}</button>}
        </div>
        <div className="cp-contact-grid">
          <span><Phone size={14} />{phone || "Sin teléfono"}</span><strong>{crmWhatsappOk(customer) ? "WhatsApp" : "Sin WhatsApp"}</strong>
          <span><Mail size={14} />{email || "Sin email"}</span><strong>{t("Email")}</strong>
        </div>
      </section>

      <CustomerDossier businessId={businessId} customer={customer} onSave={onSave} bookings={bookingHistory.map(record => ({ ...record, crmTimestamp: crmBookingDate(record)?.toISOString(), crmServiceLabel: crmBookingServiceLabel(record), crmDateLabel: crmBookingDateNice(record), crmStatusLabel: crmBookingStatusLabel(record) }))} />
      <section className="cp-detail-section cp-activity-section">
        <div className="cp-section-title-row">
          <h3>{t("Actividad reciente")}</h3>
          <button type="button" onClick={() => setHistoryOpen(true)} disabled={bookingHistoryLoading || bookingHistory.length === 0}>{t("Ver todo")}</button>
        </div>
        <div className="cp-activity-list cp-activity-real-list">
          {bookingHistoryLoading ? (
            <div><CalendarDays size={15} /><span><strong>{t("Cargando reservas...")}</strong><small>{t("Buscando historial del cliente.")}</small></span><em>—</em></div>
          ) : featuredBooking ? (
            <div>
              <CalendarDays size={15} />
              <span>
                <strong>{featuredIsFuture ? "Próxima reserva" : "Última reserva"}</strong>
                <small>{crmBookingServiceLabel(featuredBooking)} · {crmBookingStatusLabel(featuredBooking)} · {crmBookingSourceLabel(featuredBooking)}</small>
              </span>
              <em>{crmBookingDateNice(featuredBooking)}{crmBookingTimeNice(featuredBooking) ? ` · ${crmBookingTimeNice(featuredBooking)}` : ""}</em>
            </div>
          ) : (
            <div><CalendarDays size={15} /><span><strong>{t("Sin reservas todavía")}</strong><small>{t("Cuando el cliente tenga reservas, aparecerán aquí.")}</small></span><em>—</em></div>
          )}
        </div>
      </section>

      <CustomerSessionPrograms businessId={businessId} customerId={customer.id} onSchedule={typeof navigate==='function' ? program=>navigate(`/business/${encodeURIComponent(businessId)}/calendar`,{state:{prefillCustomer:customer,prefillSessionProgram:program}}) : null}/>
      {["membership", "loyalty"].map((kind) => {
        const cards = benefitCards.filter((card) => kind === "membership" ? card.type === "membership" : card.type !== "membership");
        return <section key={kind} className="cp-detail-section cp-benefits-section cp-benefits-separated">
          <div className="cp-section-title-row">
            <h3>{kind === "membership" ? t("Membresías") : t("Fidelidad")}</h3>
            <button type="button" className="cp-soft-action" onClick={() => setBenefitsManagerOpen(kind)}>{kind === "membership" ? "Gestionar membresías" : "Gestionar fidelidad"}</button>
          </div>
          {benefitsLoading ? <p>{t("Cargando…")}</p> : cards.length ? <div className="cp-benefits-grid">
            {cards.map((card) => <div key={card.key} className={`cp-benefit-card is-${card.type}`}>
              <small>{card.eyebrow}</small><strong>{card.value}</strong><em>{card.title}{card.helper ? ` · ${card.helper}` : ""}</em>
            </div>)}
          </div> : <p>{kind === "membership" ? "Sin membresías activas" : "Sin beneficios de fidelidad"}</p>}
          {kind === "loyalty" && <>
          {!benefitsLoading && loyaltyRecentEvents.length ? (
            <details className="cp-loyalty-activity-mini">
              <summary className="cp-loyalty-activity-head">
                <span>{t("Historial de fidelidad")}</span>
                <em>{loyaltyTimelineEvents.length}{" " + t("movimiento")}{loyaltyTimelineEvents.length === 1 ? "" : "s"}</em>
              </summary>
              {loyaltyRecentEvents.map((event) => {
                const detail = crmLoyaltyEventDetail(event);
                return (
                  <div className={`cp-loyalty-activity-row is-${crmLoyaltyEventTone(event)}`} key={event.id || `${event.type}-${event.createdAt?.seconds || event.bookingId || event.amount}`}>
                    <i className="cp-loyalty-activity-dot">{crmLoyaltyEventIcon(event)}</i>
                    <div>
                      <strong>{crmLoyaltyEventTitle(event)}</strong>
                      <small>{crmLoyaltyEventMeta(event)}</small>
                      {detail ? <small className="cp-loyalty-activity-detail">{detail}</small> : null}
                    </div>
                    <em>{crmLoyaltyEventDate(event.createdAt || event.bookingScheduledAt)}</em>
                  </div>
                );
              })}
            </details>
          ) : null}

          </>}
        </section>;
      })}

      {benefitsManagerOpen && typeof document !== "undefined" ? createPortal((
        <div className="cp-benefits-manager-backdrop cp-benefits-manager-backdrop-premium" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeBenefitsManager();
        }}>
          <section className="cp-benefits-manager-modal cp-benefits-manager-modal-premium" onMouseDown={(event) => event.stopPropagation()}>
            <CustomerDetail initialBenefitView={benefitsManagerOpen} customer={customer} businessId={businessId} onClose={closeBenefitsManager} onSave={onSave} />
          </section>
        </div>
      ), document.body) : null}

      {historyOpen && typeof document !== "undefined" ? createPortal((
        <div className="cp-history-backdrop cp-history-backdrop-premium" onClick={() => setHistoryOpen(false)}>
          <section className="cp-history-modal cp-history-modal-premium" onClick={(e) => e.stopPropagation()}>
            <div className="cp-history-head">
              <div>
                <span>{t("Historial de reservas")}</span>
                <h3>{customer.name || t("Cliente")}</h3>
                <p>{t("Reservas manuales y online asociadas a este cliente.")}</p>
              </div>
              <button type="button" onClick={() => setHistoryOpen(false)}>×</button>
            </div>
            <div className="cp-history-list">
              {bookingHistory.length === 0 ? <div className="cp-history-empty">{t("Sin reservas registradas.")}</div> : null}
              {bookingHistory.map((booking) => {
                const d = crmBookingDate(booking);
                const isFuture = d && d.getTime() >= Date.now();
                return (
                  <article key={booking.id || `${crmBookingServiceLabel(booking)}-${crmBookingDateNice(booking)}`} className={`cp-history-row ${isFuture ? "is-future" : ""}`}>
                    <div className="cp-history-icon"><CalendarDays size={15} /></div>
                    <div>
                      <strong>{crmBookingServiceLabel(booking)}</strong>
                      <span>{crmBookingStatusLabel(booking)} · {crmBookingSourceLabel(booking)}{booking.employeeName || booking.workerName ? ` · ${booking.employeeName || booking.workerName}` : ""}</span>
                    </div>
                    <em>{crmBookingDateNice(booking)}{crmBookingTimeNice(booking) ? ` · ${crmBookingTimeNice(booking)}` : ""}</em>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      ), document.body) : null}

      {editOpen && typeof document !== "undefined" ? createPortal((
        <div className="cp-edit-backdrop cp-edit-backdrop-premium" onClick={() => savingEdit ? null : setEditOpen(false)}>
          <section className="cp-edit-modal cp-edit-modal-premium" onClick={(e) => e.stopPropagation()}>
            <div className="cp-edit-head">
              <div>
                <span>{t("Editar cliente")}</span>
                <h3>{customer.name || t("Cliente")}</h3>
                <p>{t("Actualiza contacto, notas y etiquetas del CRM.")}</p>
              </div>
              <button type="button" onClick={() => setEditOpen(false)} disabled={savingEdit}>×</button>
            </div>

            <div className="cp-edit-body">
              <label className={editFocus === "profile" ? "is-focus" : ""}>{t("Nombre del cliente")}<input value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} /></label>
              <label className={editFocus === "contact" ? "is-focus" : ""}>{t("Teléfono / WhatsApp")}<input value={draft.phone} onChange={(e) => setDraft((prev) => ({ ...prev, phone: e.target.value }))} placeholder="+507 6000-0000" /></label>
              <label className={editFocus === "contact" ? "is-focus" : ""}>{t("Email")}<input value={draft.email} onChange={(e) => setDraft((prev) => ({ ...prev, email: e.target.value }))} placeholder="cliente@email.com" /></label>
              <label>{t("Etiquetas")}<input value={draft.tags} onChange={(e) => setDraft((prev) => ({ ...prev, tags: e.target.value }))} placeholder={t("VIP, frecuente, color...")} /></label>
              <label className={`cp-edit-full ${editFocus === "notes" ? "is-focus" : ""}`}>{t("Notas internas")}<textarea value={draft.notes} onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))} rows={4} placeholder={t("Preferencias, alergias, horarios, detalles importantes...")} /></label>
              {editError ? <div className="cp-edit-error">{editError}</div> : null}
            </div>

            <div className="cp-edit-actions">
              <button type="button" className="ghost" disabled={savingEdit} onClick={() => setEditOpen(false)}>{t("Cancelar")}</button>
              <button type="button" disabled={savingEdit} onClick={saveEdit}>{savingEdit ? t("Guardando...") : t("Guardar cliente")}</button>
            </div>
          </section>
        </div>
      ), document.body) : null}
    </aside>
  );
}


function CustomerInlinePreview({ customer, businessId, navigate = null, onOpenProfile = () => {} }) {
  useLanguage();
  if (!customer) return null;
  const phone = crmContactPhone(customer);
  const email = crmContactEmail(customer);
  const waDigits = String(phone || "").replace(/\D/g, "");
  const waUrl = waDigits ? `https://wa.me/${waDigits}` : "";
  const note = crmPrimaryNote(customer);
  const tags = Array.isArray(customer.tags) ? customer.tags.slice(0, 4) : [];
  const createReservation = () => {
    try {
      if (typeof navigate === "function" && businessId) {
        navigate(`/business/${encodeURIComponent(businessId)}/calendar?customerId=${encodeURIComponent(customer.id || "")}`, { state: { prefillCustomer: customer } });
      }
    } catch (_) {}
  };

  return (
    <div className="cp-inline-summary-v25">
      <div className="cp-inline-note-v25">
        <strong>{t("Nota rápida")}</strong>
        <span>{note}</span>
      </div>

      {tags.length ? (
        <div className="cp-inline-tags-v25">
          {tags.map((tag) => <span key={tag}>{tag}</span>)}
        </div>
      ) : null}

      <div className="cp-inline-actions-v25">
        <button type="button" onClick={createReservation}>{t("Crear reserva")}</button>
        {waUrl ? <a href={waUrl} target="_blank" rel="noreferrer">WhatsApp</a> : null}
        <button type="button" className="primary" onClick={() => onOpenProfile(customer)}>{t("Ver perfil")}</button>
      </div>
    </div>
  );
}

async function scanCrmCustomersForSearch(dbClient, businessId, term, maxRows = 2500) {
  const customersCol = collection(dbClient, `businesses/${businessId}/customers`);
  const normalized = normalizeCustomerSearchText(term);
  const digits = String(term || "").replace(/\D/g, "");
  const rows = [];
  let lastDoc = null;

  while (rows.length < maxRows) {
    const batchSize = Math.min(450, maxRows - rows.length);
    const qBuilt = lastDoc
      ? query(customersCol, orderBy("__name__"), startAfter(lastDoc), fsLimit(batchSize))
      : query(customersCol, orderBy("__name__"), fsLimit(batchSize));
    const snap = await getDocs(qBuilt);
    if (snap.empty) break;
    snap.docs.forEach((d) => {
      const row = crmCustomerRowFromDoc(d);
      if (!crmCustomerIsMergedOrHidden(row) && customerMatchesCrmSearch(row, normalized, digits)) rows.push(row);
    });
    lastDoc = snap.docs[snap.docs.length - 1] || null;
    if (snap.docs.length < batchSize) break;
  }

  return mergeCrmCustomerRows(rows).slice(0, 120);
}

async function searchCrmCustomers(dbClient, businessId, term, maxRows = 120, options = {}) {
  const raw = String(term || "").trim();
  if (!dbClient || !businessId || raw.length < 2) return [];
  const customersCol = collection(dbClient, `businesses/${businessId}/customers`);
  const normalized = normalizeCustomerSearchText(raw);
  const digits = raw.replace(/\D/g, "");
  const phone = normalizeCustomerPhone(raw);
  const email = normalizeCustomerEmail(raw);
  const promises = [];

  const pushQuery = (qBuilt) => {
    promises.push(getDocs(qBuilt).catch((err) => {
      console.warn("[CustomersPage] CRM search query failed", err);
      return null;
    }));
  };

  if (normalized) {
    pushQuery(query(customersCol, orderBy("nameKey"), where("nameKey", ">=", normalized), where("nameKey", "<=", `${normalized}\uf8ff`), fsLimit(maxRows)));
    pushQuery(query(customersCol, orderBy("searchableName"), where("searchableName", ">=", normalized), where("searchableName", "<=", `${normalized}\uf8ff`), fsLimit(maxRows)));
  }

  normalized.split(/\s+/).filter((word) => word.length >= 3).slice(0, 4).forEach((word) => {
    pushQuery(query(customersCol, orderBy("nameKey"), where("nameKey", ">=", word), where("nameKey", "<=", `${word}\uf8ff`), fsLimit(40)));
  });

  if (phone && digits.length >= 3) {
    pushQuery(query(customersCol, where("phoneNormalized", "==", phone), fsLimit(40)));
    pushQuery(query(customersCol, where("phoneE164", "==", phone), fsLimit(40)));
  }

  if (digits && digits.length >= 3) {
    pushQuery(query(customersCol, where("phoneDigits", "==", digits), fsLimit(40)));
  }

  if (email && raw.includes("@")) {
    pushQuery(query(customersCol, where("emailNormalized", "==", email), fsLimit(40)));
  }

  const settled = await Promise.allSettled(promises);
  const rows = [];
  settled.forEach((res) => {
    if (res.status !== "fulfilled" || !res.value) return;
    res.value.docs.forEach((d) => rows.push(crmCustomerRowFromDoc(d)));
  });

  const strictRows = mergeCrmCustomerRows(rows.filter((customer) => !crmCustomerIsMergedOrHidden(customer)))
    .filter((customer) => customerMatchesCrmSearch(customer, normalized, digits))
    .slice(0, maxRows);

  // V25: keep indexed search fast by default. The expensive legacy scan is
  // triggered later by the component only when the user pauses on a long search.
  if (!options?.deepScan || strictRows.length >= Math.min(8, maxRows) || raw.length < 4) return strictRows;
  const scannedRows = await scanCrmCustomersForSearch(dbClient, businessId, raw, options?.deepScanMaxRows || 1800).catch((err) => {
    console.warn("[CustomersPage] CRM bounded search scan failed", err);
    return [];
  });
  return mergeCrmCustomerRows([...strictRows, ...scannedRows])
    .filter((customer) => customerMatchesCrmSearch(customer, normalized, digits))
    .slice(0, maxRows);
}


async function loadDuplicateReviewRows(dbClient, businessId, maxRows = 2500) {
  if (!dbClient || !businessId) return [];
  const customersCol = collection(dbClient, `businesses/${businessId}/customers`);
  const rows = [];
  let lastDoc = null;

  while (rows.length < maxRows) {
    const batchSize = Math.min(400, maxRows - rows.length);
    const qBuilt = lastDoc
      ? query(customersCol, orderBy("__name__"), startAfter(lastDoc), fsLimit(batchSize))
      : query(customersCol, orderBy("__name__"), fsLimit(batchSize));
    const snap = await getDocs(qBuilt);
    if (snap.empty) break;
    snap.docs.forEach((d) => {
      const row = crmCustomerRowFromDoc(d);
      if (!crmCustomerIsMergedOrHidden(row)) rows.push(row);
    });
    lastDoc = snap.docs[snap.docs.length - 1] || null;
    if (snap.docs.length < batchSize) break;
  }

  return rows;
}

function duplicateNameKey(customer = {}) {
  return normalizeCustomerSearchText(customer.name || customer.customerName || customer.fullName || customer.displayName || "")
    .replace(/\s+/g, " ")
    .trim();
}

function duplicatePhoneKey(customer = {}) {
  return normalizeCustomerPhone(customer.phoneNormalized || customer.phone || customer.whatsapp || customer.whatsApp || "");
}

function duplicateEmailKey(customer = {}) {
  return normalizeCustomerEmail(customer.emailNormalized || customer.email || customer.customerEmail || "");
}

function customerHistoryScore(customer = {}) {
  return (
    Number(customer.totalBookings || 0) * 10 +
    Number(customer.importedTotalBookings || 0) * 8 +
    (customer.linkedUserId || customer.linkedUserUid || customer.userId || customer.userUid ? 50 : 0) +
    (duplicatePhoneKey(customer) ? 18 : 0) +
    (duplicateEmailKey(customer) ? 14 : 0) +
    (customer.notes ? 6 : 0) +
    (Array.isArray(customer.tags) && customer.tags.length ? 4 : 0)
  );
}


function duplicateGroupHasContactConflict(group = {}) {
  const items = Array.isArray(group.items) ? group.items : [];
  const phoneSet = new Set(items.map(duplicatePhoneKey).filter(Boolean));
  const emailSet = new Set(items.map(duplicateEmailKey).filter(Boolean));
  if (group.kind === "phone") return emailSet.size > 1;
  if (group.kind === "email") return phoneSet.size > 1;
  return true;
}

// LOOP CRM NAME-ONLY SMART DEDUPE V26
// Name-only auto-merge is allowed only for empty duplicate shells.
// Anything with contact, linked Loop account, real booking/activity, loyalty,
// membership or meaningful CRM notes stays manual.
function duplicateCustomerNumberValue(customer = {}, fields = []) {
  for (const field of fields) {
    const value = Number(customer?.[field] ?? 0);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return 0;
}

function duplicateCustomerHasLinkedLoopAccount(customer = {}) {
  return Boolean(
    customer?.hasUserAccount === true ||
    customer?.linkedUserId ||
    customer?.linkedUserUid ||
    customer?.userId ||
    customer?.userUid ||
    customer?.customerUserId ||
    customer?.customerUserUid
  );
}

function duplicateCustomerHasContactIdentity(customer = {}) {
  return Boolean(duplicatePhoneKey(customer) || duplicateEmailKey(customer));
}

function duplicateCustomerHasBenefitOrRealHistory(customer = {}) {
  const c = customer && typeof customer === "object" ? customer : {};
  const totalBookings = Number(c.totalBookings || 0) || 0;
  const importedBookings = Number(c.importedTotalBookings || 0) || 0;
  if (totalBookings > 0 && totalBookings > importedBookings) return true;

  if (duplicateCustomerNumberValue(c, [
    "completedBookings",
    "cancelledBookings",
    "canceledBookings",
    "noShowBookings",
    "bookingsCount",
    "reservationsCount",
    "realBookingsCount",
    "totalSpent",
    "lifetimeValue",
    "loyaltyPoints",
    "rewardPoints",
    "points",
    "pointsBalance",
    "stampsBalance",
    "rewardsAvailable",
    "membershipCreditsRemaining",
    "membershipCreditsUsed",
    "activeMembershipsCount",
  ]) > 0) return true;

  if (
    c.hasLoyalty === true ||
    c.loyaltyQrEnabled === true ||
    c.membershipActive === true ||
    c.hasActiveMembership === true ||
    c.activeMembership === true ||
    c.customerMembershipActive === true
  ) return true;

  if (
    c.publicLoyaltyToken ||
    c.loyaltyPublicToken ||
    c.customerMembershipId ||
    c.membershipId ||
    c.membershipPlanId ||
    c.activeMembershipId ||
    c.lastBookingAt ||
    c.lastVisitAt
  ) return true;

  const notes = String(c.notes || c.customerNotes || "").trim();
  if (notes && !/^(importad[oa]|import|archivo|excel|csv)$/i.test(notes)) return true;

  return false;
}

function duplicateGroupIsSafeEmptyNameOnly(group = {}) {
  const items = Array.isArray(group.items) ? group.items : [];
  if (group.kind !== "name" || items.length < 2 || !group.primary?.id || !group.secondary?.length) return false;
  const keys = new Set(items.map(duplicateNameKey).filter(Boolean));
  if (keys.size !== 1) return false;
  return items.every((customer) => (
    !duplicateCustomerHasContactIdentity(customer) &&
    !duplicateCustomerHasLinkedLoopAccount(customer) &&
    !duplicateCustomerHasBenefitOrRealHistory(customer)
  ));
}

function duplicateGroupAutoMergeKindV26(group = {}) {
  if (group.kind === "phone" || group.kind === "email") return group.kind;
  if (duplicateGroupIsSafeEmptyNameOnly(group)) return "name_empty";
  return group.kind || "manual";
}

function buildDuplicateReviewGroups(rows = []) {
  const buckets = new Map();
  const add = (kind, key, customer) => {
    if (!key || !customer?.id) return;
    if (kind === "name" && key.length < 3) return;
    const bucketKey = `${kind}:${key}`;
    const bucket = buckets.get(bucketKey) || { key: bucketKey, kind, matchValue: key, items: [] };
    if (!bucket.items.some((item) => item.id === customer.id)) bucket.items.push(customer);
    buckets.set(bucketKey, bucket);
  };

  rows.forEach((customer) => {
    const phone = duplicatePhoneKey(customer);
    const email = duplicateEmailKey(customer);
    const name = duplicateNameKey(customer);
    if (phone) add("phone", phone, customer);
    if (email) add("email", email, customer);
    if (!phone && !email && name) add("name", name, customer);
  });

  return Array.from(buckets.values())
    .filter((group) => group.items.length >= 2)
    .map((group) => {
      const sorted = [...group.items].sort((a, b) => customerHistoryScore(b) - customerHistoryScore(a));
      const primary = sorted[0];
      const secondary = sorted.slice(1);
      const confidence = group.kind === "phone" ? "Alta" : group.kind === "email" ? "Alta" : "Media";
      const reason = group.kind === "phone"
        ? "Mismo teléfono normalizado"
        : group.kind === "email"
          ? "Mismo email normalizado"
          : "Mismo nombre sin teléfono/email";
      return { ...group, items: sorted, primary, secondary, confidence, reason };
    })
    .sort((a, b) => {
      const rank = { phone: 3, email: 2, name: 1 };
      return (rank[b.kind] || 0) - (rank[a.kind] || 0) || b.items.length - a.items.length;
    });
}

function DuplicateReviewModal({ open, businessId, onClose, onMerged = () => {} }) {
  useLanguage();
  const [loading, setLoading] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const stopRequestedRef = useRef(false);

  const loadJob = async () => {
    if (!businessId) return;
    setLoading(true);
    setError("");
    try {
      const callable = httpsCallable(getFunctions(), "getCustomerCleanupJobR29");
      const result = await callable({ businessId });
      setJob(result?.data?.job || null);
    } catch (err) {
      console.error("[CustomersPage] getCustomerCleanupJobR29 failed", err);
      setError(err?.message || "No pudimos cargar el estado de la limpieza.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      stopRequestedRef.current = true;
      return;
    }
    stopRequestedRef.current = false;
    setMessage("");
    loadJob();
    return () => {
      stopRequestedRef.current = true;
    };
  }, [open, businessId]);

  const createPlan = async () => {
    if (!businessId || planning || processing) return;
    setPlanning(true);
    setError("");
    setMessage("Analizando todos los clientes del negocio…");
    try {
      const callable = httpsCallable(getFunctions(), "createCustomerCleanupJobR29");
      const result = await callable({ businessId });
      const nextJob = result?.data?.job || null;
      setJob(nextJob);
      setMessage(
        result?.data?.reused
          ? "Encontramos un plan pendiente y lo recuperamos."
          : "Análisis completo. El plan seguro está listo para ejecutarse."
      );
    } catch (err) {
      console.error("[CustomersPage] createCustomerCleanupJobR29 failed", err);
      setError(err?.message || "No pudimos crear el plan de limpieza.");
      setMessage("");
    } finally {
      setPlanning(false);
    }
  };

  const processCleanup = async () => {
    if (!businessId || !job?.jobId || processing || planning) return;
    setProcessing(true);
    setError("");
    setMessage("Procesando clientes por bloques seguros. Puedes dejar esta ventana abierta.");
    stopRequestedRef.current = false;

    try {
      const callable = httpsCallable(getFunctions(), "processCustomerCleanupJobR29");
      let currentJob = job;
      let completed = false;
      let terminalWithFailures = false;
      let terminalPayload = null;
      let calls = 0;
      let noProgressCalls = 0;
      let previousProgressKey = "";

      while (!completed && !terminalWithFailures && !stopRequestedRef.current && calls < 120) {
        calls += 1;
        const result = await callable({
          businessId,
          jobId: currentJob.jobId,
          limit: 20,
        });
        const payload = result?.data || {};
        terminalPayload = payload;
        currentJob = payload.job || currentJob;
        setJob(currentJob);

        const chunk = payload.chunk || {};
        const progressKey = [
          currentJob?.completedClustersCount || 0,
          currentJob?.failedClustersCount || 0,
          currentJob?.manualClustersCount || 0,
          currentJob?.totals?.archivedSecondaries || 0,
        ].join("|");

        if (progressKey === previousProgressKey) noProgressCalls += 1;
        else noProgressCalls = 0;
        previousProgressKey = progressKey;

        const reopened = Number(payload.recoveredIndexFailures || 0);
        const completedNow = Number(chunk.completed || 0);
        const failedNow = Number(chunk.failed || 0);
        const partialNow = Number(chunk.partial || 0);
        const processedNow = Number(chunk.processed || 0);

        if (reopened > 0) {
          setMessage(
            `Reabrimos ${reopened.toLocaleString("es-PA")} grupos que habían fallado por el índice anterior. Continuando la limpieza…`
          );
        } else if (processedNow > 0) {
          setMessage(
            `Bloque procesado: ${completedNow.toLocaleString("es-PA")} completados, ${partialNow.toLocaleString("es-PA")} para reintento y ${failedNow.toLocaleString("es-PA")} bloqueados.`
          );
        }

        completed = payload.done === true && currentJob?.status === "completed";
        terminalWithFailures =
          payload.terminal === true &&
          payload.done !== true &&
          payload.canContinue === false;

        if (!completed && !terminalWithFailures && noProgressCalls >= 4) {
          terminalWithFailures = true;
          setError(
            "El proceso dejó de avanzar después de varios bloques. Loop lo pausó para evitar llamadas infinitas y conservar el progreso."
          );
          break;
        }

        if (!completed && !terminalWithFailures) {
          await new Promise((resolve) => window.setTimeout(resolve, 450));
        }
      }

      if (completed) {
        setMessage(
          `Limpieza completada: ${Number(currentJob?.totals?.archivedSecondaries || 0).toLocaleString("es-PA")} registros archivados y ${Number(currentJob?.totals?.referencesUpdated || 0).toLocaleString("es-PA")} referencias actualizadas.`
        );
        onMerged();
      } else if (terminalWithFailures) {
        const failedCount = Number(currentJob?.failedClustersCount || 0);
        const firstFailure = Array.isArray(terminalPayload?.failedPreview)
          ? terminalPayload.failedPreview[0]
          : null;
        setError(
          firstFailure?.failureMessage
            ? `La limpieza terminó con ${failedCount.toLocaleString("es-PA")} grupo(s) bloqueado(s). Primer bloqueo: ${firstFailure.failureMessage}`
            : `La limpieza terminó con ${failedCount.toLocaleString("es-PA")} grupo(s) bloqueado(s). El progreso se guardó y ya no seguirá repitiendo llamadas.`
        );
        setMessage(
          "Los grupos completados quedaron aplicados. Los bloqueados se conservaron para revisión sin borrar clientes."
        );
        await loadJob();
      } else if (stopRequestedRef.current) {
        setMessage("La limpieza quedó guardada. Puedes continuarla cuando vuelvas a abrir esta pantalla.");
      } else {
        setMessage("Avanzamos varios bloques. Pulsa “Continuar limpieza” para seguir desde el punto guardado.");
      }
    } catch (err) {
      console.error("[CustomersPage] processCustomerCleanupJobR29 failed", err);
      setError(err?.message || "La limpieza se pausó por un error. El progreso quedó guardado.");
      setMessage("No se perdió el progreso. Puedes continuar cuando el problema esté resuelto.");
      try { await loadJob(); } catch (_) {}
    } finally {
      setProcessing(false);
    }
  };

  const requestStop = () => {
    stopRequestedRef.current = true;
    setMessage("Pausaremos después del bloque que está en proceso. El progreso queda guardado.");
  };

  if (!open) return null;

  const status = String(job?.status || "");
  const totalClusters = Number(job?.totalClusters || 0);
  const completedClusters = Number(job?.completedClustersCount || 0);
  const failedClusters = Number(job?.failedClustersCount || 0);
  const manualClusters = Number(job?.manualClustersCount || 0);
  const progressDone = Math.min(
    totalClusters,
    completedClusters + failedClusters + manualClusters
  );
  const progressPercent = totalClusters
    ? Math.min(100, Math.round((progressDone / totalClusters) * 100))
    : status === "completed"
      ? 100
      : 0;
  const readyToProcess = Boolean(job?.jobId && ["ready", "running", "partial"].includes(status));
  const completed = status === "completed";
  const secondaryCount = Number(job?.plannedSecondaryCount || 0);
  const manualConflictCount = Number(job?.manualConflictCount || 0);
  const preview = Array.isArray(job?.manualConflictPreview)
    ? job.manualConflictPreview.slice(0, 6)
    : [];

  const modal = (
    <div
      className="cp-dup-backdrop cp-dup-r29-backdrop loop-layer-modal-backdrop" role="presentation"
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget || processing) return;
        onClose();
      }}
    >
      <style>{`
        .cp-dup-r29-backdrop{
          z-index:var(--loop-z-modal-backdrop,4000)!important;
          background:rgba(8,15,25,.66)!important;
          backdrop-filter:blur(12px) saturate(.68)!important;
          -webkit-backdrop-filter:blur(12px) saturate(.68)!important
        }
        .cp-dup-r29{width:min(920px,calc(100vw - 32px));max-height:min(860px,calc(100dvh - 32px));overflow:auto;border-radius:30px;background:#fff;border:1px solid #dbe7f0;box-shadow:0 34px 100px rgba(15,23,42,.28)}
        .cp-r29-head{padding:24px 26px;border-bottom:1px solid #e2e8f0;display:flex;align-items:flex-start;justify-content:space-between;gap:20px;background:linear-gradient(135deg,#fff 0%,#f0fdfa 100%)}
        .cp-r29-head span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.13em;text-transform:uppercase}
        .cp-r29-head h2{margin:6px 0 4px;color:#0f172a;font-size:28px;line-height:1;font-weight:950;letter-spacing:-.045em}
        .cp-r29-head p{margin:0;color:#64748b;font-size:13px;font-weight:800;line-height:1.45;max-width:650px}
        .cp-r29-close{width:42px;height:42px;border-radius:999px;border:1px solid #dbe7f0;background:#fff;color:#0f172a;font-size:20px;font-weight:900;cursor:pointer;flex:0 0 auto}
        .cp-r29-body{padding:22px 24px 26px;display:grid;gap:16px}
        .cp-r29-intro{border:1px solid #dbe7f0;background:#fff;border-radius:24px;padding:18px;display:flex;justify-content:space-between;gap:18px;align-items:center}
        .cp-r29-intro strong{display:block;color:#0f172a;font-size:16px;font-weight:950}
        .cp-r29-intro p{margin:5px 0 0;color:#64748b;font-size:12.5px;font-weight:800;line-height:1.45}
        .cp-r29-primary{border:0;background:#00675f;color:#fff;border-radius:999px;min-height:44px;padding:0 18px;font-size:12.5px;font-weight:950;cursor:pointer;white-space:nowrap;box-shadow:0 14px 30px rgba(0,103,95,.18)}
        .cp-r29-primary:disabled{opacity:.58;cursor:not-allowed}
        .cp-r29-secondary{border:1px solid #dbe7f0;background:#fff;color:#334155;border-radius:999px;min-height:42px;padding:0 16px;font-size:12px;font-weight:950;cursor:pointer}
        .cp-r29-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
        .cp-r29-stat{border:1px solid #e2e8f0;background:#f8fafc;border-radius:20px;padding:14px;min-width:0}
        .cp-r29-stat small{display:block;color:#64748b;font-size:10px;font-weight:950;letter-spacing:.09em;text-transform:uppercase}
        .cp-r29-stat strong{display:block;margin-top:7px;color:#0f172a;font-size:22px;font-weight:950;letter-spacing:-.035em}
        .cp-r29-stat em{display:block;margin-top:3px;color:#64748b;font-size:11px;font-weight:800;font-style:normal}
        .cp-r29-progress{border:1px solid rgba(0,103,95,.16);background:#f0fdfa;border-radius:22px;padding:16px;display:grid;gap:9px}
        .cp-r29-progress-head{display:flex;justify-content:space-between;gap:12px;align-items:center}
        .cp-r29-progress-head strong{color:#0f172a;font-size:13px;font-weight:950}
        .cp-r29-progress-head span{color:#00675f;font-size:12px;font-weight:950}
        .cp-r29-track{height:11px;border-radius:999px;background:#dbe7e5;overflow:hidden}
        .cp-r29-track i{display:block;height:100%;border-radius:inherit;background:#00675f;transition:width .25s ease}
        .cp-r29-progress em{color:#64748b;font-size:11.5px;font-weight:850;font-style:normal}
        .cp-r29-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap}
        .cp-r29-note{border-radius:18px;padding:12px 14px;font-size:12px;font-weight:850;line-height:1.45}
        .cp-r29-note.ok{background:#ecfdf5;border:1px solid #a7f3d0;color:#047857}
        .cp-r29-note.error{background:#fef2f2;border:1px solid #fecaca;color:#b91c1c}
        .cp-r29-manual{border:1px solid #fde68a;background:#fffbeb;border-radius:22px;padding:15px}
        .cp-r29-manual>strong{display:block;color:#92400e;font-size:13px;font-weight:950}
        .cp-r29-manual>p{margin:4px 0 10px;color:#92400e;font-size:11.5px;font-weight:800;line-height:1.4}
        .cp-r29-review-list{display:grid;gap:7px}
        .cp-r29-review{border:1px solid rgba(146,64,14,.16);background:#fff;border-radius:15px;padding:10px 12px;display:flex;justify-content:space-between;gap:10px}
        .cp-r29-review strong{color:#0f172a;font-size:12px;font-weight:950}
        .cp-r29-review span{color:#64748b;font-size:10.5px;font-weight:850;text-align:right}
        @media(max-width:760px){.cp-dup-r29{width:calc(100vw - 18px);max-height:calc(100dvh - 18px);border-radius:22px}.cp-r29-head{padding:19px}.cp-r29-head h2{font-size:23px}.cp-r29-body{padding:16px}.cp-r29-intro{align-items:stretch;flex-direction:column}.cp-r29-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.cp-r29-actions{display:grid;grid-template-columns:1fr}.cp-r29-actions button{width:100%}}
      `}</style>

      <section
        className="cp-dup-r29"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-r29-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="cp-r29-head">
          <div>
            <span>{t("Limpieza inteligente R29")}</span>
            <h2 id="cp-r29-dialog-title">{t("Duplicados de clientes")}</h2>
            <p>
              Loop analiza todos los clientes desde el backend, conserva el perfil más completo,
              traslada reservas y beneficios, y archiva los secundarios sin eliminarlos.
            </p>
          </div>
          <button type="button" className="cp-r29-close" disabled={processing} onClick={onClose}>×</button>
        </header>

        <div className="cp-r29-body">
          {loading ? (
            <div className="cp-r29-note ok">{t("Revisando si existe un plan de limpieza guardado…")}</div>
          ) : null}

          {!loading && !job ? (
            <div className="cp-r29-intro">
              <div>
                <strong>{t("Primero crearemos un mapa completo")}</strong>
                <p>
                  El análisis es de solo lectura. Revisa UID, teléfono, email y nombre exacto en
                  todos los documentos, sin el límite anterior de 2,500 clientes.
                </p>
              </div>
              <button type="button" className="cp-r29-primary" disabled={planning} onClick={createPlan}>
                {planning ? "Analizando…" : "Analizar todos los clientes"}
              </button>
            </div>
          ) : null}

          {job ? (
            <>
              <div className="cp-r29-grid">
                <article className="cp-r29-stat">
                  <small>{t("Clientes analizados")}</small>
                  <strong>{Number(job.scannedCustomers || 0).toLocaleString("es-PA")}</strong>
                  <em>{t("Documentos completos")}</em>
                </article>
                <article className="cp-r29-stat">
                  <small>{t("Registros a archivar")}</small>
                  <strong>{secondaryCount.toLocaleString("es-PA")}</strong>
                  <em>{t("Nunca se eliminan")}</em>
                </article>
                <article className="cp-r29-stat">
                  <small>{t("Resultado estimado")}</small>
                  <strong>{Number(job.estimatedActiveAfter || 0).toLocaleString("es-PA")}</strong>
                  <em>{t("Clientes activos")}</em>
                </article>
                <article className="cp-r29-stat">
                  <small>{t("Revisión manual")}</small>
                  <strong>{manualConflictCount.toLocaleString("es-PA")}</strong>
                  <em>{t("Identidades distintas")}</em>
                </article>
              </div>

              <div className="cp-r29-progress">
                <div className="cp-r29-progress-head">
                  <strong>
                    {completed
                      ? "Limpieza completada"
                      : processing
                        ? "Procesando limpieza"
                        : readyToProcess
                          ? "Plan listo"
                          : "Estado del plan"}
                  </strong>
                  <span>{progressPercent}%</span>
                </div>
                <div className="cp-r29-track"><i style={{ width: `${progressPercent}%` }} /></div>
                <em>
                  {progressDone.toLocaleString("es-PA")}{" " + t("de") + " "}{totalClusters.toLocaleString("es-PA")}{" " + t("grupos finalizados ·")}{" "}
                  {Number(job?.totals?.archivedSecondaries || 0).toLocaleString("es-PA")}{t("secundarios archivados")}</em>
              </div>

              {manualConflictCount > 0 ? (
                <div className="cp-r29-manual">
                  <strong>{manualConflictCount}{" " + t("grupo")}{manualConflictCount === 1 ? "" : "s"}{" " + t("queda")}{manualConflictCount === 1 ? "" : "n"}{" " + t("para revisión manual")}</strong>
                  <p>
                    Loop no los fusionará automáticamente porque existen teléfonos, emails o usuarios
                    distintos bajo el mismo nombre.
                  </p>
                  {preview.length ? (
                    <div className="cp-r29-review-list">
                      {preview.map((group, index) => (
                        <div className="cp-r29-review" key={`${group.kind || "review"}-${group.key || index}`}>
                          <strong>{group?.preview?.[0]?.name || group.key || t("Cliente")}</strong>
                          <span>{group.count || group.customerIds?.length || 0}{" " + t("registros ·") + " "}{group.reason || "conflicto de identidad"}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="cp-r29-actions">
                <button type="button" className="cp-r29-secondary" disabled={planning || processing} onClick={createPlan}>{t("Analizar nuevamente")}</button>
                {processing ? (
                  <button type="button" className="cp-r29-secondary" onClick={requestStop}>{t("Pausar después del bloque")}</button>
                ) : null}
                {!completed ? (
                  <button type="button" className="cp-r29-primary" disabled={!readyToProcess || planning || processing} onClick={processCleanup}>
                    {processing ? "Procesando…" : progressDone > 0 ? "Continuar limpieza" : "Ejecutar limpieza segura"}
                  </button>
                ) : (
                  <button type="button" className="cp-r29-primary" onClick={() => { onMerged(); onClose(); }}>{t("Cerrar y actualizar clientes")}</button>
                )}
              </div>
            </>
          ) : null}

          {message ? <div className="cp-r29-note ok">{message}</div> : null}
          {error ? <div className="cp-r29-note error">{error}</div> : null}
        </div>
      </section>
    </div>
  );

  if (typeof document === "undefined" || !document.body) return modal;
  return createPortal(modal, document.body);
}

export function CustomerMembershipManager({ customer, businessId, onClose }) {
  return <div className="cp-benefits-manager-modal-premium">
    <style>{`${styles}${crmExactVisualStyles}${crmInlineExpansionFixStyles}`}</style>
    <CustomerDetail customer={customer} businessId={businessId} onClose={onClose} initialBenefitView="membership" />
  </div>;
}

function CustomerDetail({ customer, businessId, onClose, onSave, initialBenefitView = "membership" }) {
  useLanguage();
  const [benefitView, setBenefitView] = useState(initialBenefitView === "loyalty" ? "loyalty" : "membership");
  const [assistedEnrollmentOpen, setAssistedEnrollmentOpen] = useState(false);
  const [notes, setNotes] = useState(customer?.notes || "");
  const [tagText, setTagText] = useState(Array.isArray(customer?.tags) ? customer.tags.join(", ") : "");
  const [whatsappOptOut, setWhatsappOptOut] = useState(customer?.whatsappOptOut === true || customer?.whatsappOptIn === false || customer?.customerWhatsappConsent === false);
  const [loyalty, setLoyalty] = useState({ programs: [], accounts: [], items: [] });
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState("");
  const loyaltyActions = useRef({ running: false, requests: {} });
  const loyaltyRequestId = (key) => {
    if (!loyaltyActions.current.requests[key]) loyaltyActions.current.requests[key] = window.crypto.randomUUID();
    return loyaltyActions.current.requests[key];
  };

  const [loyaltyBusyKey, setLoyaltyBusyKey] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  const [qrData, setQrData] = useState(null);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [memberships, setMemberships] = useState({ items: [] });
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipsError, setMembershipsError] = useState("");
  const [membershipBusyKey, setMembershipBusyKey] = useState("");
  const [selectedMembershipPlanId, setSelectedMembershipPlanId] = useState("");
  const [creditAdjustDraft, setCreditAdjustDraft] = useState(null);
  const [creditAdjustSaving, setCreditAdjustSaving] = useState(false);
  const [creditAdjustMessage, setCreditAdjustMessage] = useState("");
  const [membershipRenewDraft, setMembershipRenewDraft] = useState(null);
  const [membershipRenewSaving, setMembershipRenewSaving] = useState(false);
  const [membershipRenewMessage, setMembershipRenewMessage] = useState("");
  const [membershipExpirationSaving, setMembershipExpirationSaving] = useState(false);
  const [membershipExpirationMessage, setMembershipExpirationMessage] = useState("");

  const reloadLoyalty = async (targetCustomer = customer) => {
    if (!businessId || !targetCustomer?.id) {
      setLoyalty({ programs: [], accounts: [], items: [] });
      return;
    }

    setLoyaltyLoading(true);
    setLoyaltyError("");
    try {
      const callable = httpsCallable(getFunctions(), "getCustomerLoyaltySummary");
      const res = await callable({ businessId, customerId: targetCustomer.id });
      const data = res?.data || {};
      setLoyalty({
        programs: Array.isArray(data.programs) ? data.programs : [],
        accounts: Array.isArray(data.accounts) ? data.accounts : [],
        items: Array.isArray(data.items) ? data.items : [],
      });
    } catch (e) {
      console.error("[CustomersPage] loyalty summary failed", e);
      setLoyaltyError("No pudimos cargar la fidelidad de este cliente.");
    } finally {
      setLoyaltyLoading(false);
    }
  };



  const reloadMemberships = async (targetCustomer = customer) => {
    if (!businessId || !targetCustomer?.id) {
      setMembershipPlans([]);
      setMemberships({ items: [] });
      return;
    }
    setMembershipsLoading(true);
    setMembershipsError("");
    try {
      const functions = getFunctions();
      const [plansRes, membershipsRes] = await Promise.all([
        httpsCallable(functions, "getBusinessMembershipPlans")({ businessId }),
        httpsCallable(functions, "getCustomerMemberships")({ businessId, customerId: targetCustomer.id }),
      ]);
      const plans = Array.isArray(plansRes?.data?.plans) ? plansRes.data.plans.filter((p) => p && p.active !== false) : [];
      setMembershipPlans(plans);
      const rows = Array.isArray(membershipsRes?.data?.items) ? membershipsRes.data.items : [];
      const activityByMembership = membershipsRes?.data?.activityByMembership || {};
      setMemberships({
        items: rows.map((item) => {
          const mid = String(item?.membership?.id || "");
          return { ...item, activityRecent: item.activityRecent || activityByMembership[mid] || [] };
        }),
        activity: Array.isArray(membershipsRes?.data?.activity) ? membershipsRes.data.activity : [],
      });
      setSelectedMembershipPlanId((prev) => prev || (plans[0]?.id || ""));
    } catch (e) {
      console.error("[CustomersPage] memberships failed", e);
      setMembershipsError("No pudimos cargar membresías de este cliente.");
    } finally {
      setMembershipsLoading(false);
    }
  };

  useEffect(() => {
    setNotes(customer?.notes || "");
    setTagText(Array.isArray(customer?.tags) ? customer.tags.join(", ") : "");
    setWhatsappOptOut(customer?.whatsappOptOut === true || customer?.whatsappOptIn === false || customer?.customerWhatsappConsent === false);
    reloadLoyalty(customer);
    reloadMemberships(customer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, businessId]);

  if (!customer) {
    return (
      <aside className="cp-detail cp-detail-empty">
        <User size={28} />
        <h3>{t("Selecciona un cliente")}</h3>
        <p>{t("Verás notas, etiquetas, fidelidad y acciones rápidas.")}</p>
      </aside>
    );
  }

  const save = async () => {
    const tags = tagText.split(",").map((x) => x.trim()).filter(Boolean);
    await onSave({ ...customer, notes, tags, whatsappOptOut, whatsappOptIn: !whatsappOptOut, customerWhatsappConsent: !whatsappOptOut });
  };

  const adjustLoyalty = async (programId, delta) => {
    if (!businessId || !customer?.id || !programId) return;
    if (loyaltyActions.current.running) return;
    loyaltyActions.current.running = true;
    const actionKey = `${customer.id}:${programId}:adjust:${delta}`;
    const requestId = loyaltyRequestId(actionKey);
    setLoyaltyBusyKey(programId);
    setLoyaltyError("");
    try {
      const callable = httpsCallable(getFunctions(), "adjustCustomerLoyaltyStamps");
      await callable({
        businessId,
        customerId: customer.id,
        programId,
        delta,
        requestId,
        reason: delta > 0 ? "manual_add_stamp" : "manual_remove_stamp",
      });
      delete loyaltyActions.current.requests[actionKey];
      await reloadLoyalty(customer);
    } catch (e) {
      console.error("[CustomersPage] loyalty adjust failed", e);
      setLoyaltyError("No pudimos ajustar los sellos.");
    } finally {
      loyaltyActions.current.running = false;
      setLoyaltyBusyKey("");
    }
  };

  const redeemReward = async (programId) => {
    if (!businessId || !customer?.id || !programId) return;
    if (loyaltyActions.current.running) return;
    loyaltyActions.current.running = true;
    const actionKey = `${customer.id}:${programId}:redeem`;
    const requestId = loyaltyRequestId(actionKey);
    setLoyaltyBusyKey(programId);
    setLoyaltyError("");
    try {
      const callable = httpsCallable(getFunctions(), "redeemCustomerLoyaltyReward");
      await callable({ businessId, customerId: customer.id, programId, requestId, reason: "manual_redeem_reward" });
      delete loyaltyActions.current.requests[actionKey];
      await reloadLoyalty(customer);
    } catch (e) {
      console.error("[CustomersPage] loyalty redeem failed", e);
      setLoyaltyError("No pudimos redimir la recompensa.");
    } finally {
      loyaltyActions.current.running = false;
      setLoyaltyBusyKey("");
    }
  };



  const grantMembership = async () => {
    if (hasCurrentMembershipPlan(memberships.items, selectedMembershipPlanId)) { setMembershipsError("Este cliente ya tiene ese plan vigente o con un pago pendiente. Revisa su membresía actual."); return; }
    if (!businessId || !customer?.id || !selectedMembershipPlanId) return;
    setMembershipBusyKey(selectedMembershipPlanId);
    setMembershipsError("");
    try {
      const callable = httpsCallable(getFunctions(), "grantCustomerMembership");
      await callable({ businessId, customerId: customer.id, planId: selectedMembershipPlanId });
      await reloadMemberships(customer);
    } catch (e) {
      console.error("[CustomersPage] grant membership failed", e);
      setMembershipsError("No pudimos asignar la membresía.");
    } finally {
      setMembershipBusyKey("");
    }
  };

  const cancelMembership = async (membershipId) => {
    if (!businessId || !membershipId) return;
    setMembershipBusyKey(membershipId);
    setMembershipsError("");
    try {
      const callable = httpsCallable(getFunctions(), "cancelCustomerMembership");
      await callable({ businessId, membershipId });
      await reloadMemberships(customer);
    } catch (e) {
      console.error("[CustomersPage] cancel membership failed", e);
      setMembershipsError("No pudimos cancelar la membresía.");
    } finally {
      setMembershipBusyKey("");
    }
  };

  const checkMembershipExpirations = async () => {
    if (!businessId) return;
    setMembershipExpirationSaving(true);
    setMembershipExpirationMessage("");
    try {
      const result = await expireCustomerMembershipsNowAction({ businessId, dryRun: false, limit: 300 });
      setMembershipExpirationMessage(summarizeMembershipExpirationResult(result));
      await reloadMemberships(customer);
    } catch (e) {
      console.error("[CustomersPage] expiration check failed", e);
      setMembershipExpirationMessage(e?.message || "No pudimos revisar vencimientos.");
    } finally {
      setMembershipExpirationSaving(false);
    }
  };

  const openMembershipRenewal = (item) => {
    setMembershipRenewMessage("");
    setMembershipRenewDraft({
      item,
      reason: "Renovación manual de membresía",
      resetCredits: true,
    });
  };

  const closeMembershipRenewal = () => {
    if (membershipRenewSaving) return;
    setMembershipRenewDraft(null);
    setMembershipRenewMessage("");
  };

  const submitMembershipRenewal = async () => {
    if (!businessId || !customer?.id || !membershipRenewDraft?.item) return;
    const membership = membershipRenewDraft.item?.membership || {};
    const reason = String(membershipRenewDraft.reason || "").trim();

    setMembershipRenewMessage("");
    if (!membership.id) {
      setMembershipRenewMessage("No encontré el ID de esta membresía.");
      return;
    }
    if (!reason || reason.length < 3) {
      setMembershipRenewMessage("Agrega un motivo para renovar.");
      return;
    }

    setMembershipRenewSaving(true);
    setMembershipBusyKey(membership.id);
    try {
      const result = await renewCustomerMembershipAction({
        businessId,
        customerId: customer.id,
        customerMembershipId: membership.id,
        reason,
        resetCredits: membershipRenewDraft.resetCredits !== false,
      });
      setMembershipRenewMessage(summarizeMembershipRenewalResult(result));
      await reloadMemberships(customer);
      setMembershipRenewDraft(null);
    } catch (e) {
      console.error("[CustomersPage] renewal failed", e);
      setMembershipRenewMessage(e?.message || "No pudimos renovar la membresía.");
    } finally {
      setMembershipRenewSaving(false);
      setMembershipBusyKey("");
    }
  };

  const openCreditAdjustment = (item) => {
    const membership = item?.membership || {};
    const plan = item?.plan || {};
    const credits = membershipCreditStats(membership, plan);
    setCreditAdjustMessage("");
    setCreditAdjustDraft({
      item,
      mode: credits.used > 0 ? "add" : "remove",
      amount: "1",
      reason: credits.used > 0 ? "Restauración manual por cancelación/no-show" : "Ajuste manual de créditos",
    });
  };

  const closeCreditAdjustment = () => {
    if (creditAdjustSaving) return;
    setCreditAdjustDraft(null);
    setCreditAdjustMessage("");
  };

  const submitCreditAdjustment = async () => {
    if (!businessId || !customer?.id || !creditAdjustDraft?.item) return;
    const membership = creditAdjustDraft.item?.membership || {};
    const plan = creditAdjustDraft.item?.plan || {};
    const credits = membershipCreditStats(membership, plan);
    const amount = Math.max(1, Math.trunc(Number(creditAdjustDraft.amount || 1)));
    const deltaCredits = creditAdjustDraft.mode === "add" ? amount : -amount;
    const reason = String(creditAdjustDraft.reason || "").trim();

    setCreditAdjustMessage("");
    if (!membership.id) {
      setCreditAdjustMessage("No encontré el ID de esta membresía.");
      return;
    }
    if (!reason || reason.length < 3) {
      setCreditAdjustMessage("Agrega un motivo para guardar el ajuste.");
      return;
    }
    if (creditAdjustDraft.mode === "add" && credits.remaining + amount > credits.included) {
      setCreditAdjustMessage(`No puedes superar ${credits.included} créditos incluidos.`);
      return;
    }
    if (creditAdjustDraft.mode === "remove" && credits.remaining - amount < 0) {
      setCreditAdjustMessage("No puedes dejar créditos por debajo de 0.");
      return;
    }

    setCreditAdjustSaving(true);
    setMembershipBusyKey(membership.id);
    try {
      const result = await adjustCustomerMembershipCreditsAction({
        businessId,
        customerId: customer.id,
        customerMembershipId: membership.id,
        deltaCredits,
        reason,
      });
      setCreditAdjustMessage(summarizeMembershipCreditAdjustmentResult(result));
      await reloadMemberships(customer);
      setCreditAdjustDraft(null);
    } catch (e) {
      console.error("[CustomersPage] credit adjustment failed", e);
      setCreditAdjustMessage(e?.message || "No pudimos ajustar los créditos.");
    } finally {
      setCreditAdjustSaving(false);
      setMembershipBusyKey("");
    }
  };


  const openCustomerQr = async () => {
    if (!businessId || !customer?.id) return;
    setQrOpen(true);
    setQrLoading(true);
    setQrError("");
    try {
      const callable = httpsCallable(getFunctions(), "getCustomerLoyaltyQr");
      const res = await callable({ businessId, customerId: customer.id });
      setQrData(res?.data || null);
    } catch (e) {
      console.error("[CustomersPage] loyalty QR failed", e);
      setQrError("No pudimos generar el QR de este cliente.");
    } finally {
      setQrLoading(false);
    }
  };

  const copyQrLink = async () => {
    const url = qrData?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch (_) {}
  };

  const printQr = () => {
    try {
      window.print();
    } catch (_) {}
  };

  const membershipItems = Array.isArray(memberships.items) ? memberships.items : [];
  const samePlanExists = hasCurrentMembershipPlan(membershipItems, selectedMembershipPlanId);
  const activeMembershipItems = membershipItems.filter((item) => membershipStatusValue(item?.membership?.status) === "active");
  const historicalMembershipItems = membershipItems.filter((item) => membershipStatusValue(item?.membership?.status) !== "active");

  return (
    <aside className="cp-detail">
      <div className="cp-detail-head">
        <button onClick={onClose}>←</button>
        <span>{t("Cliente")}</span>
      </div>

      <div className="cp-detail-avatar">{getInitials(customer.name)}</div>
      <h2>{customer.name || t("Cliente")}</h2>
      <p>{customer.phone || customer.email || "Sin contacto"}</p>

      <div className="cp-detail-stats">
        <div><strong>{customer.totalBookings || 0}</strong><span>{t("Citas")}</span></div>
        <div><strong>{customer.hasUserAccount ? "Sí" : t("No")}</strong><span>{t("Usuario")}</span></div>
      </div>

      <div className="cp-detail-actions">
        {customer.phone ? (
          <a href={`https://wa.me/${String(customer.phone).replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
            <MessageCircle size={16} />
            WhatsApp
          </a>
        ) : null}
        {customer.email ? (
          <a href={`mailto:${customer.email}`}>
            <Mail size={16} />{t("Email")}</a>
        ) : null}
        <button type="button" className="cp-qr-action" onClick={openCustomerQr}>
          <QrCode size={16} />{t("Ver QR")}</button>
      </div>

      <nav className="cp-benefit-tabs" aria-label={t("Gestión del cliente")}>
        <button type="button" aria-pressed={benefitView === "membership"} onClick={() => setBenefitView("membership")}>{t("Membresías")}</button>
        <button type="button" aria-pressed={benefitView === "loyalty"} onClick={() => setBenefitView("loyalty")}>{t("Fidelidad")}</button>
      </nav>
      <section className="cp-loyalty-card" hidden={benefitView !== "loyalty"}>
        <div className="cp-loyalty-head">
          <div>
            <span>{t("Fidelidad")}</span>
            <strong>{t("Sellos del cliente")}</strong>
          </div>
          <button type="button" onClick={() => reloadLoyalty(customer)} disabled={loyaltyLoading || loyaltyBusyKey}>{t("Actualizar")}</button>
        </div>

        {loyaltyLoading ? <div className="cp-loyalty-loading">{t("Cargando fidelidad...")}</div> : null}
        {loyaltyError ? <div className="cp-loyalty-error">{loyaltyError}</div> : null}

        {!loyaltyLoading && loyalty.items.length === 0 ? (
          <div className="cp-loyalty-empty">
            <strong>{t("Sin programas activos")}</strong>
            <small>{t("Crea un programa en Configuración > Fidelidad para comenzar.")}</small>
          </div>
        ) : null}

        {!loyaltyLoading && loyalty.items.map((item) => (
          <LoyaltyMiniCard
            businessId={businessId}
            customerId={customer.id}
            key={item.program?.id || item.account?.programId}
            item={item}
            busyKey={loyaltyBusyKey}
            onAdjust={adjustLoyalty}
            onRedeem={redeemReward}
          />
        ))}
      </section>



      <section className="cp-loyalty-card cp-membership-card" hidden={benefitView !== "membership"}>
        <div className="cp-loyalty-head">
          <div>
            <span>{t("Membresías")}</span>
            <strong>{t("Membresías del cliente")}</strong>
          </div>
          <div className="cp-membership-head-actions">
            <button type="button" onClick={checkMembershipExpirations} disabled={membershipExpirationSaving || membershipsLoading || !!membershipBusyKey}>{membershipExpirationSaving ? "Revisando..." : "Revisar vencimientos"}</button>
            <button type="button" onClick={() => reloadMemberships(customer)} disabled={membershipsLoading || membershipBusyKey}>{t("Actualizar")}</button>
          </div>
        </div>

        {membershipsLoading ? <div className="cp-loyalty-loading">{t("Cargando membresías...")}</div> : null}
        {membershipsError ? <div className="cp-loyalty-error">{membershipsError}</div> : null}
        {membershipExpirationMessage ? <div className="cp-loyalty-error">{membershipExpirationMessage}</div> : null}

        {!membershipsLoading && membershipPlans.length > 0 ? (
          <div className="cp-membership-grant cp-membership-grant-presential">
            <select value={selectedMembershipPlanId} onChange={(e) => setSelectedMembershipPlanId(e.target.value)}>
              {membershipPlans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name || t("Membresía")}</option>)}
            </select>
            <button type="button" onClick={() => { if (!samePlanExists) setAssistedEnrollmentOpen(true); }} disabled={!!membershipBusyKey || !selectedMembershipPlanId || samePlanExists || !!membershipsError}>{t("Cobrar membresía presencial")}</button>
            <button type="button" className="cp-membership-manual" onClick={grantMembership} disabled={!!membershipBusyKey || !selectedMembershipPlanId || samePlanExists || !!membershipsError}>{t("Asignar sin cobro automático")}</button>
          </div>
        ) : null}

        {samePlanExists && <p role="status" style={{ padding: 12, background: "#fff8e7", borderRadius: 10, color: "#765013" }}>{t("Este cliente ya tiene este plan vigente o con un pago pendiente. Revisa su membresía actual antes de registrar otro cobro.")}</p>}
        {assistedEnrollmentOpen && <AssistedMembershipEnrollment key={`${businessId}:${customer.id}:${selectedMembershipPlanId}`} businessId={businessId} customerId={customer.id} planId={selectedMembershipPlanId} onClose={() => { setAssistedEnrollmentOpen(false); reloadMemberships(customer); }} onCompleted={() => reloadMemberships(customer)} />}
        {!membershipsLoading && membershipPlans.length === 0 ? (
          <div className="cp-loyalty-empty">
            <strong>{t("Sin planes creados")}</strong>
            <small>{t("Crea planes en Configuración > Membresías.")}</small>
          </div>
        ) : null}

        {!membershipsLoading && activeMembershipItems.length === 0 && membershipPlans.length > 0 ? (
          <div className="cp-loyalty-empty">
            <strong>{t("Cliente sin membresía activa")}</strong>
            <small>{t("Asigna un plan para comenzar a probar el módulo.")}</small>
          </div>
        ) : null}

        {!membershipsLoading && activeMembershipItems.length > 0 ? (
          <div className="cp-membership-group">
            <div className="cp-membership-group-title">{t("Activas")}</div>
            {activeMembershipItems.map((item) => (
              <MembershipMiniCard
                key={item.membership?.id || item.plan?.id}
                item={item}
                busyKey={membershipBusyKey}
                onCancel={cancelMembership}
                onAdjustCredits={openCreditAdjustment}
                onRenew={openMembershipRenewal}
                businessId={businessId}
                onCompleted={() => reloadMemberships(customer)}
              />
            ))}
          </div>
        ) : null}

        {!membershipsLoading && historicalMembershipItems.length > 0 ? (
          <details className="cp-membership-group cp-membership-history">
            <summary className="cp-membership-group-title">{t("Historial ·") + " "}{historicalMembershipItems.length}</summary>
            {historicalMembershipItems.map((item) => (
              <MembershipMiniCard
                key={item.membership?.id || item.plan?.id}
                item={item}
                busyKey={membershipBusyKey}
                onCancel={cancelMembership}
                onAdjustCredits={openCreditAdjustment}
                onRenew={openMembershipRenewal}
                businessId={businessId}
                onCompleted={() => reloadMemberships(customer)}
              />
            ))}
          </details>
        ) : null}
      </section>


      {membershipRenewDraft ? (
        <div className="cp-qr-backdrop" onClick={closeMembershipRenewal}>
          <div className="cp-qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-qr-head">
              <div>
                <span>{t("Membresía")}</span>
                <strong>{t("Renovar membresía")}</strong>
                <small>{t("Inicia un nuevo ciclo, actualiza la vigencia y resetea los créditos incluidos.")}</small>
              </div>
              <button type="button" onClick={closeMembershipRenewal}>×</button>
            </div>

            <div className="cp-membership-adjust-form">
              <label>{t("Acción")}<input value="Renovar ciclo y resetear créditos" disabled />
              </label>

              <label>{t("Motivo / nota")}<textarea
                  value={membershipRenewDraft.reason}
                  onChange={(e) => setMembershipRenewDraft((prev) => ({ ...prev, reason: e.target.value }))}
                  disabled={membershipRenewSaving}
                  rows={3}
                  placeholder={t("Ej: Pago recibido por renovación mensual")}
                />
              </label>

              <label className="cp-membership-policy-note">
                <input
                  type="checkbox"
                  checked={membershipRenewDraft.resetCredits !== false}
                  onChange={(e) => setMembershipRenewDraft((prev) => ({ ...prev, resetCredits: e.target.checked }))}
                  disabled={membershipRenewSaving}
                />{t("Resetear créditos al total incluido del plan")}</label>

              {membershipRenewMessage ? <div className="cp-loyalty-error">{membershipRenewMessage}</div> : null}

              <div className="cp-membership-actions">
                <button type="button" onClick={closeMembershipRenewal} disabled={membershipRenewSaving}>{t("Cancelar")}</button>
                <button type="button" className="primary" onClick={submitMembershipRenewal} disabled={membershipRenewSaving}>
                  {membershipRenewSaving ? "Renovando..." : t("Renovar membresía")}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {creditAdjustDraft ? (
        <div className="cp-qr-backdrop" onClick={closeCreditAdjustment}>
          <div className="cp-qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-qr-head">
              <div>
                <span>{t("Membresía")}</span>
                <strong>{t("Ajustar créditos")}</strong>
                <small>{t("Registra una corrección manual con motivo. Loop no restaura créditos automáticamente por cancelaciones/no-shows.")}</small>
              </div>
              <button type="button" onClick={closeCreditAdjustment}>×</button>
            </div>

            <div className="cp-membership-adjust-form">
              <label>{t("Tipo de ajuste")}<select
                  value={creditAdjustDraft.mode}
                  onChange={(e) => setCreditAdjustDraft((prev) => ({ ...prev, mode: e.target.value }))}
                  disabled={creditAdjustSaving}
                >
                  <option value="add">{t("Devolver crédito (+)")}</option>
                  <option value="remove">{t("Descontar crédito (-)")}</option>
                </select>
              </label>

              <label>{t("Cantidad")}<input
                  type="number"
                  min="1"
                  step="1"
                  value={creditAdjustDraft.amount}
                  onChange={(e) => setCreditAdjustDraft((prev) => ({ ...prev, amount: e.target.value }))}
                  disabled={creditAdjustSaving}
                />
              </label>

              <label>{t("Motivo")}<textarea
                  value={creditAdjustDraft.reason}
                  onChange={(e) => setCreditAdjustDraft((prev) => ({ ...prev, reason: e.target.value }))}
                  disabled={creditAdjustSaving}
                  rows={3}
                  placeholder={t("Ej: Restauración manual por cancelación aprobada")}
                />
              </label>

              {creditAdjustMessage ? <div className="cp-loyalty-error">{creditAdjustMessage}</div> : null}

              <div className="cp-membership-actions">
                <button type="button" onClick={closeCreditAdjustment} disabled={creditAdjustSaving}>{t("Cancelar")}</button>
                <button type="button" className="primary" onClick={submitCreditAdjustment} disabled={creditAdjustSaving}>
                  {creditAdjustSaving ? t("Guardando...") : "Guardar ajuste"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {qrOpen ? (
        <div className="cp-qr-backdrop" onClick={() => setQrOpen(false)}>
          <div className="cp-qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-qr-head">
              <div>
                <span>{t("QR de fidelidad")}</span>
                <strong>{customer.name || t("Cliente")}</strong>
                <small>{t("Escanea este código para abrir el check-in del cliente.") + " "}{customer.hasUserAccount ? "Cliente vinculado a usuario Loop." : "Cliente sin usuario Loop vinculado."}</small>
              </div>
              <button type="button" onClick={() => setQrOpen(false)}>×</button>
            </div>

            {qrLoading ? <div className="cp-qr-loading">{t("Generando QR...")}</div> : null}
            {qrError ? <div className="cp-loyalty-error">{qrError}</div> : null}

            {!qrLoading && qrData?.url ? (
              <>
                <div className="cp-qr-box">
                  <QRCodeCanvas value={qrData.url} size={220} includeMargin level="M" />
                </div>
                <div className="cp-qr-url">{qrData.url}</div>
                <div className="cp-qr-meta">
                  <span>{t("QR activo")}</span>
                  <span>{customer.hasUserAccount ? "Usuario Loop: Sí" : "Usuario Loop: No"}</span>
                </div>
                <div className="cp-qr-actions">
                  <button type="button" onClick={copyQrLink}><Copy size={15} />{" " + t("Copiar link")}</button>
                  <button type="button" onClick={printQr}><Printer size={15} />{" " + t("Imprimir")}</button>
                  <a href={qrData.url} target="_blank" rel="noreferrer"><ExternalLink size={15} />{" " + t("Abrir")}</a>
                </div>
                <p className="cp-qr-help">{t("El QR no contiene datos personales visibles; solo un token seguro que Loop resuelve internamente.")}</p>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <label className="cp-field cp-whatsapp-consent">
        <span>WhatsApp</span>
        <button type="button" className={`cp-wa-toggle ${whatsappOptOut ? "is-off" : "is-on"}`} onClick={() => setWhatsappOptOut((v) => !v)}>
          {whatsappOptOut ? "No enviar WhatsApp a este cliente" : "WhatsApp permitido"}
        </button>
        <small>{whatsappOptOut ? "Loop bloqueará mensajes automáticos y manuales." : "Puede recibir confirmaciones y recordatorios."}</small>
      </label>

      <label className="cp-field">
        <span>{t("Etiquetas")}</span>
        <input value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder={t("VIP, color, uñas...")} />
      </label>

      <label className="cp-field">
        <span>{t("Notas internas")}</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("Preferencias, alergias, detalles importantes...")} />
      </label>

      <button className="cp-save" onClick={save}>{t("Guardar cliente")}</button>
    </aside>
  );
}



const crmExactVisualStyles = `
  .cp-page.is-embedded{background:transparent!important;padding:0!important;}
  .cp-page{background:#fbf8f1!important;}
  .cp-shell-exact{width:100%!important;max-width:none!important;margin:0!important;gap:16px!important;}
  .loop-settings-content .cp-shell-exact,.loop-crm-only-shell .cp-shell-exact{max-width:none!important;width:100%!important;}

  .cp-hero-exact{min-height:146px!important;border-radius:30px!important;padding:28px 34px!important;background:radial-gradient(ellipse at 82% 50%,rgba(0,103,95,.115),transparent 0 28%,transparent 46%),linear-gradient(135deg,rgba(255,255,255,.98),rgba(248,255,252,.94))!important;box-shadow:0 18px 52px rgba(15,23,42,.065)!important;grid-template-columns:minmax(0,1fr) auto!important;align-items:center!important;}
  .cp-hero-exact:after{right:-42px!important;top:-52px!important;bottom:auto!important;width:410px!important;height:210px!important;border-radius:0 0 0 999px!important;background:rgba(0,103,95,.075)!important;transform:rotate(-8deg);}
  .cp-hero-exact span{font-size:11px!important;letter-spacing:.18em!important;margin-bottom:9px!important;color:#00675f!important;}
  .cp-hero-exact h1{font-size:44px!important;letter-spacing:-.055em!important;color:#07142b!important;}
  .cp-hero-exact p{max-width:560px!important;margin-top:11px!important;color:#53647b!important;font-size:16px!important;line-height:1.42!important;font-weight:720!important;}
  .cp-hero-stats-exact{grid-template-columns:repeat(4,minmax(134px,154px))!important;gap:16px!important;}
  .cp-hero-stats-exact div{min-height:78px!important;border-radius:20px!important;display:grid!important;grid-template-columns:32px 1fr!important;grid-template-rows:auto auto!important;column-gap:9px!important;align-content:center!important;padding:14px 16px!important;background:rgba(255,255,255,.96)!important;box-shadow:0 14px 32px rgba(15,23,42,.055)!important;}
  .cp-hero-stats-exact i{grid-row:1/3;width:30px;height:30px;border-radius:999px;display:grid;place-items:center;background:#ecfdf5;color:#00675f;font-style:normal;font-weight:950;font-size:18px;}
  .cp-hero-stats-exact div:nth-child(3) i{background:#eff6ff;color:#2563eb}.cp-hero-stats-exact div:nth-child(4) i{background:#fff7ed;color:#f59e0b}
  .cp-hero-stats-exact strong{font-size:25px!important;color:#07142b!important;}
  .cp-hero-stats-exact span{font-size:11.5px!important;text-transform:none!important;letter-spacing:0!important;color:#40506a!important;margin:2px 0 0!important;font-weight:760!important;}
  .cp-toolbar-exact{grid-template-columns:minmax(360px,1fr) auto auto auto!important;border-radius:24px!important;padding:10px!important;gap:12px!important;background:rgba(255,255,255,.95)!important;box-shadow:0 14px 36px rgba(15,23,42,.055)!important;}
  .cp-toolbar-exact .cp-search{height:48px!important;border-radius:22px!important;background:#f8fafc!important;box-shadow:inset 0 1px 2px rgba(15,23,42,.025);}
  .cp-toolbar-exact .cp-search input{font-size:14px!important;font-weight:760!important;}
  .cp-toolbar-exact .cp-filters button{height:42px!important;border-radius:999px!important;padding:0 18px!important;font-size:12.5px!important;}
  .cp-duplicate-review{height:44px!important;border-color:rgba(0,103,95,.22)!important;color:#00675f!important;background:#fff!important;border-radius:14px!important;padding:0 18px!important;box-shadow:none!important;}
  .cp-import{height:44px!important;min-width:120px!important;border-radius:14px!important;background:linear-gradient(135deg,#00675f,#009b8c)!important;box-shadow:0 12px 24px rgba(0,103,95,.16)!important;}
  .cp-layout-exact{grid-template-columns:minmax(560px,1.08fr) minmax(520px,.92fr)!important;gap:18px!important;align-items:start!important;width:100%!important;}
  .cp-list-card-exact,.cp-detail-match{border-radius:24px!important;background:rgba(255,255,255,.96)!important;border:1px solid #dde8ef!important;box-shadow:0 18px 48px rgba(15,23,42,.06)!important;}
  .cp-list-card-exact{overflow:hidden!important;}.cp-list-card-exact .cp-list-head{height:58px;padding:0 20px!important;border-bottom:1px solid #edf2f6!important;}.cp-list-card-exact .cp-list-head strong{font-size:18px!important;color:#07142b!important}.cp-list-card-exact .cp-list-head span{color:#253854!important;font-weight:900!important;}
  .cp-list-exact{max-height:600px!important;overflow:auto!important;padding:10px 10px 12px!important;gap:10px!important;scrollbar-color:rgba(15,23,42,.35) transparent;}
  .cp-customer-exact{position:relative;grid-template-columns:64px minmax(0,1fr) auto!important;align-items:center!important;min-height:96px!important;border-radius:20px!important;padding:12px 18px!important;background:#fff!important;border:1px solid #dfe9f0!important;box-shadow:none!important;}
  .cp-customer-exact.active{border-color:#49b7aa!important;background:linear-gradient(90deg,rgba(0,103,95,.035),#fff)!important;box-shadow:0 10px 28px rgba(0,103,95,.055)!important;transform:none!important;}
  .cp-customer-exact:hover{transform:none!important;box-shadow:0 12px 28px rgba(15,23,42,.065)!important;}
  .cp-customer-exact .cp-avatar{width:54px!important;height:54px!important;border-radius:999px!important;font-size:18px!important;background:#edf6f4!important;}
  .cp-customer-exact h3{font-size:16px!important;color:#07142b!important;letter-spacing:-.025em!important;}
  .cp-customer-exact .cp-customer-top button{position:absolute;top:16px;right:16px;}
  .cp-customer-line{display:flex;align-items:center;gap:18px;margin-top:9px;min-width:0;color:#56667d;}
  .cp-customer-line span{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:780;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:220px;}
  .cp-customer-date{display:flex;align-items:center;gap:16px;color:#334155;font-weight:800;font-size:12px;padding-right:12px;white-space:nowrap;}.cp-customer-date em{font-style:normal;color:#64748b;font-size:25px;font-weight:400;line-height:1;}
  .cp-tags{margin-top:10px!important;gap:8px!important;}.cp-tags span{height:28px!important;padding:0 10px!important;font-size:11px!important;font-weight:900!important;border:1px solid #dde8ef!important;background:#f8fafc!important;color:#56667d!important;}.cp-tags .cp-wa-tag.is-on{background:#e9fbf5!important;border-color:#adf0d1!important;color:#007b68!important;}.cp-tags .cp-wa-tag.is-off{background:#fff7ed!important;border-color:#fed7aa!important;color:#c2410c!important;}
  .cp-detail-match{position:sticky!important;top:12px!important;max-height:calc(100vh - 112px)!important;overflow:auto!important;padding:0!important;}
  .cp-detail-profile-head{display:grid;grid-template-columns:64px minmax(0,1fr) 34px;gap:14px;align-items:center;padding:22px 22px 16px;border-bottom:1px solid #edf2f6;}
  .cp-detail-avatar.is-small{width:58px!important;height:58px!important;border-radius:999px!important;margin:0!important;font-size:18px!important;background:#edf6f4!important;}
  .cp-detail-name-row{display:flex;align-items:center;gap:9px;min-width:0}.cp-detail-name-row h2{margin:0!important;text-align:left!important;font-size:21px!important;color:#07142b!important;letter-spacing:-.035em!important;}.cp-status-chip{display:inline-flex;border-radius:999px;background:#dcfce7;color:#047857;padding:5px 9px;font-size:11px;font-weight:950;}
  .cp-detail-profile-copy p{margin:6px 0 0!important;text-align:left!important;color:#607089!important;font-size:12px!important;font-weight:780!important;}.cp-detail-menu{border:0;background:transparent;color:#0f172a;cursor:pointer;}
  .cp-detail-section{padding:15px 22px;border-bottom:1px solid #edf2f6;}.cp-detail-section:last-child{border-bottom:0}.cp-section-title-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px;}.cp-detail-section h3{margin:0;color:#07142b;font-size:15px;font-weight:950;letter-spacing:-.02em;}.cp-section-title-row button,.cp-soft-action{min-height:32px;border-radius:10px;border:1px solid #dde8ef;background:#fff;color:#00675f;font-size:12px;font-weight:900;padding:0 11px;text-decoration:none;display:inline-flex;align-items:center;gap:6px;cursor:pointer;}
  .cp-profile-grid,.cp-contact-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(150px,.8fr);gap:9px 12px;align-items:center;}.cp-profile-grid span,.cp-contact-grid span{display:inline-flex;gap:8px;align-items:center;color:#607089;font-size:12.5px;font-weight:780;min-width:0;}.cp-profile-grid strong,.cp-contact-grid strong{color:#07142b;font-size:12.5px;font-weight:900;text-align:left;}.cp-profile-grid em{font-style:normal;background:#e7f6f4;color:#00675f;border-radius:999px;padding:4px 7px;font-size:10.5px;font-weight:950;}
  .cp-activity-list{display:grid;gap:11px}.cp-activity-list div{display:grid;grid-template-columns:20px minmax(0,1fr) auto;gap:9px;align-items:start}.cp-activity-list svg{color:#00675f}.cp-activity-list strong{display:block;color:#07142b;font-size:12.5px;font-weight:950}.cp-activity-list small{display:block;margin-top:2px;color:#607089;font-size:12px;font-weight:760}.cp-activity-list em{font-style:normal;color:#8090a5;font-size:11.5px;font-weight:760;white-space:nowrap;}
  .cp-benefits-grid{display:grid;grid-template-columns:1fr 1.25fr;gap:10px}.cp-benefit-card{border-radius:14px;border:1px solid #dde8ef;background:#f8fafc;padding:12px;display:grid;grid-template-columns:34px minmax(0,1fr);column-gap:10px;align-items:center;}.cp-benefit-card span{grid-row:1/4;width:34px;height:34px;border-radius:999px;display:grid;place-items:center;background:#dcfce7;color:#047857;font-weight:950}.cp-benefit-card small{color:#607089;font-size:11px;font-weight:820}.cp-benefit-card strong{color:#00675f;font-size:20px;font-weight:950;line-height:1}.cp-benefit-card em{display:block;color:#8a5a00;font-size:11px;font-style:normal;font-weight:820;margin-top:2px}.cp-benefit-card.is-membership{background:#fffaf0;border-color:#f8dfae}.cp-benefit-card.is-membership span{background:#fef3c7;color:#d97706}.cp-benefit-card.is-membership strong{color:#9a5b00;font-size:13px}.cp-benefit-inline-action{justify-self:start;margin-top:7px;border:1px solid #cde7e1;background:#f0fdfa;color:#00675f;border-radius:999px;padding:6px 10px;font-size:11px;font-weight:950;cursor:pointer}.cp-benefits-manager-backdrop{position:fixed;inset:0;z-index:2200;background:rgba(15,23,42,.42);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:22px}.cp-benefits-manager-modal{width:min(760px,calc(100vw - 34px));max-height:calc(100dvh - 44px);overflow:auto;border-radius:28px;background:#fff;border:1px solid rgba(226,232,240,.96);box-shadow:0 34px 90px rgba(15,23,42,.24)}.cp-benefits-manager-modal .cp-detail{width:100%;height:auto;min-height:0;border:0;box-shadow:none;border-radius:28px;overflow:visible}.cp-benefits-manager-modal .cp-detail-body{max-height:none}
  .cp-quick-actions-section{padding-bottom:18px}.cp-quick-actions-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin-top:10px}.cp-quick-actions-grid button,.cp-quick-actions-grid a{min-height:38px;border-radius:12px;border:1px solid #dde8ef;background:#fff;color:#00675f;text-decoration:none;font-size:11.5px;font-weight:900;display:flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;}
  .cp-detail-match-empty{min-height:360px!important;}
  @media(max-width:1180px){.cp-hero-stats-exact{grid-template-columns:repeat(2,144px)!important}.cp-layout-exact{grid-template-columns:1fr!important}.cp-detail-match{position:static!important;max-height:none!important}.cp-toolbar-exact{grid-template-columns:1fr!important}.cp-quick-actions-grid{grid-template-columns:1fr 1fr}.cp-benefits-grid{grid-template-columns:1fr}.cp-customer-date{display:none}}
  @media(max-width:720px){.cp-hero-exact{padding:22px!important}.cp-hero-stats-exact{grid-template-columns:1fr 1fr!important}.cp-customer-exact{grid-template-columns:54px minmax(0,1fr)!important}.cp-profile-grid,.cp-contact-grid{grid-template-columns:1fr}.cp-activity-list div{grid-template-columns:20px minmax(0,1fr)}.cp-activity-list em{grid-column:2}.cp-quick-actions-grid{grid-template-columns:1fr}}


  /* LOOP D2.9.4E.3 — CRM actions + proportional detail polish */
  .cp-detail-match{overflow:visible!important;}
  .cp-detail-section{padding:18px 22px!important;}
  .cp-detail-profile-head{padding:20px 22px!important;}
  .cp-profile-grid,.cp-contact-grid{grid-template-columns:minmax(150px,.82fr) minmax(0,1.18fr)!important;column-gap:22px!important;row-gap:11px!important;align-items:center!important;}
  .cp-profile-grid strong,.cp-contact-grid strong{min-width:0!important;overflow-wrap:anywhere!important;line-height:1.25!important;}
  .cp-benefits-grid{display:grid!important;grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:12px!important;align-items:stretch!important;}
  .cp-benefit-card{min-height:92px!important;height:100%!important;display:grid!important;grid-template-columns:42px minmax(0,1fr)!important;grid-template-rows:auto auto auto!important;column-gap:12px!important;align-content:center!important;padding:16px!important;}
  .cp-benefit-card span{grid-row:1/4!important;width:36px!important;height:36px!important;display:grid!important;place-items:center!important;border-radius:999px!important;}
  .cp-benefit-card small,.cp-benefit-card strong,.cp-benefit-card em{min-width:0!important;white-space:normal!important;overflow-wrap:anywhere!important;line-height:1.18!important;}
  .cp-benefit-card em{display:block!important;margin-top:4px!important;}
  .cp-quick-actions-grid{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:10px!important;align-items:stretch!important;}
  .cp-quick-actions-grid button,.cp-quick-actions-grid a{height:42px!important;min-width:0!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;gap:7px!important;text-align:center!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
  .cp-more-wrap{position:relative;z-index:20;}
  .cp-more-menu{position:absolute;right:0;top:44px;width:210px;border:1px solid #dbe7f0;background:rgba(255,255,255,.98);box-shadow:0 22px 55px rgba(15,23,42,.18);border-radius:18px;padding:7px;display:grid;gap:4px;z-index:9999;backdrop-filter:blur(14px);}
  .cp-more-menu button{border:0;background:transparent;border-radius:12px;min-height:36px;padding:0 10px;text-align:left;color:#0f172a;font-size:12px;font-weight:900;cursor:pointer;}
  .cp-more-menu button:hover{background:#f0fdfa;color:#00675f;}
  .cp-edit-backdrop{position:fixed;inset:0;z-index:1000000;background:rgba(15,23,42,.42);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:18px;}
  .cp-edit-modal{width:min(640px,calc(100vw - 32px));border-radius:28px;background:#fff;border:1px solid #dbe7f0;box-shadow:0 32px 90px rgba(15,23,42,.28);overflow:hidden;}
  .cp-edit-head{display:flex;justify-content:space-between;gap:14px;padding:20px 22px;border-bottom:1px solid #e5edf3;background:linear-gradient(135deg,#fff,#f0fdfa);}
  .cp-edit-head span{display:block;color:#00675f;font-size:10.5px;font-weight:950;letter-spacing:.14em;text-transform:uppercase;}
  .cp-edit-head h3{margin:4px 0 4px;color:#07142b;font-size:24px;font-weight:950;letter-spacing:-.045em;}
  .cp-edit-head p{margin:0;color:#64748b;font-size:12px;font-weight:800;}
  .cp-edit-head button{width:40px;height:40px;border-radius:999px;border:1px solid #dbe7f0;background:#fff;color:#334155;font-size:22px;font-weight:900;cursor:pointer;}
  .cp-edit-body{padding:18px 22px;display:grid;grid-template-columns:1fr 1fr;gap:13px;}
  .cp-edit-body label{display:grid;gap:7px;color:#334155;font-size:12px;font-weight:950;}
  .cp-edit-body label.is-focus{color:#00675f;}
  .cp-edit-body input,.cp-edit-body textarea{width:100%;border:1px solid #dbe7f0;background:#f8fafc;border-radius:15px;padding:0 13px;min-height:44px;color:#0f172a;font-size:13px;font-weight:820;outline:none;}
  .cp-edit-body textarea{padding-top:12px;resize:vertical;min-height:104px;}
  .cp-edit-body input:focus,.cp-edit-body textarea:focus{border-color:#2bb3a7;background:#fff;box-shadow:0 0 0 4px rgba(0,103,95,.08);}
  .cp-edit-full{grid-column:1/-1;}
  .cp-edit-error{grid-column:1/-1;border:1px solid #fecaca;background:#fef2f2;color:#b91c1c;border-radius:14px;padding:10px 12px;font-size:12px;font-weight:900;}
  .cp-edit-actions{display:flex;justify-content:flex-end;gap:10px;padding:14px 22px 20px;border-top:1px solid #edf2f7;}
  .cp-edit-actions button{min-height:42px;border-radius:999px;padding:0 18px;border:0;background:#00675f;color:#fff;font-size:13px;font-weight:950;cursor:pointer;}
  .cp-edit-actions button.ghost{background:#fff;color:#334155;border:1px solid #dbe7f0;}
  .cp-edit-actions button:disabled{opacity:.6;cursor:not-allowed;}


  /* LOOP D2.9.4E.5 — equal CRM list/detail panel heights */
  .cp-layout-exact{
    --loop-crm-panel-height:clamp(620px,calc(100vh - 286px),760px)!important;
    align-items:stretch!important;
  }
  .cp-list-card-exact,
  .cp-detail-match{
    height:var(--loop-crm-panel-height)!important;
    min-height:var(--loop-crm-panel-height)!important;
    max-height:var(--loop-crm-panel-height)!important;
  }
  .cp-list-card-exact{
    display:grid!important;
    grid-template-rows:58px minmax(0,1fr)!important;
  }
  .cp-list-exact{
    min-height:0!important;
    height:100%!important;
    max-height:none!important;
    overflow:auto!important;
  }
  .cp-detail-match{
    overflow:auto!important;
    display:flex!important;
    flex-direction:column!important;
  }
  .cp-quick-actions-section{
    margin-top:auto!important;
  }
  @media(max-width:1180px){
    .cp-layout-exact{--loop-crm-panel-height:auto!important;align-items:start!important;}
    .cp-list-card-exact,.cp-detail-match{height:auto!important;min-height:0!important;max-height:none!important;}
    .cp-list-card-exact{display:block!important;}
    .cp-list-exact{max-height:620px!important;}
    .cp-detail-match{position:static!important;display:block!important;overflow:visible!important;}
  }



  /* LOOP D2.9.4E.6 — real activity + dynamic benefits panel */
  .cp-activity-real-list div{min-height:48px!important;align-items:center!important;}
  .cp-section-title-row button:disabled{opacity:.55;cursor:not-allowed;}
  .cp-benefits-loading{border:1px dashed #dbe7f0;background:#f8fafc;border-radius:14px;padding:12px;color:#64748b;font-size:12px;font-weight:850;}
  .cp-benefits-grid.is-single{grid-template-columns:1fr!important;}
  .cp-benefit-card.is-stamps span{background:#e0f2fe!important;color:#0369a1!important;}
  .cp-benefit-card.is-stamps strong{color:#0369a1!important;}
  .cp-benefit-card.is-inactive{background:#f8fafc!important;border-color:#e2e8f0!important;}
  .cp-benefit-card.is-inactive span{background:#f1f5f9!important;color:#64748b!important;}
  .cp-benefit-card.is-inactive strong{color:#334155!important;}
  .cp-quick-actions-section{display:none!important;}
  .cp-history-backdrop{position:fixed;inset:0;z-index:1000000;background:rgba(15,23,42,.42);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:18px;}
  .cp-history-modal{width:min(760px,calc(100vw - 34px));max-height:min(760px,calc(100vh - 42px));border-radius:28px;background:#fff;border:1px solid #dbe7f0;box-shadow:0 32px 90px rgba(15,23,42,.28);overflow:hidden;display:grid;grid-template-rows:auto minmax(0,1fr);}
  .cp-history-head{display:flex;justify-content:space-between;gap:14px;padding:20px 22px;border-bottom:1px solid #e5edf3;background:linear-gradient(135deg,#fff,#f0fdfa);}
  .cp-history-head span{display:block;color:#00675f;font-size:10.5px;font-weight:950;letter-spacing:.14em;text-transform:uppercase;}
  .cp-history-head h3{margin:4px 0;color:#07142b;font-size:24px;font-weight:950;letter-spacing:-.045em;}
  .cp-history-head p{margin:0;color:#64748b;font-size:12px;font-weight:800;}
  .cp-history-head button{width:40px;height:40px;border-radius:999px;border:1px solid #dbe7f0;background:#fff;color:#334155;font-size:22px;font-weight:900;cursor:pointer;}
  .cp-history-list{overflow:auto;padding:12px;display:grid;gap:9px;}
  .cp-history-row{display:grid;grid-template-columns:36px minmax(0,1fr) auto;gap:12px;align-items:center;border:1px solid #e5edf3;background:#fff;border-radius:16px;padding:12px;}
  .cp-history-row.is-future{border-color:#99f6e4;background:#f0fdfa;}
  .cp-history-icon{width:36px;height:36px;border-radius:999px;background:#e7f6f4;color:#00675f;display:grid;place-items:center;}
  .cp-history-row strong{display:block;color:#07142b;font-size:13px;font-weight:950;}
  .cp-history-row span{display:block;margin-top:2px;color:#64748b;font-size:12px;font-weight:800;}
  .cp-history-row em{font-style:normal;color:#334155;font-size:12px;font-weight:900;white-space:nowrap;}
  .cp-history-empty{border:1px dashed #dbe7f0;background:#f8fafc;border-radius:18px;padding:18px;color:#64748b;font-weight:850;text-align:center;}
  @media(max-width:720px){.cp-history-row{grid-template-columns:32px minmax(0,1fr)}.cp-history-row em{grid-column:2;white-space:normal}.cp-history-modal{max-height:calc(100vh - 26px)}}

  @media(max-width:900px){.cp-edit-body{grid-template-columns:1fr}.cp-quick-actions-grid,.cp-benefits-grid{grid-template-columns:1fr!important}.cp-more-menu{right:auto;left:0}}


  /* LOOP D2.9.4E.8 — CRM compact detail: remove Perfil rápido and align columns inside embedded admin. */
  .cp-status-chip.is-segment{
    background:#f0fdfa!important;
    color:#00675f!important;
    border:1px solid rgba(0,103,95,.12)!important;
  }
  .cp-page.is-embedded .cp-layout-exact{
    align-items:stretch!important;
  }
  .cp-page.is-embedded .cp-list-card-exact,
  .cp-page.is-embedded .cp-detail-match{
    align-self:stretch!important;
    height:auto!important;
    min-height:0!important;
    max-height:none!important;
  }
  .cp-page.is-embedded .cp-list-card-exact{
    display:grid!important;
    grid-template-rows:58px minmax(0,1fr)!important;
  }
  .cp-page.is-embedded .cp-list-exact{
    height:100%!important;
    min-height:0!important;
    max-height:none!important;
    overflow:auto!important;
  }
  .cp-page.is-embedded .cp-detail-match{
    display:flex!important;
    flex-direction:column!important;
    gap:0!important;
  }
  .cp-page.is-embedded .cp-contact-section{
    margin-top:14px!important;
  }
  @media(max-width:1100px){
    .cp-page.is-embedded .cp-layout-exact{
      align-items:start!important;
    }
    .cp-page.is-embedded .cp-list-card-exact{
      display:block!important;
    }
    .cp-page.is-embedded .cp-list-exact{
      height:auto!important;
      max-height:none!important;
      overflow:visible!important;
    }
    .cp-page.is-embedded .cp-contact-section{
      margin-top:0!important;
    }
  }

  /* LOOP D2.9.4E.9 — CRM embedded bounded list height
     Keeps the customer list from stretching the whole admin page.
     The list becomes the only internal scroll and ends near the right detail panel. */
  .cp-page.is-embedded .cp-layout-exact{
    --loop-crm-list-panel-height:clamp(500px,calc(100dvh - 305px),590px)!important;
    align-items:start!important;
  }

  .cp-page.is-embedded .cp-list-card-exact{
    align-self:start!important;
    height:var(--loop-crm-list-panel-height)!important;
    min-height:var(--loop-crm-list-panel-height)!important;
    max-height:var(--loop-crm-list-panel-height)!important;
    display:grid!important;
    grid-template-rows:58px minmax(0,1fr)!important;
    overflow:hidden!important;
  }

  .cp-page.is-embedded .cp-list-exact{
    height:100%!important;
    min-height:0!important;
    max-height:none!important;
    overflow:auto!important;
    overscroll-behavior:contain!important;
  }

  .cp-page.is-embedded .cp-detail-match,
  .cp-page.is-embedded .cp-detail-match-empty{
    align-self:start!important;
    height:auto!important;
    min-height:0!important;
    max-height:none!important;
    overflow:visible!important;
  }

  @media(max-width:1100px){
    .cp-page.is-embedded .cp-layout-exact{
      --loop-crm-list-panel-height:auto!important;
      align-items:start!important;
    }

    .cp-page.is-embedded .cp-list-card-exact{
      height:auto!important;
      min-height:0!important;
      max-height:none!important;
      display:block!important;
      overflow:visible!important;
    }

    .cp-page.is-embedded .cp-list-exact{
      height:auto!important;
      max-height:none!important;
      overflow:visible!important;
      overscroll-behavior:auto!important;
    }
  }

`;


const crmInlineExpansionFixStyles = `
  /* =========================================================
     LOOP CRM FULL-WIDTH INLINE EXPANSION R8
     - Removes the obsolete empty right column.
     - Lets the customer list use the complete CRM width.
     - Expands details below the selected row without a nested scroll panel.
     ========================================================= */
  .cp-page.is-embedded .cp-layout.cp-layout-exact{
    display:grid!important;
    grid-template-columns:minmax(0,1fr)!important;
    width:100%!important;
    max-width:none!important;
    gap:14px!important;
    align-items:start!important;
  }

  .cp-page.is-embedded .cp-list-card.cp-list-card-exact{
    width:100%!important;
    max-width:none!important;
    height:auto!important;
    min-height:0!important;
    max-height:none!important;
    display:block!important;
    align-self:start!important;
    overflow:visible!important;
  }

  .cp-page.is-embedded .cp-list.cp-list-exact{
    width:100%!important;
    height:auto!important;
    min-height:0!important;
    max-height:none!important;
    overflow:visible!important;
    overscroll-behavior:auto!important;
    display:grid!important;
    gap:10px!important;
    padding:10px 10px 14px!important;
  }

  .cp-page.is-embedded .cp-customer-wrap{
    width:100%!important;
    min-width:0!important;
    scroll-margin-top:calc(var(--loop-global-header-h,66px) + 18px)!important;
  }

  .cp-page.is-embedded .cp-customer-wrap.is-expanded{
    width:100%!important;
    overflow:hidden!important;
    border-color:rgba(0,103,95,.18)!important;
    box-shadow:0 18px 42px rgba(15,23,42,.08)!important;
  }

  .cp-page.is-embedded .cp-customer-exact{
    width:100%!important;
    min-width:0!important;
    grid-template-columns:64px minmax(0,1fr) minmax(120px,auto)!important;
  }

  .cp-page.is-embedded .cp-customer-main,
  .cp-page.is-embedded .cp-customer-top,
  .cp-page.is-embedded .cp-customer-line{
    min-width:0!important;
  }

  .cp-page.is-embedded .cp-customer-line span{
    max-width:min(440px,34vw)!important;
  }

  .cp-page.is-embedded .cp-inline-detail{
    width:100%!important;
    min-width:0!important;
    animation:loop-crm-inline-open-r8 .18s ease-out both!important;
  }

  @keyframes loop-crm-inline-open-r8{
    from{opacity:0;transform:translateY(-5px)}
    to{opacity:1;transform:translateY(0)}
  }

  @media(max-width:900px){
    .cp-page.is-embedded .cp-customer-exact{
      grid-template-columns:54px minmax(0,1fr) auto!important;
    }
    .cp-page.is-embedded .cp-customer-line{
      flex-wrap:wrap!important;
      gap:7px 12px!important;
    }
    .cp-page.is-embedded .cp-customer-line span{
      max-width:100%!important;
    }
  }
`;


function customerVipVisible(customer = {}) {
  return Array.isArray(customer.tags) && customer.tags.some((t) => String(t || "").toLowerCase().includes("vip"));
}

async function loadCrmGlobalStats(dbClient, businessId, maxRows = 12000) {
  if (!dbClient || !businessId) return { total: 0, linked: 0, whatsapp: 0, vip: 0, capped: false };
  const customersCol = collection(dbClient, `businesses/${businessId}/customers`);
  let lastDoc = null;
  let scanned = 0;
  const totals = { total: 0, linked: 0, whatsapp: 0, vip: 0, capped: false };

  while (scanned < maxRows) {
    const batchSize = Math.min(500, maxRows - scanned);
    const qBuilt = lastDoc
      ? query(customersCol, orderBy("__name__"), startAfter(lastDoc), fsLimit(batchSize))
      : query(customersCol, orderBy("__name__"), fsLimit(batchSize));
    const snap = await getDocs(qBuilt);
    if (snap.empty) break;
    snap.docs.forEach((d) => {
      scanned += 1;
      const row = crmCustomerRowFromDoc(d);
      if (crmCustomerIsMergedOrHidden(row)) return;
      totals.total += 1;
      if (row.hasUserAccount || row.linkedUserId || row.linkedUserUid || row.userId || row.userUid) totals.linked += 1;
      if (crmWhatsappOk(row)) totals.whatsapp += 1;
      if (customerVipVisible(row)) totals.vip += 1;
    });
    lastDoc = snap.docs[snap.docs.length - 1] || null;
    if (snap.docs.length < batchSize) break;
  }
  totals.capped = scanned >= maxRows;
  return totals;
}

export default function CustomersPage({ businessId: businessIdProp, embedded = false }) {
  useLanguage();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const businessId = businessIdProp || params.businessId || searchParams.get("businessId");

  const [customers, setCustomers] = useState([]);
  const [businessName, setBusinessName] = useState("");
  const [businessCountry, setBusinessCountry] = useState("PA");
  const [globalStats, setGlobalStats] = useState(null);
  const [globalStatsLoading, setGlobalStatsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastCustomerDoc, setLastCustomerDoc] = useState(null);
  const [hasMoreCustomers, setHasMoreCustomers] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [qText, setQText] = useState("");
  const searchCacheRef = useRef(new Map());
  const latestSearchRef = useRef(0);
  const [filter, setFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [duplicateReviewOpen, setDuplicateReviewOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const customerRowRefs = useRef(new Map());
  const [profileModalCustomer, setProfileModalCustomer] = useState(null);
  const profilePanelRef = useRef(null);
  useEffect(()=>{
    if(!profileModalCustomer)return;
    const previous=document.activeElement;
    const overflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    profilePanelRef.current?.querySelector('button')?.focus();
    const keyboard=event=>{
      const panel=profilePanelRef.current;
      if(!panel)return;
      // Leave nested editors in charge of their own dismissal.
      if(event.key==='Escape' && panel.contains(document.activeElement))setProfileModalCustomer(null);
      if(event.key==='Tab'){
        const nodes=[...panel.querySelectorAll('button:not(:disabled),a[href],input,select,textarea,[tabindex="0"]')].filter(node=>node.getClientRects().length);
        const first=nodes[0],last=nodes[nodes.length-1];
        if(event.shiftKey && document.activeElement===first){event.preventDefault();last?.focus();}
        else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first?.focus();}
      }
    };
    document.addEventListener('keydown',keyboard);
    return ()=>{document.body.style.overflow=overflow;document.removeEventListener('keydown',keyboard);previous?.focus?.();};
  },[profileModalCustomer]);

  const loadCustomers = async ({ append = false } = {}) => {
    if (!businessId) return;
    const pageSize = 80;

    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      if (!append) {
        const businessDoc = await getDoc(doc(db, "businesses", businessId));
        if (businessDoc.exists()) {
          const data = businessDoc.data() || {};
          setBusinessName(data?.name || "");
          setBusinessCountry(data?.countryCode || "PA");
          const optimisticTotal = Number(data.visibleCustomersCount ?? data.customersVisibleCount ?? data.customersCount ?? data.customerCount ?? data.totalCustomers);
          const optimisticWhatsapp = Number(data.visibleWhatsappCustomersCount ?? data.whatsappCustomersCount ?? data.customersWithWhatsappCount);
          const optimisticLinked = Number(data.visibleLinkedCustomersCount ?? data.linkedCustomersCount ?? data.customersWithUserCount);
          const optimisticVip = Number(data.visibleVipCustomersCount ?? data.vipCustomersCount);
          if ([optimisticTotal, optimisticWhatsapp, optimisticLinked, optimisticVip].some((n) => Number.isFinite(n))) {
            setGlobalStats((prev) => prev || {
              total: Number.isFinite(optimisticTotal) ? optimisticTotal : 0,
              whatsapp: Number.isFinite(optimisticWhatsapp) ? optimisticWhatsapp : 0,
              linked: Number.isFinite(optimisticLinked) ? optimisticLinked : 0,
              vip: Number.isFinite(optimisticVip) ? optimisticVip : 0,
              optimistic: true,
            });
          }
        }
      }

      const customersCol = collection(db, `businesses/${businessId}/customers`);
      const base = append && lastCustomerDoc
        ? query(customersCol, orderBy("updatedAt", "desc"), startAfter(lastCustomerDoc), fsLimit(pageSize))
        : query(customersCol, orderBy("updatedAt", "desc"), fsLimit(pageSize));

      const snap = await getDocs(base);
      const rows = snap.docs.map(crmCustomerRowFromDoc).filter((row) => !crmCustomerIsMergedOrHidden(row));
      setCustomers((prev) => append ? mergeCrmCustomerRows([...prev, ...rows]) : rows);
      setLastCustomerDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMoreCustomers(snap.docs.length === pageSize);
      setSearchMode(false);
    } catch (e) {
      console.error("[CustomersPage] paged load error", e);
      try {
        const customersCol = collection(db, `businesses/${businessId}/customers`);
        const fallback = append && lastCustomerDoc
          ? query(customersCol, orderBy("__name__"), startAfter(lastCustomerDoc), fsLimit(pageSize))
          : query(customersCol, orderBy("__name__"), fsLimit(pageSize));
        const snap = await getDocs(fallback);
        const rows = snap.docs.map(crmCustomerRowFromDoc).filter((row) => !crmCustomerIsMergedOrHidden(row));
        setCustomers((prev) => append ? mergeCrmCustomerRows([...prev, ...rows]) : rows);
        setLastCustomerDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMoreCustomers(snap.docs.length === pageSize);
        setSearchMode(false);
      } catch (err) {
        console.error("[CustomersPage] fallback paged load error", err);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return undefined;
    let alive = true;
    async function runGlobalStats() {
      setGlobalStatsLoading(true);
      try {
        const nextStats = await loadCrmGlobalStats(db, businessId, 12000);
        if (alive) setGlobalStats(nextStats);
      } catch (err) {
        console.warn("[CustomersPage] global customer stats failed", err);
      } finally {
        if (alive) setGlobalStatsLoading(false);
      }
    }
    runGlobalStats();
    return () => { alive = false; };
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return undefined;
    const term = String(qText || "").trim();
    let cancelled = false;
    let deepTimer = null;
    const searchId = latestSearchRef.current + 1;
    latestSearchRef.current = searchId;

    const mergeSearchRows = (rows = [], localRows = [], keepOnlyMatches = true) => {
      const normalized = normalizeCustomerSearchText(term);
      const digits = term.replace(/\D/g, "");
      setCustomers((prev) => {
        const merged = mergeCrmCustomerRows([...rows, ...localRows, ...prev]);
        return keepOnlyMatches
          ? merged.filter((customer) => customerMatchesCrmSearch(customer, normalized, digits)).slice(0, 180)
          : merged;
      });
    };

    const timer = window.setTimeout(async () => {
      if (term.length < 2) {
        setSearchLoading(false);
        setSearchMode(false);
        if (searchMode) loadCustomers();
        return;
      }

      const normalized = normalizeCustomerSearchText(term);
      const digits = term.replace(/\D/g, "");
      const localRows = customers.filter((customer) => customerMatchesCrmSearch(customer, normalized, digits));
      if (localRows.length) mergeSearchRows([], localRows, false);
      setSearchMode(true);
      setHasMoreCustomers(false);
      setLastCustomerDoc(null);

      const cacheKey = `${businessId}:${normalized}:${digits}`;
      const cached = searchCacheRef.current.get(cacheKey);
      if (cached && Date.now() - Number(cached.savedAt || 0) < 1000 * 60 * 8 && (cached.source === "indexed+scan" || (cached.rows || []).length >= 8 || term.length < 4)) {
        mergeSearchRows(cached.rows || [], localRows, true);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      try {
        const rows = await searchCrmCustomers(db, businessId, term, 120, { deepScan: false });
        if (cancelled || latestSearchRef.current !== searchId) return;
        searchCacheRef.current.set(cacheKey, { rows, savedAt: Date.now(), source: "indexed" });
        mergeSearchRows(rows, localRows, true);
        setSearchLoading(false);

        // V25: legacy/imported rows without nameKey are searched in the background
        // only when the indexed result is too small and the user has paused.
        if (term.length >= 4 && rows.length < 12) {
          deepTimer = window.setTimeout(async () => {
            if (cancelled || latestSearchRef.current !== searchId) return;
            setSearchLoading(true);
            try {
              const scannedRows = await scanCrmCustomersForSearch(db, businessId, term, 1800);
              if (cancelled || latestSearchRef.current !== searchId) return;
              const finalRows = mergeCrmCustomerRows([...rows, ...scannedRows])
                .filter((customer) => customerMatchesCrmSearch(customer, normalized, digits))
                .slice(0, 160);
              searchCacheRef.current.set(cacheKey, { rows: finalRows, savedAt: Date.now(), source: "indexed+scan" });
              mergeSearchRows(finalRows, localRows, true);
            } catch (err) {
              console.warn("[CustomersPage] CRM background legacy scan failed", err);
            } finally {
              if (!cancelled && latestSearchRef.current === searchId) setSearchLoading(false);
            }
          }, 850);
        }
      } catch (e) {
        console.error("[CustomersPage] CRM search failed", e);
        if (!cancelled) {
          setSearchMode(true);
          setSearchLoading(false);
        }
      }
    }, 320);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (deepTimer) window.clearTimeout(deepTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, qText]);

  const stats = useMemo(() => {
    const loadedStats = {
      total: customers.length,
      linked: customers.filter((c) => !!c.hasUserAccount || !!c.linkedUserId || !!c.linkedUserUid || !!c.userId || !!c.userUid).length,
      whatsapp: customers.filter((c) => crmWhatsappOk(c)).length,
      vip: customers.filter((c) => customerVipVisible(c)).length,
    };
    if (globalStats && Number.isFinite(Number(globalStats.total))) {
      return {
        total: Number(globalStats.total || 0),
        linked: Number(globalStats.linked || 0),
        whatsapp: Number(globalStats.whatsapp || 0),
        vip: Number(globalStats.vip || 0),
        capped: globalStats.capped === true,
        loading: globalStatsLoading,
      };
    }
    return { ...loadedStats, loading: globalStatsLoading };
  }, [customers, globalStats, globalStatsLoading]);

  const filtered = useMemo(() => {
    const needle = normalizeCustomerSearchText(qText);
    const digits = String(qText || "").replace(/\D/g, "");

    return customers.filter((c) => {
      if (!matchesCrmSegment(c, filter)) return false;
      if (filter === "linked" && !(c.hasUserAccount || c.linkedUserId)) return false;
      if (filter === "imported" && !(c.source === "imported" || c.importSource)) return false;
      if (filter === "vip" && !(Array.isArray(c.tags) && c.tags.some((t) => String(t).toLowerCase().includes("vip")))) return false;

      if (!needle) return true;

      return customerMatchesCrmSearch(c, needle, digits);
    });
  }, [customers, qText, filter]);

  const saveCustomer = async (customer) => {
    if (!businessId || !customer?.id) return;

    if (customer.dossierExpectedRevision !== undefined) {
      const result = await crmWorkspace(businessId, 'saveDossier', {customerId:customer.id,dossier:customer.dossier,expectedRevision:customer.dossierExpectedRevision});
      setCustomers(previous => previous.map(row => row.id === customer.id ? {...row,dossier:result.dossier} : row));
      setProfileModalCustomer(previous => previous?.id === customer.id ? {...previous,dossier:result.dossier} : previous);
      return;
    }
    const cleanName = String(customer.name || customer.customerName || customer.displayName || "Cliente").trim();
    const cleanPhone = String(customer.phone || customer.phoneNormalized || "").trim();
    const cleanEmail = normalizeCustomerEmail(customer.email || customer.emailNormalized || customer.customerEmail || "");
    const nextTags = Array.isArray(customer.tags) ? customer.tags : [];

    const normalizedPhone = cleanPhone ? normalizeCustomerPhone(cleanPhone, businessCountry) : "";
    const linkedUid = customer.linkedUserUid || customer.linkedUserId || customer.userUid || customer.userId || customer.platformUserUid || customer.platformUserId || "";
    const platformPhone = normalizeCustomerPhone(customer.platformUserPhone || "", businessCountry);
    const platformEmail = normalizeCustomerEmail(customer.platformUserEmail || "");
    const identityNeedsUserProfileSync = Boolean(linkedUid && ((normalizedPhone && platformPhone && normalizedPhone !== platformPhone) || (cleanEmail && platformEmail && cleanEmail !== platformEmail)));

    const patch = {
      name: cleanName,
      customerName: cleanName,
      displayName: cleanName,
      nameKey: normalizeCustomerSearchText(cleanName),
      searchableName: normalizeCustomerSearchText(cleanName),
      searchText: customerSearchText({ ...customer, name: cleanName, phone: normalizedPhone || cleanPhone, email: cleanEmail }),
      phoneDigits: String(normalizedPhone || cleanPhone || "").replace(/\D/g, ""),
      whatsappDigits: String(normalizedPhone || cleanPhone || "").replace(/\D/g, ""),
      phone: normalizedPhone || cleanPhone,
      phoneDisplay: cleanPhone,
      phoneNormalized: normalizedPhone,
      phoneE164: normalizedPhone,
      customerPhoneNormalized: normalizedPhone,
      whatsapp: normalizedPhone || cleanPhone,
      whatsApp: normalizedPhone || cleanPhone,
      email: cleanEmail,
      emailNormalized: cleanEmail,
      customerEmail: cleanEmail,
      notes: customer.notes || "",
      customerNotes: customer.notes || "",

      tags: nextTags,
      hasUserAccount: customer.hasUserAccount === true || Boolean(linkedUid),
      linkedUserId: customer.linkedUserId || customer.linkedUserUid || customer.userId || customer.userUid || null,
      linkedUserUid: customer.linkedUserUid || customer.linkedUserId || customer.userUid || customer.userId || null,
      userId: customer.userId || customer.linkedUserId || null,
      userUid: customer.userUid || customer.linkedUserUid || null,
      platformUserId: customer.platformUserId || customer.linkedUserId || customer.userId || null,
      platformUserUid: customer.platformUserUid || customer.linkedUserUid || customer.userUid || null,
      identityNeedsUserProfileSync,
      identityConflictResolvedByBusiness: identityNeedsUserProfileSync || customer.identityConflictResolvedByBusiness === true,
      identityUpdatedAt: serverTimestamp(),
      whatsappOptOut: customer.whatsappOptOut === true,
      whatsappOptIn: customer.whatsappOptOut !== true,
      customerWhatsappConsent: customer.whatsappOptOut !== true,
      whatsappPhoneStatus: normalizedPhone ? "valid" : "missing",
      whatsappPreferenceUpdatedAt: serverTimestamp(),
      whatsappPreferenceReason: customer.whatsappOptOut === true ? "business_manual_opt_out" : "business_manual_opt_in",
      updatedAt: serverTimestamp(),
    };

    const savedContact = await crmWorkspace(businessId, 'saveContact', {customerId:customer.id,patch:Object.fromEntries(Object.entries(patch).filter(([key])=>!['updatedAt','identityUpdatedAt','whatsappPreferenceUpdatedAt'].includes(key)))});

    const localPatch = {
      ...savedContact.patch,
      updatedAt: new Date().toISOString(),
      whatsappPreferenceUpdatedAt: new Date().toISOString(),
    };
    setCustomers((prev) => prev.map((c) => (c.id === customer.id ? { ...c, ...localPatch } : c)));
    setProfileModalCustomer(previous => previous?.id === customer.id ? {...previous,...localPatch} : previous);
  };

  useEffect(() => {
    if (!selectedCustomerId) return;
    const stillVisible = filtered.some(
      (customer) => String(customer?.id || "") === String(selectedCustomerId)
    );
    if (!stillVisible) setSelectedCustomerId("");
  }, [filtered, selectedCustomerId]);

  const activeCustomer = useMemo(
    () => filtered.find(
      (customer) => String(customer?.id || "") === String(selectedCustomerId)
    ) || null,
    [filtered, selectedCustomerId]
  );

  const toggleCustomerExpansion = (customer) => {
    const customerId = crmCustomerDocumentId(customer);
    if (!customerId) return;

    const opening = String(selectedCustomerId) !== String(customerId);
    setSelectedCustomerId(opening ? customerId : "");

    if (!opening || typeof window === "undefined") return;

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const row = customerRowRefs.current.get(String(customerId));
        if (row && typeof row.scrollIntoView === "function") {
          row.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      }, 90);
    });
  };

  return (
    <div className={`cp-page ${embedded ? "is-embedded" : ""}`}>
      <style>{`${styles}${crmExactVisualStyles}${crmInlineExpansionFixStyles}.cp-membership-grant{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;margin-bottom:10px}.cp-membership-grant select{min-height:38px;border-radius:14px;border:1px solid #dbe4ef;background:#fff;padding:0 10px;font-size:12px;font-weight:850;color:#0f172a}.cp-membership-grant button,.cp-membership-actions button{border:1px solid #00675f;background:#00675f;color:#fff;border-radius:14px;min-height:38px;padding:0 12px;font-size:12px;font-weight:950;cursor:pointer}.cp-membership-grant button:disabled,.cp-membership-actions button:disabled{opacity:.5;cursor:not-allowed}.cp-membership-group{margin-top:10px}.cp-membership-group-title{margin:4px 2px 8px;color:#00675f;font-size:10.5px;font-weight:950;letter-spacing:.12em;text-transform:uppercase}.cp-membership-program{border-radius:20px;border:1px solid #dbe7f0;background:#fff;padding:12px;margin-top:9px}.cp-membership-program.status-active{border-color:rgba(0,103,95,.22);background:linear-gradient(180deg,#fff,#f7fffd)}.cp-membership-program.status-cancelled,.cp-membership-program.status-expired{opacity:.72;background:#f8fafc}.cp-membership-program-head{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}.cp-membership-program-head strong{display:block;color:#0f172a;font-size:14px;font-weight:950}.cp-membership-status{display:inline-flex!important;width:max-content;margin-top:5px!important;border-radius:999px;padding:4px 8px;background:rgba(0,103,95,.08);color:#00675f;font-size:10.5px!important;font-weight:950!important}.cp-membership-status.status-cancelled,.cp-membership-status.status-expired{background:#e2e8f0;color:#475569}.cp-membership-status.status-paused{background:#fef3c7;color:#92400e}.cp-membership-program-head em{font-style:normal;border-radius:999px;background:rgba(0,103,95,.08);color:#00675f;padding:6px 9px;font-size:12px;font-weight:950;white-space:nowrap}.cp-membership-credit-box{margin-top:10px;border-radius:16px;background:#f8fafc;border:1px solid #e2e8f0;padding:10px}.cp-membership-credit-box.is-unlimited{background:linear-gradient(135deg,#ecfdf5,#f0fdfa);border-color:#99f6e4}.cp-membership-credit-box.is-discount{background:linear-gradient(135deg,#fff7ed,#ffffff);border-color:#fed7aa}.cp-membership-credit-box strong{display:block;color:#0f172a;font-size:13px;font-weight:950}.cp-membership-credit-box span{display:block;margin-top:2px;color:#64748b;font-size:11.5px;font-weight:800}.cp-membership-policy-note{display:block;margin-top:7px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:7px 9px;font-size:10.5px;font-weight:850;line-height:1.35}.cp-membership-row{display:flex;justify-content:space-between;gap:10px;margin-top:9px}.cp-membership-row span{color:#0f172a;font-size:12.5px;font-weight:900}.cp-membership-row small{color:#64748b;font-size:11px;font-weight:800;text-align:right}.cp-membership-row.muted span,.cp-membership-row.muted small{font-size:10.8px;color:#64748b}.cp-membership-actions{display:flex;justify-content:flex-end;margin-top:10px}.cp-membership-head-actions{display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end}.cp-membership-history{margin-top:14px;border-top:1px dashed #dbe4ef;padding-top:10px}.cp-membership-activity-ledger{margin-top:10px;border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;padding:9px}.cp-membership-activity-ledger>span{display:block;color:#00675f;font-size:10px;font-weight:950;letter-spacing:.1em;text-transform:uppercase;margin-bottom:6px}.cp-membership-activity-row{display:flex;justify-content:space-between;gap:8px;padding:5px 0;border-top:1px solid rgba(226,232,240,.85)}.cp-membership-activity-row:first-of-type{border-top:0}.cp-membership-activity-row strong{font-size:11.5px;color:#0f172a;font-weight:900}.cp-membership-activity-row small{font-size:10.5px;color:#64748b;font-weight:800;text-align:right;white-space:nowrap}`}
</style>

      <main className="cp-shell cp-shell-exact">
        <section className="cp-hero cp-hero-exact">
          {!embedded ? (
            <div>
              <button className="cp-back" onClick={() => navigate(-1)}>
                <ArrowLeft size={16} />{t("Volver")}</button>
              <span>{t("CRM DE CLIENTES")}</span>
              <h1>{t("Clientes")}</h1>
              <p>{t("Centraliza el historial, contacto, notas, WhatsApp, fidelidad y membresías de todos tus clientes.")}</p>
            </div>
          ) : (
            <div><h1>{t("Clientes")}</h1><p>{t("Contactos, reservas y seguimiento de tu negocio.")}</p></div>
          )}

          <div className="cp-hero-stats cp-hero-stats-exact">
            <div><i>♙</i><strong>{stats.total}</strong><span>{t("Clientes cargados")}</span></div>
            <div><i>☏</i><strong>{stats.whatsapp}</strong><span>{t("Con teléfono")}</span></div>
            <div><i>♙</i><strong>{stats.linked}</strong><span>{t("Cuenta vinculada")}</span></div>
            <div><i>☆</i><strong>{stats.vip}</strong><span>VIP</span></div>
          </div>
        </section>

        <section className="cp-toolbar cp-toolbar-exact">
          <div className="cp-search">
            <Search size={18} />
            <input value={qText} onChange={(e) => setQText(e.target.value)} placeholder={t("Buscar por nombre, teléfono, email o nota...")} />
          </div>

          <div className="cp-filters" aria-label={t("Segmentos de clientes cargados")}>
            {[
              ["all", "Todos"],

              ["new", "Nuevos · 30 días"],
              ["frequent", "Frecuentes"],

              ["followup", "Seguimiento pendiente"],
            ].map(([key, label]) => (
              <button key={key} className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>
                {label}
              </button>
            ))}
          </div>

          <details className="cp-more"><summary>{t("Más opciones")}</summary><button type="button" onClick={()=>setWorkspaceOpen(true)}>{t("Configuración del CRM")}</button><button type="button" onClick={()=>setDuplicateReviewOpen(true)}>{t("Revisar duplicados")}</button><label>{t("Otros filtros")}<select aria-label={t("Otros filtros")} value={filter} onChange={e=>setFilter(e.target.value)}><option value="all">{t("Todos")}</option><option value="imported">{t("Importados")}</option><option value="linked">{t("Cuenta vinculada")}</option><option value="vip">VIP</option><option value="inactive">{t("Sin actividad en Loop · 90 días")}</option><option value="new">{t("Nuevos")}</option><option value="frequent">{t("Frecuentes")}</option><option value="followup">{t("Seguimiento pendiente")}</option></select></label></details>


          <button className="cp-import" onClick={() => setImportOpen(true)}>
            <Upload size={17} />{t("Importar")}</button>
        </section>

        <section className="cp-layout cp-layout-exact">
          <div className="cp-list-card cp-list-card-exact">
            <div className="cp-list-head">
              <strong>{loading ? "Cargando..." : searchMode ? `${filtered.length} resultado${filtered.length === 1 ? "" : "s"}` : `${filtered.length} clientes`}</strong>
              <span>{searchLoading ? "Buscando más..." : searchMode ? "Búsqueda activa" : "Orden: más recientes"}</span>
            </div>

            <div className="cp-list cp-list-exact">
              {!loading && filtered.length === 0 ? (
                <div className="cp-empty">
                  <User size={28} />
                  <h3>{t("No hay clientes")}</h3>
                  <p>{t("Importa una base o crea una cita manual para empezar.")}</p>
                </div>
              ) : null}

              {loading && !searchMode ? Array.from({ length: 5 }).map((_, i) => <div className="cp-skeleton" key={i} />) : null}

              {!loading && filtered.map((c) => {
                const phone = crmContactPhone(c);
                const email = crmContactEmail(c);
                const bookingCount = crmBookingsCount(c);
                const dateText = crmDateNice(c);
                const rowId = crmCustomerDocumentId(c);
                const isActive = Boolean(rowId) && String(activeCustomer?.id || "") === String(rowId);
                return (
                  <div
                    key={rowId || `${c.name}-${phone}-${email}`}
                    ref={(node) => {
                      if (!rowId) return;
                      if (node) customerRowRefs.current.set(String(rowId), node);
                      else customerRowRefs.current.delete(String(rowId));
                    }}
                    className={`cp-customer-wrap ${isActive ? "is-expanded" : ""}`}
                    data-customer-id={rowId || undefined}
                  >
                    <article
                      className={`cp-customer cp-customer-exact ${isActive ? "active" : ""}`}
                      onClick={() => setProfileModalCustomer(c)}
                      role="button" tabIndex={0} aria-label={`Ver ficha de ${c.name || phone || "cliente"}`}
                      onKeyDown={event=>{if(event.target!==event.currentTarget)return;if(event.key==="Enter"||event.key===" "){event.preventDefault();setProfileModalCustomer(c);}}}
                      aria-expanded={isActive ? "true" : "false"}
                    >
                      <div className="cp-avatar">{getInitials(c.name)}</div>

                      <div className="cp-customer-main">
                        <div className="cp-customer-top">
                          <h3>{!c.name || c.name.toLowerCase()==="cliente" ? "Nombre pendiente" : c.name}</h3>
                          <button type="button" aria-label={t("Abrir perfil completo")} onClick={(event) => { event.stopPropagation(); setProfileModalCustomer(c); }}><ExternalLink size={15} /></button>
                        </div>

                        <div className="cp-customer-line">
                          {phone ? <span><Phone size={13} />{phone}</span> : null}
                          {email ? <span><Mail size={13} />{email}</span> : null}
                        </div>

                        <div className="cp-tags">
                          <span className={crmWhatsappOk(c) ? "cp-wa-tag is-on" : "cp-wa-tag is-off"}><MessageCircle size={11} />{crmWhatsappOk(c) ? "Contacto disponible" : "Contacto limitado"}</span>
                          <span><CalendarDays size={11} />{bookingCount > 0 ? `${bookingCount} reserva${bookingCount === 1 ? "" : "s"}` : "Sin visitas registradas en Loop"}</span>
                          <span><Tag size={11} />{crmOriginLabel(c).toLowerCase()}</span>
                        </div>
                      </div>

                      <div className="cp-customer-date">
                        <small>{c.lastBookingAt ? "Última reserva" : c.lastVisitAt ? t("Última visita") : "Registro"}</small><span>{dateText}</span>
                        <button type="button" aria-label={isActive ? "Cerrar detalle" : t("Ver detalle")} aria-expanded={isActive} onClick={(event) => { event.stopPropagation(); setProfileModalCustomer(c); }} className="cp-expand-toggle"><ChevronDown size={18} style={{ transform: isActive ? "rotate(180deg)" : "none" }} /></button>
                      </div>
                    </article>

                    {isActive ? (
                      <div hidden className="cp-inline-detail" onClick={(event) => event.stopPropagation()}>
                        <CustomerInlinePreview customer={c} businessId={businessId} navigate={navigate} onOpenProfile={setProfileModalCustomer} />
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {!loading && !searchMode && hasMoreCustomers ? (
                <button type="button" className="cp-load-more" onClick={() => loadCustomers({ append: true })} disabled={loadingMore}>
                  {loadingMore ? "Cargando más..." : "Cargar más clientes"}
                </button>
              ) : null}
            </div>
          </div>
        </section>
      </main>

      {workspaceOpen && <CrmWorkspaceManager businessId={businessId} onClose={()=>{setWorkspaceOpen(false);void loadCustomers();}} />}
      <CustomerImportModal
        open={importOpen}
        businessId={businessId}
        onClose={() => setImportOpen(false)}
        onDone={() => loadCustomers()}
      />

      <DuplicateReviewModal
        open={duplicateReviewOpen}
        businessId={businessId}
        onClose={() => setDuplicateReviewOpen(false)}
        onMerged={() => loadCustomers()}
      />


      {profileModalCustomer && typeof document !== "undefined" ? createPortal((
        <div className="cp-profile-full-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setProfileModalCustomer(null);
        }}>
          <section ref={profilePanelRef} role="dialog" aria-modal="true" aria-label={t("Ficha del cliente")} className="cp-profile-full-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="cp-profile-full-head">
              <div>
                <span>{t("Ficha del cliente")}</span>

              </div>
              <button type="button" aria-label={t("Cerrar perfil")} onClick={() => setProfileModalCustomer(null)}>×</button>
            </div>
            <CustomerDetailMatch customer={profileModalCustomer} businessId={businessId} onSave={saveCustomer} navigate={navigate} />
          </section>
        </div>
      ), document.body) : null}
    </div>
  );
}

const styles = `


/* LOOP CRM SEARCH IMPORT REDESIGN V24 */
/* LOOP CRM NAME-ONLY SMART DEDUPE V26 */
.cp-dup-list-head{flex-wrap:wrap!important}.cp-dup-smart-v26{width:100%!important;display:grid!important;gap:8px!important;margin-top:10px!important;padding:11px!important;border:1px solid rgba(0,103,95,.14)!important;background:linear-gradient(180deg,#f0fdfa,#ffffff)!important;border-radius:18px!important;box-shadow:0 10px 24px rgba(0,103,95,.05)!important}.cp-dup-smart-v26-head{display:flex!important;align-items:flex-start!important;justify-content:space-between!important;gap:10px!important}.cp-dup-smart-v26-head span{color:#00675f!important;font-size:10px!important;font-weight:950!important;letter-spacing:.12em!important;text-transform:uppercase!important}.cp-dup-smart-v26-head strong{color:#0f172a!important;font-size:12px!important;font-weight:950!important;text-align:right!important}.cp-dup-smart-v26-stats{display:flex!important;flex-wrap:wrap!important;gap:6px!important}.cp-dup-smart-v26-stats em{font-style:normal!important;border:1px solid rgba(0,103,95,.12)!important;background:#fff!important;color:#334155!important;border-radius:999px!important;padding:4px 7px!important;font-size:10.5px!important;font-weight:900!important}.cp-dup-auto-merge{margin-top:2px;border:1px solid #00675f!important;background:#00675f!important;color:#fff!important;border-radius:999px!important;min-height:34px!important;padding:0 11px!important;font-size:11.5px!important;font-weight:950!important;cursor:pointer!important}.cp-dup-auto-merge:disabled{opacity:.55!important;cursor:not-allowed!important;background:#94a3b8!important;border-color:#94a3b8!important}.cp-dup-bulk-message{display:block!important;margin-top:6px!important;color:#00675f!important;font-weight:900!important}.cp-bulk-progress-v27{display:grid!important;gap:7px!important;margin:10px 0!important;padding:10px!important;border:1px solid rgba(0,103,95,.14)!important;border-radius:16px!important;background:#f8fafc!important}.cp-bulk-progress-v27 strong{color:#0f172a!important;font-size:12px!important;font-weight:950!important}.cp-bulk-progress-v27 span{display:block!important;height:9px!important;border-radius:999px!important;background:#e2e8f0!important;overflow:hidden!important}.cp-bulk-progress-v27 span i{display:block!important;height:100%!important;border-radius:999px!important;background:#00675f!important;transition:width .24s ease!important}.cp-bulk-progress-v27 em{font-style:normal!important;color:#64748b!important;font-size:11px!important;font-weight:900!important}
.cp-layout-exact{grid-template-columns:minmax(0,1fr)!important;gap:14px!important;}
.cp-list-card-exact{width:100%!important;}
.cp-list-exact{max-height:none!important;min-height:0!important;overflow:visible!important;display:grid!important;gap:10px!important;}
.cp-customer-wrap{display:grid!important;gap:0!important;border-radius:24px!important;}
.cp-customer-wrap.is-expanded{background:#ffffff!important;border:1px solid rgba(0,103,95,.14)!important;box-shadow:0 18px 42px rgba(15,23,42,.07)!important;overflow:hidden!important;}
.cp-customer-wrap.is-expanded .cp-customer-exact{border-color:transparent!important;border-radius:24px 24px 0 0!important;box-shadow:none!important;}
.cp-inline-detail{border-top:1px solid rgba(226,232,240,.92)!important;background:linear-gradient(180deg,#fbfdfc,#ffffff)!important;padding:12px!important;}
.cp-inline-detail .cp-detail-match{box-shadow:none!important;border:0!important;border-radius:20px!important;background:transparent!important;overflow:visible!important;}
.cp-inline-detail-actions{display:flex!important;justify-content:flex-end!important;margin-bottom:10px!important;}
.cp-inline-detail-actions button{border:1px solid rgba(0,103,95,.18)!important;background:#f0fdfa!important;color:#00675f!important;border-radius:999px!important;min-height:36px!important;padding:0 12px!important;font-size:12px!important;font-weight:950!important;display:inline-flex!important;align-items:center!important;gap:7px!important;cursor:pointer!important;}.cp-inline-summary-v25{display:grid!important;gap:12px!important}.cp-inline-summary-grid-v25{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:10px!important}.cp-inline-summary-card-v25{border:1px solid rgba(226,232,240,.95)!important;background:#fff!important;border-radius:18px!important;padding:12px!important}.cp-inline-summary-card-v25 span,.cp-inline-note-v25 strong{display:block!important;color:#00675f!important;font-size:10px!important;font-weight:950!important;letter-spacing:.12em!important;text-transform:uppercase!important}.cp-inline-summary-card-v25 strong{display:block!important;margin-top:5px!important;color:#0f172a!important;font-size:14px!important;font-weight:950!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important}.cp-inline-summary-card-v25 small{display:block!important;margin-top:4px!important;color:#64748b!important;font-size:11px!important;font-weight:800!important;line-height:1.3!important}.cp-inline-note-v25{border:1px dashed rgba(0,103,95,.18)!important;background:#f8fffd!important;border-radius:18px!important;padding:11px 12px!important}.cp-inline-note-v25 span{display:block!important;margin-top:5px!important;color:#475569!important;font-size:12.5px!important;font-weight:820!important;line-height:1.35!important}.cp-inline-tags-v25{display:flex!important;flex-wrap:wrap!important;gap:6px!important}.cp-inline-tags-v25 span{border-radius:999px!important;background:#eefdf9!important;color:#00675f!important;border:1px solid rgba(0,103,95,.12)!important;padding:5px 8px!important;font-size:11px!important;font-weight:900!important}.cp-inline-actions-v25{display:flex!important;flex-wrap:wrap!important;justify-content:flex-end!important;gap:8px!important}.cp-inline-actions-v25 button,.cp-inline-actions-v25 a{border:1px solid #dbe7f0!important;background:#fff!important;color:#334155!important;border-radius:999px!important;min-height:38px!important;padding:0 13px!important;font-size:12px!important;font-weight:950!important;display:inline-flex!important;align-items:center!important;text-decoration:none!important;cursor:pointer!important}.cp-inline-actions-v25 .primary{background:#00675f!important;border-color:#00675f!important;color:#fff!important}.cp-dup-safe-note{display:block!important;flex-basis:100%!important;margin-top:6px!important;color:#92400e!important;background:#fffbeb!important;border:1px solid #fde68a!important;border-radius:12px!important;padding:7px 9px!important;text-align:left!important;font-size:10.5px!important;line-height:1.35!important}.cp-bulk-confirm .cp-dup-merge-message{margin:10px 0!important}@media(max-width:760px){.cp-inline-summary-grid-v25{grid-template-columns:1fr!important}.cp-inline-actions-v25{justify-content:stretch!important}.cp-inline-actions-v25 button,.cp-inline-actions-v25 a{width:100%!important;justify-content:center!important}}
.cp-profile-full-backdrop{position:fixed!important;inset:0!important;z-index:2147483646!important;background:rgba(15,23,42,.46)!important;backdrop-filter:blur(12px)!important;-webkit-backdrop-filter:blur(12px)!important;display:grid!important;place-items:center!important;padding:18px!important;box-sizing:border-box!important;}
.cp-profile-full-modal{width:min(980px,100%)!important;max-height:min(92dvh,860px)!important;overflow:auto!important;-webkit-overflow-scrolling:touch!important;border-radius:30px!important;border:1px solid rgba(226,232,240,.96)!important;background:#ffffff!important;box-shadow:0 34px 110px rgba(15,23,42,.30)!important;padding:0!important;}
.cp-profile-full-head{position:sticky!important;top:0!important;z-index:2!important;background:rgba(255,255,255,.96)!important;backdrop-filter:blur(10px)!important;-webkit-backdrop-filter:blur(10px)!important;border-bottom:1px solid #edf2f6!important;padding:18px 20px!important;display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;}
.cp-profile-full-head span{display:block!important;color:#00675f!important;font-size:11px!important;font-weight:950!important;letter-spacing:.14em!important;text-transform:uppercase!important;}
.cp-profile-full-head h3{margin:4px 0 0!important;color:#0f172a!important;font-size:24px!important;font-weight:950!important;letter-spacing:-.04em!important;}
.cp-profile-full-head button{width:40px!important;height:40px!important;border-radius:999px!important;border:1px solid #dbe4ef!important;background:#fff!important;color:#0f172a!important;font-size:24px!important;font-weight:800!important;cursor:pointer!important;}
.cp-profile-full-modal .cp-detail-match{box-shadow:none!important;border:0!important;border-radius:0!important;}
.cp-duplicate-review{min-height:42px;border:1px solid rgba(0,103,95,.16);border-radius:999px;background:#f0fdfa;color:#00675f;font-weight:950;padding:0 13px;display:inline-flex;align-items:center;gap:8px;cursor:pointer;white-space:nowrap;}
@media(max-width:760px){.cp-profile-full-backdrop{padding:8px!important;align-items:stretch!important}.cp-profile-full-modal{max-height:96dvh!important;border-radius:24px!important}.cp-inline-detail{padding:8px!important}.cp-inline-detail .cp-detail-profile-head{padding:16px!important}}

  .cp-page.is-embedded{min-height:auto;padding:0;background:transparent}.cp-page.is-embedded .cp-shell{width:100%;max-width:100%;margin:0}.cp-page.is-embedded .cp-hero{box-shadow:none;border-radius:28px;background:linear-gradient(135deg,rgba(0,103,95,.06),#fff)}.cp-page.is-embedded .cp-layout{min-height:560px}.cp-page.is-embedded .cp-detail{max-height:760px;overflow:auto}.cp-page.is-embedded .cp-list{max-height:720px;overflow:auto}.cp-page.is-embedded .cp-hero h1{font-size:32px}


  .cp-duplicate-review{display:inline-flex;align-items:center;gap:8px;border:1px solid #dbe7f0;background:#fff;color:#00675f;border-radius:999px;min-height:44px;padding:0 16px;font-size:13px;font-weight:950;cursor:pointer;white-space:nowrap;box-shadow:0 10px 24px rgba(15,23,42,.04)}.cp-duplicate-review:hover{background:#f0fdfa;border-color:#99f6e4}.cp-dup-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.42);backdrop-filter:blur(8px);z-index:999999;display:flex;align-items:center;justify-content:center;padding:24px}.cp-dup-modal{width:min(1080px,calc(100vw - 36px));max-height:min(820px,calc(100vh - 36px));overflow:hidden;border-radius:30px;background:#fff;border:1px solid #dbe7f0;box-shadow:0 30px 90px rgba(15,23,42,.28);display:flex;flex-direction:column}.cp-dup-head{padding:22px 26px;border-bottom:1px solid #e2e8f0;display:flex;justify-content:space-between;gap:18px;align-items:flex-start;background:linear-gradient(135deg,#fff,rgba(0,103,95,.06))}.cp-dup-head span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.13em;text-transform:uppercase}.cp-dup-head h2{margin:5px 0 3px;color:#0f172a;font-size:26px;font-weight:950;letter-spacing:-.04em}.cp-dup-head p{margin:0;color:#64748b;font-size:13px;font-weight:800;line-height:1.45}.cp-dup-head button{width:42px;height:42px;border-radius:999px;border:1px solid #dbe7f0;background:#fff;color:#0f172a;font-weight:950;cursor:pointer}.cp-dup-body{display:grid;grid-template-columns:360px minmax(0,1fr);min-height:520px;overflow:hidden}.cp-dup-list{border-right:1px solid #e2e8f0;background:#f8fafc;padding:16px;overflow:auto}.cp-dup-list-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:12px}.cp-dup-list-head strong{display:block;color:#0f172a;font-size:15px;font-weight:950}.cp-dup-list-head small{color:#64748b;font-size:11px;font-weight:850;text-align:right}.cp-dup-group{width:100%;border:1px solid #dbe7f0;background:#fff;border-radius:18px;padding:13px;text-align:left;margin-bottom:9px;cursor:pointer;position:relative}.cp-dup-group.active{border-color:#7dd3c7;background:#f0fdfa;box-shadow:0 12px 28px rgba(0,103,95,.08)}.cp-dup-group strong{display:block;color:#0f172a;font-size:14px;font-weight:950}.cp-dup-group span{display:block;margin-top:4px;color:#64748b;font-size:11.5px;font-weight:850;line-height:1.35}.cp-dup-group em{position:absolute;top:10px;right:10px;font-style:normal;border-radius:999px;background:rgba(0,103,95,.09);color:#00675f;font-size:10.5px;font-weight:950;padding:4px 7px}.cp-dup-detail{padding:20px;overflow:auto;background:#fff}.cp-dup-detail-head{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;margin-bottom:14px}.cp-dup-detail-head span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.1em;text-transform:uppercase}.cp-dup-detail-head h3{margin:4px 0 2px;color:#0f172a;font-size:24px;font-weight:950;letter-spacing:-.035em}.cp-dup-detail-head p{margin:0;color:#64748b;font-size:12px;font-weight:850}.cp-dup-detail-head button{border:1px solid #dbe7f0;background:#fff;border-radius:999px;color:#334155;font-size:12px;font-weight:950;min-height:38px;padding:0 14px;cursor:pointer}.cp-dup-primary{border-radius:24px;border:1px solid #99f6e4;background:linear-gradient(135deg,#f0fdfa,#fff);padding:16px;margin-bottom:14px}.cp-dup-primary span,.cp-dup-secondary-list>span{display:block;color:#00675f;font-size:10.5px;font-weight:950;letter-spacing:.11em;text-transform:uppercase;margin-bottom:7px}.cp-dup-primary strong{display:block;color:#0f172a;font-size:20px;font-weight:950}.cp-dup-primary small{display:block;margin-top:4px;color:#64748b;font-size:12.5px;font-weight:850}.cp-dup-primary em{display:inline-flex;margin-top:9px;border-radius:999px;background:#fff;border:1px solid #dbe7f0;color:#0f172a;padding:6px 9px;font-style:normal;font-size:11.5px;font-weight:950}.cp-dup-secondary-list{display:grid;gap:9px}.cp-dup-secondary{display:grid;grid-template-columns:48px minmax(0,1fr);gap:12px;border:1px solid #e2e8f0;border-radius:18px;padding:11px;background:#fff}.cp-dup-secondary strong{display:block;color:#0f172a;font-size:14px;font-weight:950}.cp-dup-secondary small,.cp-dup-secondary em{display:block;margin-top:3px;color:#64748b;font-size:11.5px;font-weight:800;font-style:normal}.cp-dup-next-step{margin-top:15px;border-radius:20px;border:1px solid #fde68a;background:#fffbeb;padding:13px}.cp-dup-next-step strong{display:block;color:#92400e;font-size:13px;font-weight:950}.cp-dup-next-step span{display:block;margin-top:4px;color:#92400e;font-size:12px;font-weight:800;line-height:1.45}.cp-dup-skeleton{height:72px;border-radius:18px;background:linear-gradient(90deg,#eef2f7,#f8fafc,#eef2f7);margin-bottom:9px}.cp-dup-alert,.cp-dup-empty{border-radius:18px;border:1px solid #e2e8f0;background:#fff;padding:16px;color:#64748b;font-size:12.5px;font-weight:850;text-align:center}.cp-dup-empty strong{display:block;margin-top:8px;color:#0f172a;font-size:15px;font-weight:950}.cp-dup-empty span{display:block;margin-top:4px;color:#64748b;font-size:12px;font-weight:800;line-height:1.35}.cp-dup-empty.is-detail{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:360px}.cp-dup-merge-ready button{margin-top:11px;border:0;background:#00675f;color:#fff;border-radius:999px;min-height:40px;padding:0 16px;font-size:12.5px;font-weight:950;cursor:pointer;box-shadow:0 14px 28px rgba(0,103,95,.18)}.cp-dup-merge-ready button:disabled{opacity:.6;cursor:not-allowed}.cp-dup-merge-message{margin-top:10px;border-radius:16px;padding:11px 13px;font-size:12px;font-weight:900}.cp-dup-merge-message.is-ok{background:#ecfdf5;color:#047857;border:1px solid #a7f3d0}.cp-dup-merge-message.is-error{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca}.cp-merge-confirm-backdrop{position:absolute;inset:0;background:rgba(15,23,42,.38);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:18px;z-index:5}.cp-merge-confirm{width:min(520px,100%);border-radius:26px;background:#fff;border:1px solid #dbe7f0;box-shadow:0 26px 70px rgba(15,23,42,.28);padding:22px}.cp-merge-confirm>span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.12em;text-transform:uppercase}.cp-merge-confirm h3{margin:6px 0 6px;color:#0f172a;font-size:24px;font-weight:950;letter-spacing:-.04em}.cp-merge-confirm p{margin:0;color:#64748b;font-size:13px;font-weight:850;line-height:1.45}.cp-merge-preview-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:16px 0}.cp-merge-preview-grid div{border:1px solid #e2e8f0;border-radius:18px;background:#f8fafc;padding:12px}.cp-merge-preview-grid small{display:block;color:#00675f;font-size:10.5px;font-weight:950;letter-spacing:.1em;text-transform:uppercase}.cp-merge-preview-grid strong{display:block;margin-top:5px;color:#0f172a;font-size:13px;font-weight:950}.cp-merge-preview-grid em{display:block;margin-top:3px;color:#64748b;font-size:11.5px;font-style:normal;font-weight:800}.cp-merge-confirm-actions{display:flex;justify-content:flex-end;gap:10px}.cp-merge-confirm-actions button{border:0;background:#00675f;color:#fff;border-radius:999px;min-height:40px;padding:0 16px;font-size:12.5px;font-weight:950;cursor:pointer}.cp-merge-confirm-actions button.ghost{background:#fff;color:#334155;border:1px solid #dbe7f0}.cp-merge-confirm-actions button:disabled{opacity:.6;cursor:not-allowed}@media(max-width:860px){.cp-dup-body{grid-template-columns:1fr}.cp-dup-list{border-right:0;border-bottom:1px solid #e2e8f0;max-height:300px}.cp-dup-backdrop{padding:10px}.cp-dup-modal{border-radius:22px;width:calc(100vw - 20px);max-height:calc(100vh - 20px)}}

  .cp-loyalty-card{margin-top:16px;border:1px solid #dbe4ef;border-radius:24px;background:linear-gradient(180deg,#ffffff,#f8fafc);padding:13px;box-shadow:0 12px 28px rgba(15,23,42,.045)}
  .cp-loyalty-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px}.cp-loyalty-head span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.1em;text-transform:uppercase}.cp-loyalty-head strong{display:block;margin-top:2px;font-size:15px;font-weight:950;color:#0f172a}.cp-loyalty-head button{border:1px solid #dbe4ef;background:#fff;border-radius:999px;min-height:34px;padding:0 10px;font-size:11.5px;font-weight:900;color:#334155;cursor:pointer}.cp-loyalty-head button:disabled{opacity:.55;cursor:not-allowed}
  .cp-loyalty-loading,.cp-loyalty-empty,.cp-loyalty-error{border-radius:18px;padding:12px;background:#fff;border:1px solid #e2e8f0;color:#64748b;font-size:12.5px;font-weight:800}.cp-loyalty-error{background:#fff7ed;border-color:#fed7aa;color:#c2410c}.cp-loyalty-empty strong{display:block;color:#0f172a;font-weight:950}.cp-loyalty-empty small{display:block;margin-top:4px;line-height:1.35;color:#64748b;font-weight:750}
  .cp-loyalty-program{border-radius:20px;border:1px solid #e2e8f0;background:#fff;padding:12px;margin-top:9px}.cp-loyalty-program.has-reward{border-color:#99f6e4;background:linear-gradient(180deg,#f0fdfa,#ffffff)}.cp-loyalty-program-head{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}.cp-loyalty-program-head strong{display:block;color:#0f172a;font-size:14px;font-weight:950}.cp-loyalty-program-head span{display:block;margin-top:3px;color:#64748b;font-size:11.5px;font-weight:800;line-height:1.25}.cp-loyalty-program-head em{font-style:normal;border-radius:999px;background:rgba(0,103,95,.08);color:#00675f;padding:6px 9px;font-size:12px;font-weight:950;white-space:nowrap}
  .cp-loyalty-progress{height:9px;border-radius:999px;background:#e2e8f0;overflow:hidden;margin-top:11px}.cp-loyalty-progress i{display:block;height:100%;border-radius:999px;background:linear-gradient(135deg,#00675f,#14b8a6);transition:width .18s ease}.cp-loyalty-row{display:flex;justify-content:space-between;gap:10px;margin-top:9px}.cp-loyalty-row span{color:#0f172a;font-size:12.5px;font-weight:900}.cp-loyalty-row small{color:#64748b;font-size:11px;font-weight:800;text-align:right}.cp-loyalty-actions{display:grid;grid-template-columns:1fr 1fr 1.2fr;gap:7px;margin-top:10px}.cp-loyalty-actions button{min-height:36px;border-radius:13px;border:1px solid #dbe4ef;background:#fff;color:#334155;font-size:11.5px;font-weight:950;cursor:pointer}.cp-loyalty-actions button.primary{background:#00675f;border-color:#00675f;color:#fff}.cp-loyalty-actions button:disabled{opacity:.5;cursor:not-allowed}
  .cp-wa-toggle{border:1px solid #dbe4ef;border-radius:999px;padding:10px 12px;background:#fff;font-weight:900;color:#0f172a;text-align:left}.cp-wa-toggle.is-on{border-color:#99f6e4;background:#f0fdfa;color:#00675f}.cp-wa-toggle.is-off{border-color:#fed7aa;background:#fff7ed;color:#c2410c}.cp-whatsapp-consent small{display:block;color:#64748b;font-size:12px;font-weight:700;margin-top:6px}.cp-wa-tag.is-on{background:#ecfdf5;color:#047857}.cp-wa-tag.is-off{background:#fff7ed;color:#c2410c}
  .cp-page {
    min-height: 100vh;
    padding: 24px 16px calc(120px + env(safe-area-inset-bottom, 0px));
    background: radial-gradient(circle at top left, rgba(0,103,95,.10), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #ffffff 52%, #f8fafc 100%);
    color: #0f172a;
    box-sizing: border-box;
  }
  .cp-shell {
    width: min(100%, 1180px);
    margin: 0 auto;
    display: grid;
    gap: 16px;
  }
  .cp-hero {
    position: relative;
    overflow: hidden;
    border-radius: 34px;
    border: 1px solid rgba(226,232,240,.94);
    background: rgba(255,255,255,.94);
    box-shadow: 0 24px 72px rgba(15,23,42,.09);
    padding: 24px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 18px;
    align-items: end;
  }
  .cp-hero::after {
    content: "";
    position: absolute;
    right: -90px;
    bottom: -130px;
    width: 300px;
    height: 300px;
    border-radius: 999px;
    background: rgba(0,103,95,.075);
  }
  .cp-back {
    width: fit-content;
    min-height: 38px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #334155;
    border-radius: 999px;
    padding: 0 13px;
    font-size: 13px;
    font-weight: 950;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 14px;
  }
  .cp-hero span {
    display: inline-flex;
    color: #00675f;
    font-size: 12px;
    font-weight: 950;
    letter-spacing: .12em;
    text-transform: uppercase;
    margin-bottom: 8px;
  }
  .cp-hero h1 {
    margin: 0;
    font-size: clamp(34px, 5vw, 58px);
    line-height: .95;
    font-weight: 950;
    letter-spacing: -.07em;
  }
  .cp-hero p {
    margin: 12px 0 0;
    color: #64748b;
    font-size: 15px;
    line-height: 1.45;
    font-weight: 750;
    max-width: 620px;
  }
  .cp-hero-stats {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(3, 108px);
    gap: 10px;
  }
  .cp-hero-stats div {
    border-radius: 22px;
    border: 1px solid #e2e8f0;
    background: #fff;
    padding: 14px;
    box-shadow: 0 12px 28px rgba(15,23,42,.055);
  }
  .cp-hero-stats strong {
    display: block;
    font-size: 25px;
    line-height: 1;
    font-weight: 950;
  }
  .cp-hero-stats span {
    display: block;
    margin-top: 5px;
    color: #64748b;
    font-size: 11px;
    font-weight: 900;
  }
  .cp-toolbar {
    border-radius: 28px;
    border: 1px solid rgba(226,232,240,.94);
    background: rgba(255,255,255,.94);
    box-shadow: 0 16px 42px rgba(15,23,42,.065);
    padding: 12px;
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto auto;
    gap: 10px;
    align-items: center;
  }
  .cp-search {
    min-height: 48px;
    border-radius: 18px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 13px;
    color: #64748b;
  }
  .cp-search input {
    width: 100%;
    border: 0;
    outline: none;
    background: transparent;
    color: #0f172a;
    font-size: 14px;
    font-weight: 750;
  }
  .cp-filters {
    display: flex;
    gap: 7px;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .cp-filters button {
    min-height: 42px;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    background: #fff;
    color: #475569;
    padding: 0 13px;
    font-size: 12.5px;
    font-weight: 900;
    cursor: pointer;
    white-space: nowrap;
  }
  .cp-filters button.active {
    color: white;
    border-color: transparent;
    background: linear-gradient(135deg, #00675f, #14b8a6);
  }
  .cp-import {
    min-height: 42px;
    border: 0;
    border-radius: 999px;
    color: white;
    background: linear-gradient(135deg, #00675f, #14b8a6);
    font-weight: 950;
    padding: 0 15px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .cp-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 16px;
    align-items: start;
  }
  .cp-list-card, .cp-detail {
    border-radius: 30px;
    border: 1px solid rgba(226,232,240,.94);
    background: rgba(255,255,255,.96);
    box-shadow: 0 18px 48px rgba(15,23,42,.07);
    overflow: hidden;
  }
  .cp-list-head {
    padding: 16px 18px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .cp-list-head strong { font-size: 17px; font-weight: 950; }
  .cp-list-head span { color: #64748b; font-size: 12px; font-weight: 800; }
  .cp-list { padding: 12px; display: grid; gap: 10px; }
  .cp-customer {
    border-radius: 24px;
    border: 1px solid #e2e8f0;
    background: #fff;
    padding: 12px;
    display: grid;
    grid-template-columns: 58px 1fr;
    gap: 12px;
    cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
  }
  .cp-customer:hover, .cp-customer.active {
    transform: translateY(-1px);
    border-color: rgba(0,103,95,.24);
    box-shadow: 0 14px 32px rgba(15,23,42,.08);
  }
  .cp-avatar, .cp-detail-avatar {
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(0,103,95,.10), #f8fafc);
    color: #00675f;
    display: grid;
    place-items: center;
    font-weight: 950;
  }
  .cp-avatar { width: 58px; height: 58px; font-size: 16px; }
  .cp-customer-main { min-width: 0; }
  .cp-customer-top { display: flex; justify-content: space-between; gap: 10px; }
  .cp-customer h3 {
    margin: 0;
    font-size: 16px;
    line-height: 1.12;
    font-weight: 950;
    letter-spacing: -.035em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cp-customer-top button {
    border: 0;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
  }
  .cp-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 7px;
  }
  .cp-meta span, .cp-tags span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 999px;
    background: #f8fafc;
    color: #64748b;
    padding: 5px 8px;
    font-size: 11px;
    font-weight: 850;
  }
  .cp-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .cp-tags span { color: #00675f; background: rgba(0,103,95,.07); }
  .cp-detail {
    position: sticky;
    top: 14px;
    padding: 18px;
  }
  .cp-detail-empty {
    display: grid;
    place-items: center;
    text-align: center;
    min-height: 320px;
    color: #64748b;
  }
  .cp-detail-empty h3 { margin: 10px 0 0; color: #0f172a; font-weight: 950; }
  .cp-detail-empty p { margin: 6px 0 0; font-size: 13px; font-weight: 750; }
  .cp-detail-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #64748b;
    font-size: 12px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .08em;
  }
  .cp-detail-head button {
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 999px;
    height: 34px;
    padding: 0 12px;
    font-weight: 900;
    cursor: pointer;
  }
  .cp-detail-avatar {
    width: 90px;
    height: 90px;
    margin: 18px auto 12px;
    font-size: 26px;
    border-radius: 30px;
  }
  .cp-detail h2 {
    margin: 0;
    text-align: center;
    font-size: 26px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: -.055em;
  }
  .cp-detail p {
    margin: 8px 0 0;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }
  .cp-detail-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 9px;
    margin-top: 16px;
  }
  .cp-detail-stats div {
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    padding: 12px;
    text-align: center;
  }
  .cp-detail-stats strong { display: block; font-size: 20px; font-weight: 950; }
  .cp-detail-stats span { display: block; margin-top: 4px; color: #64748b; font-size: 11px; font-weight: 850; }
  .cp-detail-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 14px;
  }
  .cp-detail-actions a, .cp-detail-actions button.cp-qr-action {
    min-height: 42px;
    border-radius: 15px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #334155;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 900;
  }
  .cp-field { display: block; margin-top: 14px; }
  .cp-field span {
    display: block;
    margin-bottom: 7px;
    color: #334155;
    font-size: 13px;
    font-weight: 950;
  }
  .cp-field input, .cp-field textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e2e8f0;
    border-radius: 17px;
    min-height: 48px;
    padding: 0 13px;
    outline: none;
    color: #0f172a;
    font-weight: 750;
  }
  .cp-field textarea {
    min-height: 110px;
    padding-top: 12px;
    resize: vertical;
  }
  .cp-save {
    width: 100%;
    min-height: 48px;
    margin-top: 14px;
    border: 0;
    border-radius: 17px;
    color: white;
    background: linear-gradient(135deg, #00675f, #14b8a6);
    font-weight: 950;
    cursor: pointer;
  }
  .cp-empty {
    min-height: 260px;
    display: grid;
    place-items: center;
    text-align: center;
    color: #64748b;
    padding: 24px;
  }
  .cp-empty h3 { margin: 10px 0 0; color: #0f172a; font-size: 20px; font-weight: 950; }
  .cp-empty p { margin: 6px 0 0; font-size: 13px; font-weight: 750; }
  .cp-skeleton {
    height: 92px;
    border-radius: 24px;
    background: linear-gradient(90deg, #f1f5f9 0%, #f8fafc 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: cpShimmer 1.2s infinite linear;
  }
  @keyframes cpShimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }

  .cp-load-more{width:100%;border:1px solid #dbe4ef;background:#fff;color:#00675f;border-radius:18px;min-height:46px;font-size:13px;font-weight:950;cursor:pointer;margin:10px 0 4px}.cp-load-more:hover{background:#f0fdfa}.cp-load-more:disabled{opacity:.55;cursor:not-allowed}.cp-qr-action{cursor:pointer}.cp-qr-backdrop{position:fixed;inset:0;z-index:10000;background:rgba(15,23,42,.46);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);display:grid;place-items:center;padding:16px;box-sizing:border-box}.cp-qr-modal{width:min(100%,420px);border-radius:30px;border:1px solid #e2e8f0;background:#fff;box-shadow:0 30px 90px rgba(15,23,42,.28);padding:18px}.cp-qr-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px}.cp-qr-head span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.13em;text-transform:uppercase}.cp-qr-head strong{display:block;margin-top:5px;color:#0f172a;font-size:23px;line-height:1;font-weight:950;letter-spacing:-.045em}.cp-qr-head small{display:block;margin-top:7px;color:#64748b;font-size:12px;line-height:1.35;font-weight:750}.cp-qr-head button{width:38px;height:38px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#64748b;font-size:22px;font-weight:900;cursor:pointer}.cp-qr-loading{margin-top:14px;border-radius:18px;background:#f8fafc;border:1px solid #e2e8f0;padding:14px;text-align:center;color:#64748b;font-weight:900}.cp-qr-box{margin:18px auto 12px;width:256px;height:256px;border-radius:28px;border:1px solid #e2e8f0;background:#fff;display:grid;place-items:center;box-shadow:0 16px 42px rgba(15,23,42,.08)}.cp-qr-url{border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;padding:10px;color:#64748b;font-size:11px;font-weight:760;word-break:break-all}.cp-qr-meta{margin-top:9px;display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;color:#64748b;font-size:11px;font-weight:900}.cp-qr-meta span{border:1px solid #e2e8f0;border-radius:999px;padding:7px 9px;background:#f8fafc}.cp-qr-actions{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:10px}.cp-qr-actions button,.cp-qr-actions a{min-height:42px;border-radius:15px;border:1px solid #dbe4ef;background:#fff;color:#334155;text-decoration:none;font-size:12px;font-weight:950;display:flex;align-items:center;justify-content:center;gap:7px;cursor:pointer}.cp-qr-actions a{background:#00675f;border-color:#00675f;color:#fff}.cp-qr-help{margin:12px 2px 0!important;text-align:left!important;color:#64748b!important;font-size:12px!important;line-height:1.4!important;font-weight:750!important}


  /* LOOP D2.9.4E.7 — customer card contact/chip wrap polish */
  .cp-customer-exact{
    align-items:flex-start!important;
    min-height:106px!important;
    padding:14px 18px 14px!important;
    overflow:hidden!important;
  }
  .cp-customer-exact .cp-customer-main{
    min-width:0!important;
    width:100%!important;
    align-self:stretch!important;
    display:flex!important;
    flex-direction:column!important;
    justify-content:center!important;
    padding-right:10px!important;
  }
  .cp-customer-line{
    display:flex!important;
    align-items:center!important;
    flex-wrap:wrap!important;
    gap:6px 14px!important;
    margin-top:7px!important;
    max-width:100%!important;
    min-width:0!important;
  }
  .cp-customer-line span{
    max-width:min(100%,240px)!important;
    min-width:0!important;
    flex:0 1 auto!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .cp-tags{
    display:flex!important;
    flex-wrap:wrap!important;
    align-items:center!important;
    gap:6px!important;
    margin-top:8px!important;
    max-width:100%!important;
    min-width:0!important;
    overflow:hidden!important;
  }
  .cp-tags span{
    flex:0 1 auto!important;
    max-width:100%!important;
    min-width:0!important;
    height:26px!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .cp-customer-date{
    align-self:center!important;
    min-width:112px!important;
    justify-content:flex-end!important;
    padding-right:6px!important;
  }
  @media(max-width:1380px){
    .cp-customer-exact{grid-template-columns:58px minmax(0,1fr) 96px!important;}
    .cp-customer-line span{max-width:180px!important;}
    .cp-tags span{height:25px!important;padding:0 8px!important;font-size:10.5px!important;}
    .cp-customer-date{min-width:88px!important;font-size:11px!important;}
  }

  @media (max-width: 920px) {
    .cp-wa-toggle{border:1px solid #dbe4ef;border-radius:999px;padding:10px 12px;background:#fff;font-weight:900;color:#0f172a;text-align:left}.cp-wa-toggle.is-on{border-color:#99f6e4;background:#f0fdfa;color:#00675f}.cp-wa-toggle.is-off{border-color:#fed7aa;background:#fff7ed;color:#c2410c}.cp-whatsapp-consent small{display:block;color:#64748b;font-size:12px;font-weight:700;margin-top:6px}.cp-wa-tag.is-on{background:#ecfdf5;color:#047857}.cp-wa-tag.is-off{background:#fff7ed;color:#c2410c}
  .cp-page { padding: 12px 10px calc(130px + env(safe-area-inset-bottom, 0px)); }
    .cp-hero {
      grid-template-columns: 1fr;
      border-radius: 28px;
      padding: 18px;
    }
    .cp-hero h1 { font-size: 38px; }
    .cp-hero-stats { grid-template-columns: repeat(3, 1fr); }
    .cp-toolbar {
      grid-template-columns: 1fr;
      border-radius: 24px;
    }
    .cp-filters { width: 100%; }
    .cp-import { width: 100%; justify-content: center; }
    .cp-layout { grid-template-columns: 1fr; }
    .cp-detail {
      position: static;
      border-radius: 28px;
    }
    .cp-list-card { border-radius: 26px; }
  }


/* =========================================================
   LOOP CUSTOMERS — EMBEDDED CRM PREMIUM CLEANUP
   - BusinessEditPage already prints "Clientes", so embedded CRM hides its own title.
   - Right customer profile does not create a second internal scroll.
   - Keeps only the customer list as the internal scroll; the page owns the rest.
   ========================================================= */

.cp-page.is-embedded .cp-hero-exact{
  min-height:auto!important;
  padding:0!important;
  border:0!important;
  border-radius:0!important;
  background:transparent!important;
  box-shadow:none!important;
  display:grid!important;
  grid-template-columns:1fr!important;
  gap:12px!important;
  overflow:visible!important;
}

.cp-page.is-embedded .cp-hero-exact:after,
.cp-page.is-embedded .cp-hero-exact:before{
  display:none!important;
}

.cp-page.is-embedded .cp-embedded-hero-spacer{
  display:none!important;
}

.cp-page.is-embedded .cp-hero-stats-exact{
  width:100%!important;
  display:grid!important;
  grid-template-columns:repeat(4,minmax(0,1fr))!important;
  gap:12px!important;
  margin:0!important;
}

.cp-page.is-embedded .cp-hero-stats-exact div{
  min-height:76px!important;
  padding:14px 16px!important;
  border-radius:20px!important;
  border:1px solid rgba(226,232,240,.92)!important;
  background:rgba(255,255,255,.94)!important;
  box-shadow:0 10px 28px rgba(15,23,42,.045)!important;
}

.cp-page.is-embedded .cp-hero-stats-exact div strong{
  font-size:24px!important;
  line-height:1!important;
}

.cp-page.is-embedded .cp-hero-stats-exact div span{
  font-size:11.5px!important;
  line-height:1.15!important;
  font-weight:850!important;
}

.cp-page.is-embedded .cp-toolbar-exact{
  margin-top:2px!important;
  border-radius:22px!important;
  padding:10px!important;
  min-height:64px!important;
  box-shadow:0 12px 32px rgba(15,23,42,.045)!important;
}

.cp-page.is-embedded .cp-layout-exact{
  align-items:start!important;
  gap:18px!important;
  min-height:0!important;
}

/* Important: remove the second/right scroll */
.cp-page.is-embedded .cp-detail,
.cp-page.is-embedded .cp-detail-match,
.cp-page.is-embedded .cp-detail-match-empty{
  max-height:none!important;
  height:auto!important;
  min-height:0!important;
  overflow:visible!important;
  position:relative!important;
}

/* The list can keep one controlled internal scroll so thousands of clients don't stretch the page. */
.cp-page.is-embedded .cp-list-card-exact{
  overflow:hidden!important;
}

.cp-page.is-embedded .cp-list-exact{
  max-height:calc(100dvh - 430px)!important;
  min-height:430px!important;
  overflow:auto!important;
}

/* Detail panel becomes a clean non-scrolling customer profile */
.cp-page.is-embedded .cp-detail-match{
  padding:0!important;
  display:grid!important;
  gap:14px!important;
}

.cp-page.is-embedded .cp-detail-profile-head{
  padding:20px 20px 16px!important;
  border-bottom:1px solid rgba(226,232,240,.9)!important;
}

.cp-page.is-embedded .cp-detail-section{
  margin:0 14px!important;
  border-radius:18px!important;
  border:1px solid rgba(226,232,240,.9)!important;
  background:#fff!important;
  box-shadow:none!important;
}

.cp-page.is-embedded .cp-detail-section:last-of-type{
  margin-bottom:14px!important;
}

.cp-page.is-embedded .cp-profile-quick,
.cp-page.is-embedded .cp-contact-section,
.cp-page.is-embedded .cp-activity-section,
.cp-page.is-embedded .cp-benefits-section{
  padding:16px!important;
}

.cp-page.is-embedded .cp-profile-grid,
.cp-page.is-embedded .cp-contact-grid{
  gap:8px 12px!important;
}

/* Reduce visual noise from secondary internal action menus */
.cp-page.is-embedded .cp-detail-menu{
  width:38px!important;
  height:38px!important;
  border-radius:14px!important;
}

/* Mobile/tablet: no double title, compact stats, detail should open naturally below list */
@media(max-width:1100px){
  .cp-page.is-embedded .cp-hero-stats-exact{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }

  .cp-page.is-embedded .cp-layout-exact{
    grid-template-columns:1fr!important;
  }

  .cp-page.is-embedded .cp-list-exact{
    max-height:none!important;
    min-height:0!important;
    overflow:visible!important;
  }

  .cp-page.is-embedded .cp-detail,
  .cp-page.is-embedded .cp-detail-match{
    overflow:visible!important;
    max-height:none!important;
  }
}

@media(max-width:620px){
  .cp-page.is-embedded .cp-hero-stats-exact{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:8px!important;
  }

  .cp-page.is-embedded .cp-hero-stats-exact div{
    min-height:66px!important;
    padding:11px!important;
    border-radius:16px!important;
  }

  .cp-page.is-embedded .cp-hero-stats-exact div strong{
    font-size:20px!important;
  }

  .cp-page.is-embedded .cp-toolbar-exact{
    border-radius:18px!important;
  }

  .cp-page.is-embedded .cp-detail-section{
    margin-left:8px!important;
    margin-right:8px!important;
  }
}



/* =========================================================
   LOOP CUSTOMERS — BENEFITS CRM PREMIUM V2
   - Upgrades Fidelidad/Membresías summary cards inside CRM.
   - Opens benefits manager as a true full-screen portal modal.
   - Makes the benefits manager visually simpler/friendlier.
   ========================================================= */

/* CRM detail benefit cards: cleaner, more premium, less noisy */
.cp-page.is-embedded .cp-benefits-section,
.cp-benefits-section{
  background:linear-gradient(180deg,#ffffff 0%,#fbfdfc 100%)!important;
  border-color:rgba(219,231,240,.96)!important;
}

.cp-benefits-section .cp-section-title-row{
  margin-bottom:12px!important;
}

.cp-benefits-section .cp-section-title-row h3{
  font-size:17px!important;
  letter-spacing:-.02em!important;
}

.cp-benefits-grid{
  display:grid!important;
  grid-template-columns:repeat(2,minmax(0,1fr))!important;
  gap:12px!important;
}

.cp-benefits-grid.is-single{
  grid-template-columns:1fr!important;
}

.cp-benefit-card{
  position:relative!important;
  display:grid!important;
  grid-template-columns:44px minmax(0,1fr)!important;
  grid-template-areas:
    "icon eyebrow"
    "icon value"
    "icon helper"
    "action action"!important;
  align-items:center!important;
  gap:2px 12px!important;
  min-height:132px!important;
  padding:18px!important;
  border-radius:22px!important;
  border:1px solid rgba(219,231,240,.95)!important;
  background:
    radial-gradient(circle at 88% 18%,rgba(0,103,95,.08),transparent 0 34%,transparent 54%),
    linear-gradient(180deg,#ffffff 0%,#fbfaf7 100%)!important;
  box-shadow:0 14px 34px rgba(15,23,42,.045)!important;
  overflow:hidden!important;
}

.cp-benefit-card::after{
  content:""!important;
  position:absolute!important;
  right:-32px!important;
  bottom:-36px!important;
  width:104px!important;
  height:104px!important;
  border-radius:999px!important;
  background:rgba(0,103,95,.055)!important;
  pointer-events:none!important;
}

.cp-benefit-card > span{
  grid-area:icon!important;
  width:44px!important;
  height:44px!important;
  border-radius:16px!important;
  display:grid!important;
  place-items:center!important;
  background:#e8f6f3!important;
  color:#00675f!important;
  border:1px solid rgba(0,103,95,.12)!important;
  font-size:0!important;
  box-shadow:0 10px 22px rgba(0,103,95,.08)!important;
}

.cp-benefit-card > span::before{
  font-size:18px!important;
  line-height:1!important;
}

.cp-benefit-card.is-stamps > span::before,
.cp-benefit-card.is-points > span::before{
  content:"✦"!important;
}

.cp-benefit-card.is-membership > span::before{
  content:"♕"!important;
}

.cp-benefit-card > small{
  grid-area:eyebrow!important;
  color:#00675f!important;
  font-size:10.5px!important;
  line-height:1!important;
  font-weight:950!important;
  letter-spacing:.11em!important;
  text-transform:uppercase!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}

.cp-benefit-card > strong{
  grid-area:value!important;
  color:#07142b!important;
  font-size:22px!important;
  line-height:1!important;
  font-weight:950!important;
  letter-spacing:-.035em!important;
}

.cp-benefit-card > em{
  grid-area:helper!important;
  margin-top:3px!important;
  color:#53647b!important;
  font-size:12px!important;
  line-height:1.28!important;
  font-style:normal!important;
  font-weight:780!important;
  display:-webkit-box!important;
  -webkit-line-clamp:2!important;
  -webkit-box-orient:vertical!important;
  overflow:hidden!important;
}

.cp-benefit-card.is-inactive{
  background:
    radial-gradient(circle at 88% 18%,rgba(148,163,184,.10),transparent 0 34%,transparent 54%),
    linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)!important;
}

.cp-benefit-card.is-inactive > span{
  background:#f1f5f9!important;
  color:#64748b!important;
  border-color:rgba(203,213,225,.9)!important;
}

.cp-benefit-inline-action{
  grid-area:action!important;
  justify-self:start!important;
  margin-top:12px!important;
  min-height:36px!important;
  border-radius:999px!important;
  padding:0 14px!important;
  border:1px solid rgba(0,103,95,.16)!important;
  background:#f0fdfa!important;
  color:#00675f!important;
  font-size:12px!important;
  line-height:1!important;
  font-weight:950!important;
  box-shadow:none!important;
}

/* True full screen modal: no longer trapped by the CRM/right panel */
.cp-benefits-manager-backdrop.cp-benefits-manager-backdrop-premium,
.cp-benefits-manager-backdrop-premium{
  position:fixed!important;
  inset:0!important;
  width:100vw!important;
  height:100dvh!important;
  z-index:2147483646!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:28px!important;
  background:rgba(15,23,42,.56)!important;
  backdrop-filter:blur(14px)!important;
  -webkit-backdrop-filter:blur(14px)!important;
}

.cp-benefits-manager-modal.cp-benefits-manager-modal-premium,
.cp-benefits-manager-modal-premium{
  width:min(760px,calc(100vw - 42px))!important;
  max-height:calc(100dvh - 56px)!important;
  overflow:hidden!important;
  border-radius:32px!important;
  background:#ffffff!important;
  border:1px solid rgba(226,232,240,.98)!important;
  box-shadow:0 40px 110px rgba(15,23,42,.34)!important;
}

/* The inner customer-detail manager becomes a modal sheet, not a full CRM page */
.cp-benefits-manager-modal-premium > .cp-detail{
  width:100%!important;
  max-width:none!important;
  height:auto!important;
  max-height:calc(100dvh - 56px)!important;
  min-height:0!important;
  overflow:auto!important;
  border:0!important;
  border-radius:32px!important;
  background:#fff!important;
  box-shadow:none!important;
  padding:0 18px 18px!important;
}

/* Hide noisy duplicated client header inside benefits manager */
.cp-benefits-manager-modal-premium .cp-detail-head{
  position:sticky!important;
  top:0!important;
  z-index:4!important;
  height:70px!important;
  margin:0 -18px 14px!important;
  padding:0 22px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  border-bottom:1px solid rgba(226,232,240,.92)!important;
  background:rgba(255,255,255,.97)!important;
  backdrop-filter:blur(18px)!important;
  -webkit-backdrop-filter:blur(18px)!important;
}

.cp-benefits-manager-modal-premium .cp-detail-head button{
  width:38px!important;
  height:38px!important;
  border-radius:999px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#fff!important;
  color:#344054!important;
  display:grid!important;
  place-items:center!important;
}

.cp-benefits-manager-modal-premium .cp-detail-head span{
  color:#00675f!important;
  font-size:11px!important;
  font-weight:950!important;
  letter-spacing:.14em!important;
  text-transform:uppercase!important;
}

/* Remove the heavy profile block so the modal focuses on fidelity/membership */
.cp-benefits-manager-modal-premium .cp-detail > .cp-detail-avatar,
.cp-benefits-manager-modal-premium .cp-detail > h2,
.cp-benefits-manager-modal-premium .cp-detail > p,
.cp-benefits-manager-modal-premium .cp-detail > .cp-detail-stats,
.cp-benefits-manager-modal-premium .cp-detail > .cp-detail-actions{
  display:none!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-card{
  margin:0 0 14px!important;
  padding:16px!important;
  border-radius:24px!important;
  border:1px solid rgba(219,231,240,.96)!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfdfc 100%)!important;
  box-shadow:0 14px 34px rgba(15,23,42,.045)!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-head{
  display:flex!important;
  align-items:flex-start!important;
  justify-content:space-between!important;
  gap:12px!important;
  margin-bottom:12px!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-head span{
  color:#00675f!important;
  font-size:10.5px!important;
  font-weight:950!important;
  letter-spacing:.14em!important;
  text-transform:uppercase!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-head strong{
  display:block!important;
  color:#07142b!important;
  font-size:18px!important;
  line-height:1.08!important;
  letter-spacing:-.025em!important;
  font-weight:950!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-head button,
.cp-benefits-manager-modal-premium .cp-membership-head-actions button{
  min-height:38px!important;
  border-radius:999px!important;
  border:1px solid rgba(203,213,225,.92)!important;
  background:#fff!important;
  color:#344054!important;
  padding:0 12px!important;
  font-size:11.5px!important;
  font-weight:950!important;
  white-space:nowrap!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-program,
.cp-benefits-manager-modal-premium .cp-membership-program,
.cp-benefits-manager-modal-premium .cp-loyalty-empty{
  border-radius:20px!important;
  border:1px solid rgba(226,232,240,.95)!important;
  background:#fff!important;
  box-shadow:none!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-program{
  padding:14px!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-program-head{
  display:flex!important;
  justify-content:space-between!important;
  gap:14px!important;
  align-items:flex-start!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-program-head strong,
.cp-benefits-manager-modal-premium .cp-membership-program-head strong{
  font-size:15px!important;
  color:#07142b!important;
  font-weight:950!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-progress{
  height:8px!important;
  margin:12px 0 8px!important;
  border-radius:999px!important;
  background:#edf2f7!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-progress i{
  background:linear-gradient(90deg,#00675f,#17b9a5)!important;
  border-radius:999px!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-actions,
.cp-benefits-manager-modal-premium .cp-membership-actions{
  display:grid!important;
  grid-template-columns:repeat(3,minmax(0,1fr))!important;
  gap:8px!important;
  margin-top:12px!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-actions button,
.cp-benefits-manager-modal-premium .cp-membership-actions button,
.cp-benefits-manager-modal-premium .cp-membership-grant button{
  min-height:40px!important;
  border-radius:14px!important;
  font-size:12px!important;
  font-weight:950!important;
}

.cp-benefits-manager-modal-premium .cp-loyalty-actions button.primary,
.cp-benefits-manager-modal-premium .cp-membership-actions button.primary,
.cp-benefits-manager-modal-premium .cp-membership-grant button{
  background:#00796f!important;
  border-color:#00796f!important;
  color:#fff!important;
}

.cp-benefits-manager-modal-premium .cp-membership-grant{
  display:grid!important;
  grid-template-columns:minmax(0,1fr) 92px!important;
  gap:8px!important;
  margin-bottom:12px!important;
}

.cp-benefits-manager-modal-premium .cp-membership-grant select,
.cp-benefits-manager-modal-premium .cp-membership-adjust-form input,
.cp-benefits-manager-modal-premium .cp-membership-adjust-form select,
.cp-benefits-manager-modal-premium .cp-membership-adjust-form textarea{
  border-radius:14px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#fff!important;
  color:#07142b!important;
  font-weight:850!important;
}

.cp-benefits-manager-modal-premium .cp-field,
.cp-benefits-manager-modal-premium .cp-save{
  display:none!important;
}

/* Inner renewal / credit / QR modals above the benefits modal */
.cp-benefits-manager-modal-premium .cp-qr-backdrop,
.cp-qr-backdrop{
  z-index:2147483647!important;
  background:rgba(15,23,42,.50)!important;
  backdrop-filter:blur(12px)!important;
  -webkit-backdrop-filter:blur(12px)!important;
}

.cp-benefits-manager-modal-premium .cp-qr-modal,
.cp-qr-modal{
  border-radius:28px!important;
  border:1px solid rgba(226,232,240,.96)!important;
  box-shadow:0 34px 90px rgba(15,23,42,.28)!important;
}

/* Responsive */
@media(max-width:900px){
  .cp-benefits-grid{
    grid-template-columns:1fr!important;
  }

  .cp-benefits-manager-backdrop-premium{
    padding:12px!important;
    align-items:flex-end!important;
  }

  .cp-benefits-manager-modal-premium{
    width:100%!important;
    max-height:calc(100dvh - 24px)!important;
    border-radius:28px 28px 0 0!important;
  }

  .cp-benefits-manager-modal-premium > .cp-detail{
    max-height:calc(100dvh - 24px)!important;
    border-radius:28px 28px 0 0!important;
  }
}

@media(max-width:560px){
  .cp-benefit-card{
    grid-template-columns:40px minmax(0,1fr)!important;
    min-height:118px!important;
    padding:15px!important;
  }

  .cp-benefit-card > span{
    width:40px!important;
    height:40px!important;
    border-radius:14px!important;
  }

  .cp-benefits-manager-modal-premium .cp-loyalty-head{
    display:grid!important;
  }

  .cp-benefits-manager-modal-premium .cp-membership-head-actions{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
  }

  .cp-benefits-manager-modal-premium .cp-loyalty-actions,
  .cp-benefits-manager-modal-premium .cp-membership-actions{
    grid-template-columns:1fr!important;
  }
}



/* =========================================================
   LOOP CUSTOMERS — EDIT + HISTORY PORTAL PREMIUM FIX
   The customer edit modal and recent activity history modal now behave
   like true app-level popups, not panels trapped inside CRM detail.
   ========================================================= */

.cp-edit-backdrop.cp-edit-backdrop-premium,
.cp-history-backdrop.cp-history-backdrop-premium{
  position:fixed!important;
  inset:0!important;
  width:100vw!important;
  height:100dvh!important;
  z-index:2147483646!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:28px!important;
  background:rgba(15,23,42,.56)!important;
  backdrop-filter:blur(14px)!important;
  -webkit-backdrop-filter:blur(14px)!important;
}

.cp-edit-modal.cp-edit-modal-premium,
.cp-history-modal.cp-history-modal-premium{
  width:min(720px,calc(100vw - 42px))!important;
  max-height:calc(100dvh - 56px)!important;
  overflow:hidden!important;
  border-radius:32px!important;
  background:#ffffff!important;
  border:1px solid rgba(226,232,240,.98)!important;
  box-shadow:0 40px 110px rgba(15,23,42,.34)!important;
}

.cp-history-modal.cp-history-modal-premium{
  width:min(840px,calc(100vw - 42px))!important;
}

.cp-edit-modal-premium .cp-edit-head,
.cp-history-modal-premium .cp-history-head{
  position:sticky!important;
  top:0!important;
  z-index:4!important;
  display:flex!important;
  align-items:flex-start!important;
  justify-content:space-between!important;
  gap:16px!important;
  padding:22px 24px!important;
  border-bottom:1px solid rgba(226,232,240,.92)!important;
  background:rgba(255,255,255,.97)!important;
  backdrop-filter:blur(18px)!important;
  -webkit-backdrop-filter:blur(18px)!important;
}

.cp-edit-modal-premium .cp-edit-head span,
.cp-history-modal-premium .cp-history-head span{
  color:#00675f!important;
  font-size:11px!important;
  font-weight:950!important;
  letter-spacing:.14em!important;
  text-transform:uppercase!important;
}

.cp-edit-modal-premium .cp-edit-head h3,
.cp-history-modal-premium .cp-history-head h3{
  margin:4px 0 0!important;
  color:#07142b!important;
  font-size:24px!important;
  line-height:1.05!important;
  letter-spacing:-.04em!important;
  font-weight:950!important;
}

.cp-edit-modal-premium .cp-edit-head p,
.cp-history-modal-premium .cp-history-head p{
  margin:6px 0 0!important;
  color:#667085!important;
  font-size:13px!important;
  line-height:1.35!important;
  font-weight:780!important;
}

.cp-edit-modal-premium .cp-edit-head button,
.cp-history-modal-premium .cp-history-head button{
  width:40px!important;
  height:40px!important;
  flex:0 0 40px!important;
  border-radius:999px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#f8fafc!important;
  color:#344054!important;
  display:grid!important;
  place-items:center!important;
  font-size:22px!important;
  line-height:1!important;
}

.cp-edit-modal-premium .cp-edit-body{
  display:grid!important;
  grid-template-columns:1fr 1fr!important;
  gap:14px!important;
  padding:22px 24px!important;
  max-height:calc(100dvh - 245px)!important;
  overflow:auto!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfdfc 100%)!important;
}

.cp-edit-modal-premium .cp-edit-body label{
  display:grid!important;
  gap:7px!important;
  color:#475467!important;
  font-size:11.5px!important;
  font-weight:950!important;
  letter-spacing:.04em!important;
  text-transform:uppercase!important;
}

.cp-edit-modal-premium .cp-edit-body label.is-focus{
  color:#00675f!important;
}

.cp-edit-modal-premium .cp-edit-body input,
.cp-edit-modal-premium .cp-edit-body textarea{
  width:100%!important;
  border-radius:16px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#ffffff!important;
  color:#07142b!important;
  padding:0 14px!important;
  font-size:14px!important;
  font-weight:850!important;
  letter-spacing:0!important;
  text-transform:none!important;
  outline:none!important;
  box-shadow:0 7px 16px rgba(15,23,42,.025)!important;
}

.cp-edit-modal-premium .cp-edit-body input{
  height:46px!important;
}

.cp-edit-modal-premium .cp-edit-body textarea{
  min-height:110px!important;
  padding-top:12px!important;
  resize:vertical!important;
}

.cp-edit-modal-premium .cp-edit-body input:focus,
.cp-edit-modal-premium .cp-edit-body textarea:focus{
  border-color:rgba(0,103,95,.38)!important;
  box-shadow:0 0 0 4px rgba(0,103,95,.08)!important;
}

.cp-edit-modal-premium .cp-edit-full,
.cp-edit-modal-premium .cp-edit-error{
  grid-column:1 / -1!important;
}

.cp-edit-modal-premium .cp-edit-error{
  border-radius:16px!important;
  padding:12px 14px!important;
  background:#fff7ed!important;
  border:1px solid #fed7aa!important;
  color:#9a3412!important;
  font-size:12px!important;
  font-weight:850!important;
}

.cp-edit-modal-premium .cp-edit-actions{
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:10px!important;
  padding:18px 24px!important;
  border-top:1px solid rgba(226,232,240,.92)!important;
  background:rgba(255,255,255,.98)!important;
}

.cp-edit-modal-premium .cp-edit-actions button{
  min-width:132px!important;
  height:48px!important;
  border-radius:16px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#fff!important;
  color:#07142b!important;
  padding:0 18px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  font-size:13px!important;
  line-height:1!important;
  font-weight:950!important;
}

.cp-edit-modal-premium .cp-edit-actions button:not(.ghost){
  background:#00796f!important;
  border-color:#00796f!important;
  color:#fff!important;
  box-shadow:0 18px 36px rgba(0,103,95,.22)!important;
}

.cp-history-modal-premium .cp-history-list{
  max-height:calc(100dvh - 170px)!important;
  overflow:auto!important;
  padding:16px 18px 22px!important;
  display:grid!important;
  gap:10px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfdfc 100%)!important;
}

.cp-history-modal-premium .cp-history-row{
  display:grid!important;
  grid-template-columns:42px minmax(0,1fr) auto!important;
  gap:12px!important;
  align-items:center!important;
  min-height:72px!important;
  padding:12px 14px!important;
  border-radius:18px!important;
  border:1px solid rgba(226,232,240,.95)!important;
  background:#fff!important;
  box-shadow:0 10px 26px rgba(15,23,42,.035)!important;
}

.cp-history-modal-premium .cp-history-row.is-future{
  border-color:rgba(0,103,95,.22)!important;
  background:linear-gradient(180deg,#f0fdfa 0%,#ffffff 100%)!important;
}

.cp-history-modal-premium .cp-history-icon{
  width:42px!important;
  height:42px!important;
  border-radius:15px!important;
  display:grid!important;
  place-items:center!important;
  background:#e8f6f3!important;
  color:#00675f!important;
  border:1px solid rgba(0,103,95,.12)!important;
}

.cp-history-modal-premium .cp-history-row strong{
  display:block!important;
  color:#07142b!important;
  font-size:14px!important;
  line-height:1.1!important;
  font-weight:950!important;
}

.cp-history-modal-premium .cp-history-row span{
  display:block!important;
  margin-top:4px!important;
  color:#667085!important;
  font-size:12px!important;
  line-height:1.28!important;
  font-weight:780!important;
}

.cp-history-modal-premium .cp-history-row em{
  color:#53647b!important;
  font-size:12px!important;
  line-height:1.25!important;
  font-weight:900!important;
  font-style:normal!important;
  white-space:nowrap!important;
}

.cp-history-modal-premium .cp-history-empty{
  min-height:170px!important;
  border-radius:22px!important;
  border:1px dashed rgba(203,213,225,.95)!important;
  display:grid!important;
  place-items:center!important;
  color:#667085!important;
  font-size:13px!important;
  font-weight:850!important;
  background:#f8fafc!important;
}

/* Dropdown actions should float above detail panel, but below true modals */
.cp-more-menu{
  z-index:5000!important;
}

/* Mobile: edit and history behave as bottom sheets */
@media(max-width:720px){
  .cp-edit-backdrop-premium,
  .cp-history-backdrop-premium{
    padding:10px!important;
    align-items:flex-end!important;
  }

  .cp-edit-modal-premium,
  .cp-history-modal-premium{
    width:100%!important;
    max-height:calc(100dvh - 20px)!important;
    border-radius:28px 28px 0 0!important;
  }

  .cp-edit-modal-premium .cp-edit-body{
    grid-template-columns:1fr!important;
    max-height:calc(100dvh - 255px)!important;
    padding:18px!important;
  }

  .cp-edit-modal-premium .cp-edit-head,
  .cp-history-modal-premium .cp-history-head{
    padding:18px!important;
  }

  .cp-edit-modal-premium .cp-edit-head h3,
  .cp-history-modal-premium .cp-history-head h3{
    font-size:21px!important;
  }

  .cp-edit-modal-premium .cp-edit-actions{
    padding:14px 18px 18px!important;
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
  }

  .cp-edit-modal-premium .cp-edit-actions button{
    width:100%!important;
    min-width:0!important;
  }

  .cp-history-modal-premium .cp-history-list{
    max-height:calc(100dvh - 155px)!important;
    padding:12px!important;
  }

  .cp-history-modal-premium .cp-history-row{
    grid-template-columns:38px minmax(0,1fr)!important;
  }

  .cp-history-modal-premium .cp-history-row em{
    grid-column:2!important;
    white-space:normal!important;
  }
}

`;
